/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.async
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import models.SavingsGoal
import models.GoalPriority
import models.getDaysRemaining
import recommendations.DashboardInsights
import recommendations.SavingsRecommendationCardState
import recommendations.computeSavingsRecommendationCardState
import kotlinx.browser.localStorage
import org.w3c.dom.HTMLElement
import kotlin.js.Date
import kotlin.js.JSON
import kotlinx.serialization.json.Json
import kotlinx.serialization.encodeToString
import kotlin.math.round
import kotlin.math.roundToInt

/**
 * Step a (year, month) by [delta] whole months and clamp the result to the
 * inclusive window [[minY],[minM]]‥[[maxY],[maxM]]. Months are 1-based.
 *
 * Extracted as a pure top-level function so the stepper's boundary behaviour —
 * December→January year rollover and the two end-stops — is unit-tested without
 * standing up the whole dashboard component.
 */
internal fun stepMonthClamped(
    y0: Int, m0: Int, delta: Int,
    minY: Int, minM: Int, maxY: Int, maxM: Int,
): Pair<Int, Int> = utils.DashboardMonth.stepClamped(y0, m0, delta, minY, minM, maxY, maxM)

/** Colours the month stepper needs, so its markup builder stays theme-agnostic and renderable in isolation. */
internal class StepperColors(
    val cardBg: String, val border: String, val shadow: String,
    val textPrimary: String, val accent: String, val disabled: String,
)

/**
 * Pure markup for the dashboard month stepper. Split from the component so its
 * layout — arrow enabled/disabled states and the in-progress badge — can be
 * rendered and eyeballed without standing up the whole dashboard.
 * Button ids `monthStepperPrev` / `monthStepperNext` are the click hooks.
 */
internal fun buildMonthStepperHtml(
    label: String,
    badgeText: String?,
    canPrev: Boolean,
    canNext: Boolean,
    c: StepperColors,
): String {
    fun arrow(id: String, enabled: Boolean, glyph: String) = """
        <button id="$id" ${if (!enabled) "disabled" else ""} style="
            background: transparent; border: none;
            color: ${if (enabled) c.accent else c.disabled};
            font-size: 18px; cursor: ${if (enabled) "pointer" else "not-allowed"};
            padding: 4px 12px; display: flex; align-items: center; line-height: 1;
        ">$glyph</button>
    """.trimIndent()

    val badge = if (badgeText != null) """
        <span style="
            font-size: 11px; font-weight: 600; color: #b45309;
            background: rgba(245,158,11,0.15); border: 1px solid rgba(245,158,11,0.35);
            border-radius: 999px; padding: 2px 8px; margin-left: 8px; white-space: nowrap;
        ">$badgeText</span>
    """.trimIndent() else ""

    return """
        <div id="month-stepper" data-guide="month-stepper" style="
            display: flex; align-items: center; justify-content: center; gap: 4px;
            background: ${c.cardBg}; border: 1px solid ${c.border}; border-radius: 12px;
            box-shadow: ${c.shadow}; padding: 8px 12px;
        ">
            ${arrow("monthStepperPrev", canPrev, "&#9664;")}
            <span id="monthStepperLabel" style="
                color: ${c.textPrimary}; font-size: 16px; font-weight: 700;
                min-width: 130px; text-align: center; display: inline-flex;
                align-items: center; justify-content: center;
            ">$label$badge</span>
            ${arrow("monthStepperNext", canNext, "&#9654;")}
        </div>
    """.trimIndent()
}

/**
 * The dashboard's minimal type scale. Fixed sizes, so the same kind of text
 * reads identically in every widget instead of each one scaling its font by its
 * own dimensions. Widgets adapt to size by showing MORE content (more list
 * rows, more detail), not bigger text — a widget's single hero number (a big
 * stat) is the only thing that stays responsive.
 *
 * Colour roles pair with these: textPrimary for names/values/hero numbers,
 * textSecondary for labels/headers/units/captions, accent for links, and the
 * semantic red/green for +/- changes.
 */
internal object DashType {
    const val TITLE = "14px" // widget section titles
    const val BODY = "13px" // list names & values, primary content
    const val LABEL = "12px" // section labels, units, meta, hints
    const val CAPTION = "11px" // fine print
    const val SEMI = "600" // font-weight for titles and values
}

/**
 * Redesigned Dashboard component with split-view layout.
 *
 * Layout:
 * - Left Panel (40%): Chart type selector with visualization
 * - Right Panel (60%): Year selector with Income/Expense/Savings triple-bar charts
 * - Bottom: Savings Recommendation Panel
 */
class WebDashboardRedesigned(
    private val navigationManager: WebNavigationManager,
    private val accountId: String? = null,
    private val onAuthError: () -> Unit = {},
    private val isDemoMode: Boolean = false,
    private val useGridLayout: Boolean = false
) {
    private var selectedYear = Date().getFullYear()

    // DEMO-ONLY cosmetic year relabel. `?demo-year-shift=-1` makes the Monthly
    // Overview header render (selectedYear + shift) — e.g. 2026 shown as 2025 —
    // WITHOUT touching any data. Used for the marketing hero's "a year later"
    // time-jump: the S0/S1/S2 "getting-started" captures are relabeled to last
    // year so the jump to the (real, current-year) S3 reads as a genuine year,
    // while every current-month widget keeps working because the data is
    // untouched. Read from sessionStorage (armed at boot in WebApp.main) — NOT
    // window.location.search, because the /login redirect strips the query
    // before the dashboard ever renders. Omit the param (S3) → shift 0 → truth.
    private val demoYearLabelShift: Int =
        kotlinx.browser.window.sessionStorage.getItem("wiz3r_demo_year_shift")?.toIntOrNull() ?: 0
    private fun displayYearLabel(year: Int): Int = year + demoYearLabelShift

    private var selectedChartType = ChartType.SAVINGS_LINE
    private var container: HTMLElement? = null
    // False until the first transaction load settles. While false, render() returns a
    // loading skeleton instead of the full dashboard populated with zeroes.
    private var dashboardDataReady = false

    // Monthly data by year: Map<Year, Map<Month, MonthlyFinanceData>>
    private val yearlyData = mutableMapOf<Int, Map<Int, MonthlyFinanceData>>()

    // Transaction data for left panel charts (loaded from server)
    private var transactionsByCategory = mutableMapOf<String, Double>()
    private var categoryDisplayToPlaidCode = mutableMapOf<String, String>() // "Rent/Mortgage" -> "RENT_AND_UTILITIES"
    // Display name -> DETAILED plaid code, for colour lookups only. Kept separate from
    // categoryDisplayToPlaidCode (which holds the PRIMARY, used for budget matching) so a
    // promoted sub-category like Insurance can get its own colour distinct from its parent.
    private var categoryDisplayToColorCode = mutableMapOf<String, String>() // "Insurance" -> "GENERAL_SERVICES_INSURANCE"
    private var allTransactions = mutableListOf<TransactionData>() // Raw transaction data
    // C3 perf fix: allTransactions is always cleared + refilled in place (never
    // reassigned), so its object identity never changes across reloads and can't be
    // used to detect "the data changed". Bumped every time allTransactions is
    // repopulated; used as part of the memoization key in computeMonthlyAggregates().
    private var transactionsVersion = 0
    // transactionsVersion the focal count-up last played for, so a rebuild for the same
    // data doesn't restart the counter. Null = never played.
    private var focalAnimatedForVersion: Int? = null
    // Each primary loader flips its flag once it has SETTLED (succeeded, failed, or bailed
    // out early), not once it succeeded — see maybeRevealDashboard().
    private var transactionsSettled = false
    private var budgetPlansSettled = false
    private var transactionsLoaded = false
    /** Server-supplied smoothed monthly income (annual estimate ÷ 12) — the
     *  canonical savings-rate denominator. */
    private var smoothedMonthlyIncome: Double? = null

    // Account filtering
    private var allAccounts = mutableListOf<AccountInfo>()
    private var selectedAccountIds = mutableSetOf<String>() // All selected by default

    // Context filtering
    private var allContexts = mutableListOf<ContextInfo>()
    private var selectedContextIds = mutableSetOf<String>() // empty = "All" mode

    // Goals ViewModel for recommendations
    private val goalsViewModel = WebSavingsGoalsViewModel()
    private var goalsLoaded = false

    // Settings manager for user preferences
    private val settingsManager = settings.SettingsManager()
    private val apiService = ApiService()
    private var savingsTarget: Double = settingsManager.getSavingsTargetPercentage()

    // User custom category color overrides (loaded from server settings)
    private var userCategoryColors: Map<String, String> = emptyMap()

    // Cached budget plans from server (key = year)
    private val cachedBudgetPlans = mutableMapOf<Int, List<BudgetPlanResponse>>()
    private var budgetPlansLoaded = false

    // Legend toggle states - persisted via SettingsManager
    private var showIncome = settingsManager.getChartSeriesVisibility("income")
    private var showExpenses = settingsManager.getChartSeriesVisibility("expenses")
    private var showSavings = settingsManager.getChartSeriesVisibility("savings")
    private var showBudget = settingsManager.getChartSeriesVisibility("budget")
    private var showCategoryBudget = settingsManager.getChartSeriesVisibility("categoryBudget")
    // Pie chart time-range toggle: true = rolling 12 months (default), false = focused/current month
    private var pieRollingMode = settingsManager.getChartSeriesVisibility("pieRollingMode")

    // Demo mode: no Plaid account linked
    private var isNoAccountLinked: Boolean = isDemoMode
    // Removed: needsPlaidRelink banner was shown passively on dashboard — stale data is shown silently

    // Focal savings display - tracks which month is highlighted (null = current month or yearly avg)
    private var focusedMonth: Int? = null

    /** Shorthand for the current theme; re-read on every access so it tracks dark/light switches. */
    private inline val t: theme.ThemeColors get() = WebTheme.current

    // Layout storage prefix — mobile and desktop have independent layouts
    private val layoutPrefix = if (ResponsiveUtils.isMobile()) "mobile_" else "desktop_"
    private fun layoutKey(key: String) = "${layoutPrefix}$key"

    // GridStack instance
    private var grid: GridStack? = null
    // Track which widgets are currently on the grid
    private val activeWidgetIds = mutableSetOf<String>()
    // Sidebar width for resizable pane
    // Currently active named layout
    private var activeLayoutName: String? = null
    // Guard: prevent auto-save from corrupting layouts during restore
    private var isRestoringLayout = false
    // Locked layout names (no drag/resize when locked)
    private val lockedLayoutNames = mutableSetOf<String>()
    private var isLayoutLocked = false
    // Pinned layout names (shown as quick-access buttons when Customize is collapsed)
    private val pinnedLayoutNames = mutableSetOf<String>()

    // Widget definitions
    /** Reusable scaffolding for per-widget responsive layouts.
     *  Each widget defines its own LayoutMode data class + a decision
     *  function `(cols: Int, rows: Int, …): LayoutMode` that maps the
     *  measured size to a render strategy. Common bucketing here so
     *  every widget's decision table speaks the same vocabulary. */
    private enum class SizeBucket { COMPACT, MEDIUM, LARGE }

    /** Column-count buckets — 12-col grid. Boundaries picked so the
     *  default widget defaultW (8 cols) lands in LARGE; "user shrank it"
     *  lands in MEDIUM; "user really squeezed it" lands in COMPACT. */
    private fun colBucket(cols: Int): SizeBucket = when {
        cols < 5 -> SizeBucket.COMPACT
        cols < 9 -> SizeBucket.MEDIUM
        else -> SizeBucket.LARGE
    }

    /** Row-count buckets — grid rowHeight is 60 px. Most chart widgets
     *  default to 5 rows; LARGE = 6+ (room for legend below + labels);
     *  COMPACT = 3 or fewer (must REORGANIZE or hide secondary
     *  elements). */
    private fun rowBucket(rows: Int): SizeBucket = when {
        rows < 4 -> SizeBucket.COMPACT
        rows < 6 -> SizeBucket.MEDIUM
        else -> SizeBucket.LARGE
    }

    private data class WidgetDef(
        val id: String,
        val title: String,
        val icon: String,
        val defaultX: Int,
        val defaultY: Int,
        val defaultW: Int,
        val defaultH: Int,
        val minW: Int,
        val minH: Int,
        /**
         * Tallest row count this widget may be resized to, when it has one.
         *
         * Only for widgets whose content is finite, and it must be set by the
         * widget's RICHEST rendering: the cap exists to stop a drag going
         * somewhere pointless, never to put content out of reach. A cap below
         * the height the fullest layout needs is a bug — the user drags to the
         * limit and the widget still has more to show. Null means "as tall as
         * you like", the default for charts, which always fill.
         */
        val maxH: Int? = null,
        // One-line hover tooltip explaining the widget (shown on palette pills).
        val description: String = "",
    )

    // Short description shown on hover for each palette pill. Localized inline so
    // we don't add 16+ StringKeys for UI that Pro-only customize-mode users see.
    private fun widgetDescription(id: String): String {
        val fr = LocaleManager.getCurrentLocale().code == "fr"
        return when (id) {
            "savings-rate" -> if (fr) "Votre taux d'\u00e9pargne du mois en grand." else "Your savings rate for the month, front and center."
            "accounts" -> if (fr) "Choisir quels comptes alimentent le tableau." else "Pick which accounts feed the dashboard."
            "contexts" -> if (fr) "Filtrer par contexte (AND)." else "Filter everything by context (AND logic)."
            "triple-bar" -> if (fr) "Revenus, d\u00e9penses et \u00e9pargne par mois sur l'ann\u00e9e." else "Income, expenses and savings per month for the year."
            "category-spending" -> if (fr) "D\u00e9penses du mois par cat\u00e9gorie, vs budget." else "This month's spending by category, versus budget."
            "expense-dist" -> if (fr) "R\u00e9partition des d\u00e9penses en camembert." else "Share of expenses per category (pie)."
            "savings-trend" -> if (fr) "\u00c9volution du taux d'\u00e9pargne sur 12 mois." else "Savings rate trend over the rolling 12 months."
            "expense-timeline" -> if (fr) "Chronologie de vos d\u00e9penses mensuelles." else "Monthly spending timeline."
            "recommendation" -> if (fr) "Marge d'\u00e9pargne mensuelle et objectifs sugg\u00e9r\u00e9s." else "Monthly room to save, plus suggested goals."
            "cashflow-mtd" -> if (fr) "Revenus \u2212 d\u00e9penses ce mois-ci, avec \u00e9cart vs le mois dernier au m\u00eame jour." else "Income minus expenses this month, with delta vs same point last month."
            "budget-health" -> if (fr) "Part du budget d\u00e9pens\u00e9e ce mois-ci." else "Share of your monthly budget already consumed."
            "budget-categories-count" -> if (fr) "Combien de cat\u00e9gories sont au-dessus, sous, ou sans budget." else "How many categories are over, under, or without a budget."
            "savings-streak" -> if (fr) "Mois cons\u00e9cutifs o\u00f9 vous avez atteint votre cible d'\u00e9pargne." else "Consecutive months meeting your savings target."
            "yoy-month" -> if (fr) "Ce mois-ci compar\u00e9 au m\u00eame mois l'an dernier." else "This month side by side with the same month last year."
            "category-movers" -> if (fr) "Cat\u00e9gories qui bougent le plus vs leur moyenne sur 6 mois." else "Categories with the biggest change vs their 6-month average."
            "largest-txns" -> if (fr) "Les 5 plus grosses d\u00e9penses du mois." else "Top 5 biggest expenses this month."
            "top-merchants" -> if (fr) "Les 5 commer\u00e7ants o\u00f9 vous avez le plus d\u00e9pens\u00e9." else "Top 5 merchants by spend this month."
            "needs-wants-flow" -> if (fr) "O\u00f9 va votre revenu : besoins, envies, \u00e9pargne." else "Where your income goes: needs, wants, savings."
            else -> ""
        }
    }

    private val widgetDefinitions = listOf(
        WidgetDef("savings-rate", "Savings Rate",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 11v.01"/><path d="M5.173 8.378a3 3 0 1 1 4.656 -1.377"/><path d="M16 4v3.803a6.019 6.019 0 0 1 2.658 3.197h1.341a1 1 0 0 1 1 1v2a1 1 0 0 1 -1 1h-1.342c-.336 .95 -.907 1.8 -1.658 2.473v2.027a1.5 1.5 0 0 1 -3 0v-.583a6.04 6.04 0 0 1 -1 .083h-4a6.04 6.04 0 0 1 -1 -.083v.583a1.5 1.5 0 0 1 -3 0v-2l0 -.027a6 6 0 0 1 4 -10.473h2.5l4.5 -3"/></svg>""",
            0, 0, 4, 2, 2, 2),
        WidgetDef("accounts", "Accounts",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21l18 0"/><path d="M3 10l18 0"/><path d="M5 6l7 -3l7 3"/><path d="M4 10l0 11"/><path d="M20 10l0 11"/><path d="M8 14l0 3"/><path d="M12 14l0 3"/><path d="M16 14l0 3"/></svg>""",
            0, 2, 4, 3, 2, 2),
        WidgetDef("contexts", StringKey.CONTEXT_TITLE.tr(),
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 5a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v10a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2l0 -10"/><path d="M17 17v2a2 2 0 0 1 -2 2h-10a2 2 0 0 1 -2 -2v-10a2 2 0 0 1 2 -2h2"/></svg>""",
            0, 5, 4, 2, 2, 2),
        WidgetDef("triple-bar", "Monthly Overview",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6"/><path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10"/><path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14"/><path d="M4 20h14"/></svg>""",
            4, 0, 8, 5, 4, 3),
        WidgetDef("category-spending", "Category Spending",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2"/></svg>""",
            0, 7, 6, 5, 3, 3),
        WidgetDef("expense-dist", "Expense Distribution",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3.2a9 9 0 1 0 10.8 10.8a1 1 0 0 0 -1 -1h-6.8a2 2 0 0 1 -2 -2v-7a.9 .9 0 0 0 -1 -.8"/><path d="M15 3.5a9 9 0 0 1 5.5 5.5h-4.5a1 1 0 0 1 -1 -1v-4.5"/></svg>""",
            6, 7, 6, 5, 3, 3),
        WidgetDef("savings-trend", "Savings Trend",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 16l6 -7l5 5l5 -6"/><path d="M14 14a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M9 9a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M3 16a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M19 8a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/></svg>""",
            0, 12, 8, 4, 4, 4),
        WidgetDef("expense-timeline", "Expense Timeline",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M3 8a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -8"/><path d="M18 12h.01"/><path d="M6 12h.01"/></svg>""",
            8, 12, 4, 4, 4, 4),
        // minH is RecWidget.MIN_GRID_ROWS: below it the widget cannot draw its
        // hero number, its CTA and one supporting line without clipping, so it
        // is not allowed to get there. maxH is RecWidget.MAX_GRID_ROWS: the
        // height at which the widget's fullest card state is finally whole, so
        // every rendering the ladder can produce is reachable by dragging. A
        // sparse month will not fill that height, and that is fine — see the
        // constant. defaultH matches minH so a freshly dropped tile starts at a
        // height that renders cleanly.
        WidgetDef("recommendation", "Recommendation",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 3l0 7l6 0l-8 11l0 -7l-6 0l8 -11"/></svg>""",
            0, 16, 12, RecWidget.MIN_GRID_ROWS, 4, RecWidget.MIN_GRID_ROWS,
            maxH = RecWidget.MAX_GRID_ROWS),
        WidgetDef("needs-wants-flow", "Needs & Wants Flow", "🌊",
            defaultX = 0, defaultY = 0, defaultW = 8, defaultH = 6, minW = 3, minH = 3,
            description = widgetDescription("needs-wants-flow")),
        // --- New KPI widgets ---
        // Small single-number KPIs: can shrink to 2×2 (half-width), content scales
        // with the tile via CSS container queries (clamp + cqw/cqh).
        WidgetDef("cashflow-mtd", "Cash flow",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M17 8a4 4 0 0 0 -4 -3h-2a3.5 3.5 0 0 0 0 7h2a3.5 3.5 0 0 1 0 7h-2a4 4 0 0 1 -4 -3"/></svg>""",
            0, 19, 4, 2, 2, 2),
        WidgetDef("budget-health", "Budget health",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a9 9 0 1 0 9 9"/><path d="M12 7v5l3 3"/></svg>""",
            4, 19, 4, 2, 2, 2),
        WidgetDef("budget-categories-count", "Over / Under",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2l4 -4"/><path d="M12 3a9 9 0 1 0 9 9a9 9 0 0 0 -9 -9"/></svg>""",
            8, 19, 4, 2, 2, 2),
        WidgetDef("savings-streak", "Streak",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0"/><path d="M13 8a2.5 2.5 0 0 0 -5 0v4a2.5 2.5 0 0 0 5 0"/></svg>""",
            0, 21, 4, 2, 2, 2),
        // List/comparison KPIs: need at least 3 cols × 3 rows to avoid cropping
        WidgetDef("yoy-month", "This year vs last",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4v-6h-4z"/><path d="M10 20h4v-10h-4z"/><path d="M16 20h4v-14h-4z"/></svg>""",
            4, 21, 4, 3, 3, 3),
        WidgetDef("category-movers", "Category movers",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 17l6 -6l4 4l8 -8"/><path d="M14 7l7 0l0 7"/></svg>""",
            8, 21, 4, 3, 3, 3),
        // Top-N lists: need 4 rows of vertical space for 5 entries + header
        WidgetDef("largest-txns", "Top transactions",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 6l16 0"/><path d="M4 12l16 0"/><path d="M4 18l12 0"/></svg>""",
            0, 24, 6, 4, 3, 4),
        WidgetDef("top-merchants", "Top merchants",
            """<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21l18 0"/><path d="M3 7v1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1m0 1a3 3 0 0 0 6 0v-1h-18l2 -4h14l2 4"/><path d="M5 21l0 -10.15"/><path d="M19 21l0 -10.15"/><path d="M9 21v-4a2 2 0 0 1 2 -2h2a2 2 0 0 1 2 2v4"/></svg>""",
            6, 24, 6, 4, 3, 4),
    )

    // Get the focal savings percentage for display
    private fun getFocalSavingsPercentage(): Double {
        val currentYear = Date().getFullYear()
        val currentMonth = Date().getMonth() + 1
        val yearData = yearlyData[selectedYear] ?: emptyMap()

        // If viewing a past year and no specific month selected, show yearly average
        if (selectedYear < currentYear && focusedMonth == null) {
            val monthsWithData = yearData.values.filter { it.hasData }
            if (monthsWithData.isEmpty()) return 0.0
            val totalIncome = monthsWithData.sumOf { it.confirmedIncome }
            val totalGoalAllocations = monthsWithData.sumOf { it.goalAllocations }
            return if (totalIncome > 0) (totalGoalAllocations / totalIncome * 100) else 0.0
        }

        // Show the focal month's savings — last complete month by default.
        val (dataYear, month) = effectiveDisplayYearMonth()
        val data = (yearlyData[dataYear] ?: emptyMap())[month]
        return data?.savingsPercentage ?: 0.0
    }

    // Get the month name for the focal display label
    private fun getFocalMonthName(): String? {
        val currentYear = Date().getFullYear()
        val currentMonth = Date().getMonth() + 1

        // If viewing past year with no specific month, null (yearly average)
        if (selectedYear < currentYear && focusedMonth == null) {
            return null
        }

        val (_, month) = effectiveDisplayYearMonth()
        return monthName(month)
    }

    /** Localized full month name for a 1-based month index. */
    private fun monthName(month: Int): String? = listOf(
        StringKey.MONTH_JANUARY.tr(), StringKey.MONTH_FEBRUARY.tr(), StringKey.MONTH_MARCH.tr(),
        StringKey.MONTH_APRIL.tr(), StringKey.MONTH_MAY_FULL.tr(), StringKey.MONTH_JUNE.tr(),
        StringKey.MONTH_JULY.tr(), StringKey.MONTH_AUGUST.tr(), StringKey.MONTH_SEPTEMBER.tr(),
        StringKey.MONTH_OCTOBER.tr(), StringKey.MONTH_NOVEMBER.tr(), StringKey.MONTH_DECEMBER.tr(),
    ).getOrNull(month - 1)

    // Check if showing yearly average
    private fun isYearlyAverage(): Boolean {
        val currentYear = Date().getFullYear()
        return selectedYear < currentYear && focusedMonth == null
    }

    /**
     * Year/month every month-scoped widget displays. Single source of truth —
     * the focal helpers and the bar/pie placeholders all route through the same
     * default so the whole dashboard agrees on which month it's showing.
     *
     * With no bar selected the default is the **last complete month**, never the
     * in-progress current one: mid-month, rent and fixed bills have landed but
     * income hasn't, so "current month" reads as a wildly over-spent month. The
     * user can still click the current month to see it. Past years with no
     * selection fall back to their latest month with data.
     */
    private fun effectiveDisplayYearMonth(): Pair<Int, Int> {
        val currentYear = Date().getFullYear()
        focusedMonth?.let { return selectedYear to it }
        if (selectedYear == currentYear) return lastFullYearMonth()
        val yearData = yearlyData[selectedYear] ?: emptyMap()
        return selectedYear to (yearData.keys.maxOrNull() ?: 12)
    }

    /** True when the given (year, month) is the current, still-incomplete calendar month. */
    private fun isInProgressMonth(year: Int, month: Int): Boolean {
        val now = Date()
        return utils.DashboardMonth.isInProgress(year, month, now.getFullYear(), now.getMonth() + 1)
    }

    /** Earliest (year, month) that has data — the back-stop for the month stepper. */
    private fun earliestDataYearMonth(): Pair<Int, Int> {
        val minY = getMinYearFromData()
        val firstMonth = yearlyData[minY]?.filterValues { it.hasData }?.keys?.minOrNull() ?: 1
        return minY to firstMonth
    }

    /** Move the focal month by [delta] and rebuild. The stepper's only writer. */
    private fun stepFocalMonth(delta: Int) {
        val (y0, m0) = effectiveDisplayYearMonth()
        val (minY, minM) = earliestDataYearMonth()
        val now = Date()
        val (ny, nm) = stepMonthClamped(
            y0, m0, delta,
            minY, minM,
            now.getFullYear(), now.getMonth() + 1,
        )
        if (ny == y0 && nm == m0) return // already at an end-stop
        if (ny != selectedYear) {
            selectedYear = ny
            loadBudgetPlansForYear(selectedYear)
            loadBudgetPlansForYear(selectedYear - 1)
            updateYearlyDataWithActualTransactions()
        }
        focusedMonth = nm
        refreshDashboard()
    }

    /**
     * Set the focal month from a chart click — the monthly-overview bars and the
     * data points on the savings-trend / expense-timeline lines all land here.
     *
     * The line charts render a ROLLING 13-month window that reaches back into the
     * previous calendar year, so a clicked point carries its own year. Landing on a
     * point from another year moves [selectedYear] with it, the same way
     * [stepFocalMonth] does when a step crosses a year boundary — otherwise last
     * year's "Sep" would select this year's empty future September.
     */
    private fun selectFocalYearMonth(year: Int, month: Int) {
        if (year != selectedYear) {
            selectedYear = year
            focusedMonth = month
            loadBudgetPlansForYear(selectedYear)
            loadBudgetPlansForYear(selectedYear - 1)
            updateYearlyDataWithActualTransactions()
            // Year chrome (year label, whole-year windows) has to rebuild too, so
            // take the full refresh rather than the in-place widget update.
            refreshDashboard()
            return
        }
        focusedMonth = month
        applyFocalMonthSelection()
    }

    /**
     * The global "which month am I looking at" header. Steps the focal month and,
     * on the current (still-incomplete) month, says so — the default is always the
     * last COMPLETE month, so seeing "in progress" means the user navigated forward.
     */
    private fun renderMonthStepper(): String {
        val (year, month) = effectiveDisplayYearMonth()
        val (minY, minM) = earliestDataYearMonth()
        val now = Date()
        val nowKey = now.getFullYear() * 12 + now.getMonth() + 1
        val key = year * 12 + month
        val inProgress = isInProgressMonth(year, month)
        return buildMonthStepperHtml(
            label = "${monthName(month)} $year",
            badgeText = if (inProgress) StringKey.DASHBOARD_MONTH_IN_PROGRESS.tr() else null,
            canPrev = key > (minY * 12 + minM),
            canNext = key < nowKey,
            c = StepperColors(
                cardBg = t.card, border = t.border, shadow = t.cardShadow,
                textPrimary = t.textPrimary, accent = t.accent, disabled = "rgba(136,136,136,0.3)",
            ),
        )
    }

    /**
     * Re-render the stepper in place. On the grid a "refresh" only re-renders
     * widget CONTENT, never the chrome — so the label must be updated
     * explicitly, or the arrows would move the widgets while the label sat
     * frozen. Safe on the fixed layout too (a full refresh just re-renders it
     * again). Re-wires the arrows since the element is replaced.
     */
    private fun updateMonthStepper() {
        val el = container?.querySelector("#month-stepper") as? HTMLElement ?: return
        val parent = el.parentElement ?: return
        val tmp = kotlinx.browser.document.createElement("div") as HTMLElement
        tmp.innerHTML = renderMonthStepper()
        val fresh = tmp.firstElementChild as? HTMLElement ?: return
        parent.replaceChild(fresh, el)
        fresh.querySelector("#monthStepperPrev")?.addEventListener("click", { stepFocalMonth(-1) })
        fresh.querySelector("#monthStepperNext")?.addEventListener("click", { stepFocalMonth(1) })
    }

    // Calculate the minimum year from transaction data (oldest transaction year)
    private fun getMinYearFromData(): Int {
        return if (allTransactions.isEmpty()) {
            Date().getFullYear() - 1  // Default to last year if no data
        } else {
            allTransactions.minOfOrNull { it.year } ?: (Date().getFullYear() - 1)
        }
    }

    // Simple data class to hold transaction info needed for charts
    private data class TransactionData(
        val amount: Double,
        val category: String,
        val detailedCategory: String, // Plaid detailed category for splitting RENT_AND_UTILITIES
        val year: Int,
        val month: Int,
        val isIncome: Boolean, // negative amounts in Plaid = income
        val accountId: String,
        val isSplitParent: Boolean = false,
        val isDistributedParent: Boolean = false,
        val isExcluded: Boolean = false,
        val assignedGoalAmount: Double? = null,
        /** [assignedGoalAmount] split per goal — what the recommendation card needs
         *  to say how much each goal actually received. A single id would credit a
         *  two-goal transaction entirely to the larger goal. */
        val goalAllocations: Map<String, Double> = emptyMap(),
        val contextIds: List<String> = emptyList(),
        // day/name are used by the "largest transactions" and "top merchants" widgets
        // and by month-to-date cash flow. Demo-data sites leave the defaults alone.
        val day: Int = 1,
        val name: String = "",
    ) {
        val excludeFromTotals: Boolean
            get() = models.TransactionEligibility.excludeFromTotals(
                isSplitParent, isDistributedParent, isExcluded,
            )

        /** True for internal money-movements (savings transfers, card
         *  payments) that must never count as income or spending. Single
         *  source of truth: [models.PlaidCategories.isTransfer]. Kept OUT of
         *  [excludeFromTotals] on purpose — an assigned savings transfer must
         *  still feed the Savings bar (goalAllocations), only the income /
         *  expense bars exclude it. */
        val isTransfer: Boolean
            get() = models.PlaidCategories.isTransfer(category, detailedCategory)
    }

    // Account data for filtering
    private data class AccountInfo(
        val accountId: String,
        val name: String,
        val officialName: String?,
        val mask: String?,
        val customName: String?
    )

    // Context data for filtering
    private data class ContextInfo(
        val id: String,
        val name: String,
        val color: String
    )

    /** Check if a transaction passes the context filter (AND logic). */
    private fun TransactionData.passesContextFilter(): Boolean {
        if (selectedContextIds.isEmpty()) return true
        val txnContexts = contextIds.toSet()
        return selectedContextIds.all { it in txnContexts }
    }

    enum class ChartType(val displayName: String) {
        BAR_CHART("Category Spending"),
        PIE_CHART("Expense Distribution"),
        EXPENSE_TIMELINE("Expense Timeline"),
        SAVINGS_LINE("Savings % Trend"),
        PREV_YEAR_COMPARISON("Previous Year Comparison"),
        NEEDS_WANTS_FLOW("Needs & Wants Flow")
    }

    private fun getChartTypeDisplayName(type: ChartType): String {
        return when (type) {
            ChartType.BAR_CHART -> StringKey.DASHBOARD_CATEGORY_SPENDING.tr()
            ChartType.PIE_CHART -> StringKey.DASHBOARD_EXPENSE_DISTRIBUTION.tr()
            ChartType.EXPENSE_TIMELINE -> StringKey.DASHBOARD_EXPENSE_TIMELINE.tr()
            ChartType.SAVINGS_LINE -> StringKey.DASHBOARD_SAVINGS_TREND.tr()
            ChartType.PREV_YEAR_COMPARISON -> StringKey.DASHBOARD_COMPARISON.tr()
            ChartType.NEEDS_WANTS_FLOW -> StringKey.DASHBOARD_NEEDS_WANTS_FLOW.tr()
        }
    }

    private fun getChartTypeIconSvg(type: ChartType): String {
        val paths = when (type) {
            ChartType.BAR_CHART, ChartType.PREV_YEAR_COMPARISON ->
                """<path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M3 13a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v6a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -6" /><path d="M15 9a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v10a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -10" /><path d="M9 5a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v14a1 1 0 0 1 -1 1h-4a1 1 0 0 1 -1 -1l0 -14" /><path d="M4 20h14" />"""
            ChartType.PIE_CHART ->
                """<path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M10 3.2a9 9 0 1 0 10.8 10.8a1 1 0 0 0 -1 -1h-6.8a2 2 0 0 1 -2 -2v-7a.9 .9 0 0 0 -1 -.8" /><path d="M15 3.5a9 9 0 0 1 5.5 5.5h-4.5a1 1 0 0 1 -1 -1v-4.5" />"""
            ChartType.EXPENSE_TIMELINE, ChartType.SAVINGS_LINE ->
                """<path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 16l6 -7l5 5l5 -6" /><path d="M14 14a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M9 9a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M3 16a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" /><path d="M19 8a1 1 0 1 0 2 0a1 1 0 1 0 -2 0" />"""
            ChartType.NEEDS_WANTS_FLOW ->
                """<path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 6h4l8 4l-8 4h-4" /><path d="M4 6v12" /><path d="M16 10h4" /><path d="M16 14h4" />"""
        }
        return """<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">$paths</svg>"""
    }

    data class MonthlyFinanceData(
        val month: Int,
        val expectedIncome: Double = 0.0,
        val confirmedIncome: Double = 0.0,
        val plannedExpenses: Double = 0.0,
        val realizedExpenses: Double = 0.0,
        val targetSavings: Double = 0.0,
        val actualSavings: Double = 0.0,
        val goalAllocations: Double = 0.0,
        /** Server-supplied smoothed monthly income (annual estimate ÷ 12). */
        val smoothedMonthlyIncome: Double? = null,
    ) {
        /** Canonical savings rate — the single shared formula every surface uses
         *  (allocations ÷ smoothed monthly income, fallback actual). */
        val savingsPercentage: Double
            get() = analytics.savingsRatePercent(goalAllocations, smoothedMonthlyIncome, confirmedIncome)

        val incomeExceeded: Double
            get() = maxOf(0.0, confirmedIncome - expectedIncome)

        val expensesExceeded: Double
            get() = maxOf(0.0, realizedExpenses - plannedExpenses)

        val savingsExceeded: Double
            get() = maxOf(0.0, goalAllocations - targetSavings)

        val hasData: Boolean
            get() = confirmedIncome > 0 || realizedExpenses > 0
    }

    /**
     * Data class to represent a year/month combination for the rolling window.
     */
    private data class YearMonth(val year: Int, val month: Int) {
        companion object {
            val MONTH_NAMES = listOf(
                StringKey.MONTH_JAN.tr(), StringKey.MONTH_FEB.tr(), StringKey.MONTH_MAR.tr(),
                StringKey.MONTH_APR.tr(), StringKey.MONTH_MAY.tr(), StringKey.MONTH_JUN.tr(),
                StringKey.MONTH_JUL.tr(), StringKey.MONTH_AUG.tr(), StringKey.MONTH_SEP.tr(),
                StringKey.MONTH_OCT.tr(), StringKey.MONTH_NOV.tr(), StringKey.MONTH_DEC.tr()
            )
        }
        fun toLabel(): String = MONTH_NAMES[month - 1]
        fun toFullLabel(): String = "${MONTH_NAMES[month - 1]} $year"
    }

    private val monthNames = YearMonth.MONTH_NAMES

    /**
     * Get the rolling 12-month window ending at the current month,
     * plus one future month for the X-axis (no data point).
     * Returns 13 YearMonth entries: 12 with data + 1 future.
     */
    private fun getRolling12MonthWindow(): List<YearMonth> {
        val now = Date()
        val currentYear = now.getFullYear()
        val currentMonth = now.getMonth() + 1 // JavaScript months are 0-indexed

        // Build list of 13 months: 11 past + current + 1 future
        val months = mutableListOf<YearMonth>()

        // Start from 11 months ago
        var year = currentYear
        var month = currentMonth - 11
        if (month <= 0) {
            year -= 1
            month += 12
        }

        // Add 13 months total (11 past + current + 1 future)
        repeat(13) {
            months.add(YearMonth(year, month))
            month++
            if (month > 12) {
                month = 1
                year++
            }
        }

        return months
    }

    /**
     * Get the index of the current month in the rolling window (0-based).
     * Returns 11 (the 12th item, since we have 11 past months + current).
     */
    private fun getCurrentMonthIndexInWindow(): Int = 11

    /**
     * Get monthly finance data for a specific year/month from yearlyData.
     */
    private fun getDataForYearMonth(ym: YearMonth): MonthlyFinanceData? {
        return yearlyData[ym.year]?.get(ym.month)
    }

    /**
     * Get the display window based on selected year:
     * - Current year: rolling 12-month window (11 past + current + 1 future = 13 months)
     * - Past year: full calendar year (Jan-Dec = 12 months)
     * Returns Pair(window, lastIndexWithData)
     */
    private fun getDisplayWindow(): Pair<List<YearMonth>, Int> {
        val now = Date()
        val currentYear = now.getFullYear()

        return if (selectedYear == currentYear) {
            // Rolling 12-month window for current year
            val w = getRolling12MonthWindow() // 13 months: 12 data + 1 future
            Pair(w, getCurrentMonthIndexInWindow()) // Index 11 is current month
        } else {
            // Full calendar year for past years (Jan-Dec of selectedYear)
            val w = (1..12).map { YearMonth(selectedYear, it) }
            Pair(w, 11) // All 12 months have data for past years
        }
    }

    /**
     * Get category spending filtered by selected accounts based on display mode.
     * Current year: rolling 12-month window
     * Past year: full calendar year
     */
    private fun getFilteredCategorySpendingForDisplayWindow(): Map<String, Double> {
        val (window, lastIndexWithData) = getDisplayWindow()
        // Only include months up to lastIndexWithData
        val dataWindow = window.take(lastIndexWithData + 1)
        val filtered = mutableMapOf<String, Double>()

        allTransactions
            .filter { txn ->
                !txn.isIncome &&
                !txn.excludeFromTotals &&
                !txn.isTransfer &&
                txn.accountId in selectedAccountIds && txn.passesContextFilter() &&
                dataWindow.any { ym -> txn.year == ym.year && txn.month == ym.month }
            }
            .forEach { txn ->
                // Use refined category mapping for proper Rent/Mortgage vs Utilities split
                val effectiveCategory = getEffectiveDisplayCategory(txn.category, txn.detailedCategory)
                filtered[effectiveCategory] = (filtered[effectiveCategory] ?: 0.0) + txn.amount
                // Track display name → plaid code mapping for color lookups
                if (effectiveCategory !in categoryDisplayToPlaidCode) {
                    categoryDisplayToPlaidCode[effectiveCategory] = txn.category
                }
                if (effectiveCategory !in categoryDisplayToColorCode) {
                    categoryDisplayToColorCode[effectiveCategory] = txn.detailedCategory.ifEmpty { txn.category }
                }
            }
        return filtered
    }

    /**
     * Get category spending for the current month only.
     * Used for the Category Spending bar chart to show month-to-date progress.
     * Uses refined categories (Rent/Mortgage, Utilities, Subscriptions split).
     */
    private fun getCategorySpendingForMonth(year: Int, month: Int): Map<String, Double> {
        val filtered = mutableMapOf<String, Double>()
        allTransactions
            .filter { txn ->
                !txn.isIncome &&
                !txn.excludeFromTotals &&
                !txn.isTransfer &&
                txn.accountId in selectedAccountIds && txn.passesContextFilter() &&
                txn.year == year &&
                txn.month == month
            }
            .forEach { txn ->
                val effectiveCategory = getEffectiveDisplayCategory(txn.category, txn.detailedCategory)
                filtered[effectiveCategory] = (filtered[effectiveCategory] ?: 0.0) + txn.amount
                if (effectiveCategory !in categoryDisplayToPlaidCode) {
                    categoryDisplayToPlaidCode[effectiveCategory] = txn.category
                }
                if (effectiveCategory !in categoryDisplayToColorCode) {
                    categoryDisplayToColorCode[effectiveCategory] = txn.detailedCategory.ifEmpty { txn.category }
                }
            }
        return filtered
    }

    private fun getCurrentMonthCategorySpending(): Map<String, Double> {
        val now = Date()
        return getCategorySpendingForMonth(now.getFullYear(), now.getMonth() + 1)
    }

    private fun getPlannedBudgetsForMonth(year: Int, month: Int): Map<String, Double> {
        val categoryBudgets = mutableMapOf<String, Double>()
        val plans = cachedBudgetPlans[year] ?: return categoryBudgets

        plans.filter { it.month == month && it.categoryType.uppercase() == "EXPENSE" }
            .forEach { plan ->
                val plaidCategory = mapBudgetCategoryToPlaidCategory(plan.categoryName)
                categoryBudgets[plaidCategory] = (categoryBudgets[plaidCategory] ?: 0.0) + plan.plannedAmount
            }

        return categoryBudgets
    }

    /**
     * Get planned budget by category for the current month only.
     * Uses cached budget plans from server API.
     */
    private fun getCurrentMonthPlannedBudgets(): Map<String, Double> {
        val now = Date()
        return getPlannedBudgetsForMonth(now.getFullYear(), now.getMonth() + 1)
    }

    init {
        // Load budget plans from server (async), then populate yearlyData
        loadBudgetPlansFromServer()
        // Load transaction data for left panel charts
        loadTransactionData()
        // Sync settings from server
        syncSettingsFromServer()
        // Load the user's 50/30/20 bucket overrides for the Needs & Wants Flow widget
        GlobalScope.launch { loadBucketClassificationsForDashboard() }

        // Safety net: if a loader never settles (hung request), reveal anyway rather than
        // stranding the user on the skeleton. Showing a partially-populated dashboard
        // beats showing a placeholder forever.
        window.setTimeout({
            if (!dashboardDataReady) {
                console.log("[Dashboard] reveal timeout — showing with whatever loaded")
                dashboardDataReady = true
                performDashboardRefresh()
            }
        }, 4000)
    }

    /**
     * Reveal the real dashboard once BOTH primary loaders have settled (succeeded or
     * failed). Transactions and budget plans feed the same widgets — notably the triple
     * bar chart, whose budget bars come from the plans — so revealing after only one of
     * them means the user watches the dashboard build, then rebuild with different
     * numbers. Waiting for both costs a little skeleton time and yields ONE render with
     * complete data. Guarded by the timeout in init so a hung loader can't block forever.
     */
    private fun maybeRevealDashboard() {
        if (dashboardDataReady) return
        if (!transactionsSettled || !budgetPlansSettled) return
        dashboardDataReady = true
        performDashboardRefresh()
    }

    /**
     * Load budget plans from server API for required years.
     * Caches the results and builds yearlyData.
     */
    private fun loadBudgetPlansFromServer() {
        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()
        if (sharedToken == null || sharedUserId == null) {
            console.log("Dashboard: Cannot load budget plans - not authenticated")
            // Settled (nothing more coming) — don't hold the reveal on this loader.
            budgetPlansSettled = true
            maybeRevealDashboard()
            return
        }
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }

        // Determine which years to load
        val yearsNeeded = mutableSetOf<Int>()
        yearsNeeded.add(selectedYear)
        yearsNeeded.add(selectedYear - 1)
        getRolling12MonthWindow().forEach { yearsNeeded.add(it.year) }

        GlobalScope.launch {
            try {
                // C2 perf fix: these years are independent server round trips (typically
                // 2-3 of them) that were previously awaited one after another. Fire them
                // all at once and await together — latency becomes the slowest single
                // call instead of their sum.
                val yearsToFetch = yearsNeeded.filter { !cachedBudgetPlans.containsKey(it) }
                val deferredByYear = yearsToFetch.associateWith { year ->
                    async { apiService.getBudgetPlansByYear(year) }
                }
                deferredByYear.forEach { (year, deferred) ->
                    val plans = deferred.await()
                    cachedBudgetPlans[year] = plans
                    console.log("Dashboard: Loaded ${plans.size} budget plans for $year from server")
                }
                budgetPlansLoaded = true

                // Don't overwrite demo data if already in demo mode
                if (!isNoAccountLinked) {
                    // Now build yearlyData from cached plans
                    yearsNeeded.forEach { year ->
                        buildYearlyDataFromCachedPlans(year)
                    }

                    // Merge in actual transaction data
                    updateYearlyDataWithActualTransactions()

                    // Refresh the dashboard display
                    refreshDashboard()
                }
                budgetPlansSettled = true
                maybeRevealDashboard()
            } catch (e: Exception) {
                budgetPlansSettled = true
                maybeRevealDashboard()
                console.log("Dashboard: Failed to load budget plans from server: ${e.message}")
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    /**
     * Build yearlyData for a year from cached budget plans.
     * Preserves existing realized values if transactions already loaded.
     */
    private fun buildYearlyDataFromCachedPlans(year: Int) {
        val plans = cachedBudgetPlans[year] ?: emptyList()
        val existingData = yearlyData[year]  // Preserve existing realized values

        // Group plans by month
        val incomeByMonth = mutableMapOf<Int, Double>()
        val expenseByMonth = mutableMapOf<Int, Double>()

        plans.forEach { plan ->
            when (plan.categoryType.uppercase()) {
                "INCOME" -> {
                    incomeByMonth[plan.month] = (incomeByMonth[plan.month] ?: 0.0) + plan.plannedAmount
                }
                "EXPENSE" -> {
                    expenseByMonth[plan.month] = (expenseByMonth[plan.month] ?: 0.0) + plan.plannedAmount
                }
            }
        }

        // Build monthly finance data for this year, preserving existing realized values
        val monthlyData = mutableMapOf<Int, MonthlyFinanceData>()
        (1..12).forEach { month ->
            val existing = existingData?.get(month)
            val plannedIncome = incomeByMonth[month] ?: 0.0
            val plannedExpense = expenseByMonth[month] ?: 0.0
            monthlyData[month] = MonthlyFinanceData(
                month = month,
                expectedIncome = plannedIncome,
                confirmedIncome = existing?.confirmedIncome ?: 0.0,  // Preserve if exists
                plannedExpenses = plannedExpense,
                realizedExpenses = existing?.realizedExpenses ?: 0.0,  // Preserve if exists
                targetSavings = existing?.targetSavings ?: 0.0,  // Computed from actual income in updateYearlyData
                actualSavings = existing?.actualSavings ?: 0.0  // Preserve if exists
            )
        }

        yearlyData[year] = monthlyData
        console.log("Dashboard: Built yearly data for $year - Income months: ${incomeByMonth.filterValues { it > 0 }.size}, Expense months: ${expenseByMonth.filterValues { it > 0 }.size}")
    }

    /**
     * Load budget plans for a single year (used when year selector changes).
     */
    private fun loadBudgetPlansForYear(year: Int) {
        if (cachedBudgetPlans.containsKey(year)) {
            // Already cached, just rebuild yearlyData
            buildYearlyDataFromCachedPlans(year)
            return
        }

        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()
        if (sharedToken == null || sharedUserId == null) return
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }

        GlobalScope.launch {
            try {
                val plans = apiService.getBudgetPlansByYear(year)
                cachedBudgetPlans[year] = plans
                buildYearlyDataFromCachedPlans(year)
                refreshDashboard()
            } catch (e: Exception) {
                console.log("Dashboard: Failed to load budget plans for $year: ${e.message}")
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    private fun syncSettingsFromServer() {
        GlobalScope.launch {
            try {
                val serverValue = apiService.getSetting("savings_target_percentage")
                serverValue?.toDoubleOrNull()?.let { percentage ->
                    savingsTarget = percentage
                    settingsManager.setSavingsTargetPercentage(percentage)
                    console.log("Dashboard synced savings target from server: $percentage%")
                }
            } catch (e: Exception) {
                console.log("Could not sync settings from server: ${e.message}")
                if (e.message == "AUTH_REQUIRED") {
                    onAuthError()
                }
            }
        }
    }

    /**
     * Map Plaid detailed category to refined DISPLAY category name.
     * Delegates to shared CategoryUtils — single source of truth.
     * Returns localized display names that MATCH mapBudgetCategoryToPlaidCategory().
     */
    private fun getEffectiveDisplayCategory(primaryCategory: String, detailedCategory: String): String {
        return utils.CategoryUtils.getEffectiveDisplayCategory(primaryCategory, detailedCategory)
    }

    /**
     * Load hardcoded demo data when no bank account is linked.
     */
    private fun loadDemoData() {
        isNoAccountLinked = true
        transactionsLoaded = true
        val currentYear = Date().getFullYear()
        val currentMonth = (Date().getMonth() + 1).toInt() // JS months are 0-based
        val demoIncome = listOf(4200.0, 4200.0, 4500.0, 4200.0, 4200.0, 5100.0, 4200.0, 4200.0, 4600.0, 4200.0, 4200.0, 6300.0)
        val demoExpenses = listOf(3100.0, 3450.0, 3200.0, 2900.0, 3600.0, 3400.0, 3800.0, 3100.0, 3300.0, 3550.0, 4100.0, 5200.0)

        // Current year: only show months up to (and including) the current month
        yearlyData[currentYear] = (1..currentMonth).associate { month ->
            month to MonthlyFinanceData(
                month = month,
                confirmedIncome = demoIncome[month - 1],
                realizedExpenses = demoExpenses[month - 1],
                actualSavings = demoIncome[month - 1] - demoExpenses[month - 1],
                goalAllocations = demoIncome[month - 1] * 0.20
            )
        }
        // Previous year: all 12 months of history
        yearlyData[currentYear - 1] = (1..12).associate { month ->
            month to MonthlyFinanceData(
                month = month,
                confirmedIncome = demoIncome[month - 1] * 0.92,
                realizedExpenses = demoExpenses[month - 1] * 0.95,
                actualSavings = (demoIncome[month - 1] * 0.92) - (demoExpenses[month - 1] * 0.95),
                goalAllocations = demoIncome[month - 1] * 0.92 * 0.18
            )
        }

        // Demo category breakdown for secondary charts (pie + bar)
        val categoryBreakdown = listOf(
            Triple("RENT_AND_UTILITIES", "RENT_AND_UTILITIES_RENT", 1200.0),
            Triple("FOOD_AND_DRINK", "FOOD_AND_DRINK_RESTAURANTS", 320.0),
            Triple("TRANSPORTATION", "TRANSPORTATION_GAS", 145.0),
            Triple("GENERAL_MERCHANDISE", "GENERAL_MERCHANDISE_SUPERSTORES", 210.0),
            Triple("ENTERTAINMENT", "ENTERTAINMENT_SPORTING_EVENTS", 92.0),
            Triple("RENT_AND_UTILITIES", "RENT_AND_UTILITIES_GAS_AND_ELECTRICITY", 80.0),
            Triple("PERSONAL_CARE", "PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS", 48.0),
            Triple("GENERAL_SERVICES", "GENERAL_SERVICES_OTHER_GENERAL_SERVICES", 55.0)
        )

        // Category spending bar chart uses transactionsByCategory (current month)
        transactionsByCategory.clear()
        transactionsByCategory["Rent/Mortgage"] = 1200.0
        transactionsByCategory["Food & Drink"] = 320.0
        transactionsByCategory["Transportation"] = 145.0
        transactionsByCategory["Shopping"] = 210.0
        transactionsByCategory["Entertainment"] = 92.0
        transactionsByCategory["Utilities"] = 80.0
        transactionsByCategory["Personal Care"] = 48.0
        transactionsByCategory["Services"] = 55.0

        // Populate demo allTransactions for rolling charts (savings trend line + pie chart)
        allTransactions.clear()
        val prevYear = currentYear - 1

        // Previous year: full 12 months of history
        for (month in 1..12) {
            allTransactions.add(TransactionData(
                amount = demoIncome[month - 1] * 0.92, category = "INCOME",
                detailedCategory = "INCOME_WAGES", year = prevYear, month = month,
                isIncome = true, accountId = "demo"
            ))
            categoryBreakdown.forEach { (cat, detailed, baseAmt) ->
                allTransactions.add(TransactionData(
                    amount = baseAmt * 0.95, category = cat, detailedCategory = detailed,
                    year = prevYear, month = month, isIncome = false, accountId = "demo"
                ))
            }
        }

        // Current year: only up to current month (no future data)
        for (month in 1..currentMonth) {
            allTransactions.add(TransactionData(
                amount = demoIncome[month - 1], category = "INCOME",
                detailedCategory = "INCOME_WAGES", year = currentYear, month = month,
                isIncome = true, accountId = "demo"
            ))
            categoryBreakdown.forEach { (cat, detailed, baseAmt) ->
                allTransactions.add(TransactionData(
                    amount = baseAmt, category = cat, detailedCategory = detailed,
                    year = currentYear, month = month, isIncome = false, accountId = "demo"
                ))
            }
        }

        selectedAccountIds.add("demo")
        transactionsVersion++
        // Demo data IS the real content for this path — reveal it (set here rather than
        // at the call sites so every caller of loadDemoData is covered).
        dashboardDataReady = true
        refreshDashboard()
    }

    /**
     * Load transaction data from server for charts.
     * Stores both income (negative amounts) and expenses (positive amounts).
     */
    private fun loadTransactionData() {
        if (isDemoMode) {
            loadDemoData()
            return
        }
        GlobalScope.launch {
            try {
                val apiService = ApiService()

                // C2 perf fix: these round trips are independent of each other — fire them
                // all concurrently instead of awaiting one after another (was 5 sequential
                // round trips). The only genuine data dependency kept serial: restoring
                // selected_context_ids only makes sense (and, to match prior behaviour,
                // only happens) once the contexts fetch itself has succeeded — same as the
                // original nested try/catch pairing.
                val transactionsDeferred = async { apiService.getTransactionsFromServer() }
                val deselectedDeferred = async {
                    try { apiService.getSetting("deselected_account_ids") ?: "" } catch (_: Exception) { "" }
                }
                val contextsAndSelectionDeferred = async {
                    try {
                        val contextResponse = apiService.getContexts()
                        val savedSelection = try { apiService.getSetting("selected_context_ids") } catch (_: Exception) { null }
                        contextResponse to savedSelection
                    } catch (e: Exception) {
                        console.log("⚠️ Dashboard: Could not load contexts: ${e.message}")
                        null
                    }
                }
                val colorJsonDeferred = async {
                    try { apiService.getSetting("custom_category_colors") } catch (_: Exception) { null }
                }

                val response = transactionsDeferred.await()

                // Store account info and restore persisted selection from server
                allAccounts.clear()
                selectedAccountIds.clear()
                val deselectedStr = deselectedDeferred.await()
                val deselectedIds = if (deselectedStr.isNotEmpty()) deselectedStr.split(",").toSet() else emptySet()
                response.accounts.forEach { account ->
                    allAccounts.add(AccountInfo(
                        accountId = account.account_id,
                        name = account.name,
                        officialName = account.official_name,
                        mask = account.mask,
                        customName = account.custom_name
                    ))
                    if (account.account_id !in deselectedIds) {
                        selectedAccountIds.add(account.account_id)
                    }
                }
                // If all were deselected (e.g. stale data), select all
                if (selectedAccountIds.isEmpty()) {
                    allAccounts.forEach { selectedAccountIds.add(it.accountId) }
                }
                console.log("📊 Dashboard: Loaded ${allAccounts.size} accounts")

                // Load contexts for filtering
                contextsAndSelectionDeferred.await()?.let { (contextResponse, savedSelection) ->
                    allContexts.clear()
                    contextResponse.contexts.forEach { ctx ->
                        allContexts.add(ContextInfo(id = ctx.id, name = ctx.name, color = ctx.color))
                    }
                    console.log("📊 Dashboard: Loaded ${allContexts.size} contexts")
                    // Restore persisted context selection from server
                    if (!savedSelection.isNullOrBlank()) {
                        selectedContextIds.clear()
                        savedSelection.split(",").filter { it.isNotBlank() }.forEach { selectedContextIds.add(it) }
                    }
                }

                // Load user custom category colors
                val colorJson = colorJsonDeferred.await()
                if (!colorJson.isNullOrBlank()) {
                    userCategoryColors = Json.decodeFromString(colorJson)
                }

                smoothedMonthlyIncome = response.estimated_monthly_income

                // Store ALL transactions for later filtering
                allTransactions.clear()
                transactionsByCategory.clear()

                response.transactions.forEach { txn ->
                    // Use personal_finance_category.primary if available, else category list
                    val category = txn.personal_finance_category?.primary
                        ?: txn.category?.firstOrNull()
                        ?: "OTHER"

                    // Get detailed category for finer-grained classification
                    val detailedCategory = txn.personal_finance_category?.detailed ?: category

                    // Debug: Log RENT_AND_UTILITIES transactions to verify splitting
                    if (category.uppercase() == "RENT_AND_UTILITIES") {
                        console.log("🏠 RENT_AND_UTILITIES txn: ${txn.name} - \$${txn.amount}")
                        console.log("   primary: $category, detailed: $detailedCategory")
                        val refinedCat = getEffectiveDisplayCategory(category, detailedCategory)
                        console.log("   -> refined to: $refinedCat")
                    }

                    // In Plaid: positive = expense, negative = income
                    val isIncome = txn.amount < 0

                    // Only add expenses to category spending chart (using refined category)
                    // Skip split/distributed parents, excluded, and transfers
                    // (savings transfers / card payments aren't spending — see
                    // models.PlaidCategories.isTransfer).
                    if (!isIncome && !txn.is_split_parent && !txn.is_distributed_parent && !txn.is_excluded &&
                        !models.PlaidCategories.isTransfer(category, detailedCategory)) {
                        val refinedCategory = getEffectiveDisplayCategory(category, detailedCategory)
                        transactionsByCategory[refinedCategory] = (transactionsByCategory[refinedCategory] ?: 0.0) + txn.amount
                    }

                    // Parse date and store transaction data (both income and expenses)
                    val dateParts = txn.date.split("-")
                    if (dateParts.size >= 2) {
                        val txnYear = dateParts[0].toIntOrNull() ?: 0
                        val txnMonth = dateParts[1].toIntOrNull() ?: 0
                        val txnDay = dateParts.getOrNull(2)?.toIntOrNull() ?: 1
                        allTransactions.add(TransactionData(
                            amount = kotlin.math.abs(txn.amount), // Store absolute value
                            category = category,
                            detailedCategory = detailedCategory,
                            year = txnYear,
                            month = txnMonth,
                            isIncome = isIncome,
                            accountId = txn.account_id,
                            isSplitParent = txn.is_split_parent,
                            isDistributedParent = txn.is_distributed_parent,
                            isExcluded = txn.is_excluded,
                            assignedGoalAmount = txn.assigned_goal_amount,
                            goalAllocations = txn.assigned_goals.associate { it.goalId to it.amount },
                            contextIds = txn.context_ids,
                            day = txnDay,
                            name = txn.name,
                        ))
                    }
                }

                transactionsLoaded = true
                transactionsVersion++
                val excludedTransactionCount = response.transactions.count { it.is_excluded }
                val excludedCount = allTransactions.count { it.excludeFromTotals }
                console.log("📊 Dashboard: Loaded ${response.transactions.size} transactions")
                console.log("   - Excluded transactions from API: $excludedTransactionCount")
                console.log("   - Excluded from totals: $excludedCount")
                response.transactions.filter { it.is_excluded }.forEach { txn ->
                    console.log("   - EXCLUDED: ${txn.name} \$${txn.amount} on ${txn.date}")
                }
                console.log("   - Expenses: ${allTransactions.count { !it.isIncome }}")
                console.log("   - Income: ${allTransactions.count { it.isIncome }}")
                console.log("   - Categories: ${transactionsByCategory.size}")
                transactionsByCategory.forEach { (cat, amount) ->
                    console.log("   - $cat: \$${amount.toInt()}")
                }

                // Update yearlyData with actual transaction amounts
                updateYearlyDataWithActualTransactions()

                // Real numbers are in. Don't reveal yet — the triple bar chart also needs
                // the budget plans, and revealing now means the user sees it render
                // without budget bars and then rebuild with them.
                transactionsSettled = true
                maybeRevealDashboard()
            } catch (e: Exception) {
                console.log("⚠️ Dashboard: Could not load transactions: ${e.message}")
                when {
                    e.message == "AUTH_REQUIRED" -> onAuthError()
                    e.message?.contains("No Plaid access token", ignoreCase = true) == true -> loadDemoData()
                }
                // Even on failure, stop holding the skeleton on this loader's account —
                // render the real (empty or demo) dashboard rather than a placeholder.
                transactionsSettled = true
                maybeRevealDashboard()
            }
        }
    }

    /**
     * Adapts [TransactionData] to the shared monthly aggregator.
     *
     * Web stores the MAGNITUDE (`abs(plaidAmount)`) plus an [TransactionData.isIncome]
     * flag; [analytics.MonthlyTotalsAggregator.TxnLike] wants the raw Plaid sign
     * (positive = expense, negative = income), so re-sign it here. The eligibility
     * flags are forwarded, not filtered — the aggregator is what drops them.
     */
    private class MonthlyTxn(private val t: TransactionData) : analytics.MonthlyTotalsAggregator.TxnLike {
        override val amount: Double get() = if (t.isIncome) -t.amount else t.amount
        override val year: Int get() = t.year
        override val month: Int get() = t.month
        override val excluded: Boolean get() = t.excludeFromTotals
        override val isTransfer: Boolean get() = t.isTransfer
        override val goalAllocation: Double get() = t.assignedGoalAmount ?: 0.0
        override val goalAllocations: Map<String, Double> get() = t.goalAllocations
    }

    /**
     * C3 perf fix: cached result of [computeMonthlyAggregates] plus the inputs it was
     * computed from. Recomputed only when one of those inputs actually changed —
     * [transactionsVersion] (bumped whenever allTransactions is repopulated; the list
     * itself is cleared+refilled in place rather than reassigned, so its object identity
     * never changes), the selected-context filter, or the selected (non-deselected)
     * accounts. Avoids repeating the full-list scan when
     * updateYearlyDataWithActualTransactions() runs multiple times against unchanged data.
     */
    private var cachedAggregates: Map<Int, List<analytics.MonthlyTotalsAggregator.MonthlyTotals>>? = null
    private var cachedAggregatesKey: Triple<Int, Set<String>, Set<String>>? = null

    /**
     * Single pass over allTransactions producing per (year, month) income / expense /
     * goal-allocation totals — replaces what used to be 3 separate full-list filters
     * per year (getMonthlyIncomeForYear/getMonthlyExpensesForYear/
     * getMonthlyGoalAllocationsForYear), i.e. 6-9 full passes per refresh across 2-3
     * years.
     *
     * Account and context scoping stays here (it's live UI state); which rows may
     * contribute, and to what, is [analytics.MonthlyTotalsAggregator]'s call — the
     * one copy of that rule, shared with iOS and Android.
     */
    private fun computeMonthlyAggregates(): Map<Int, List<analytics.MonthlyTotalsAggregator.MonthlyTotals>> {
        val key = Triple(transactionsVersion, selectedAccountIds.toSet(), selectedContextIds.toSet())
        cachedAggregates?.let { if (cachedAggregatesKey == key) return it }

        val scoped = allTransactions
            .filter { it.accountId in selectedAccountIds && it.passesContextFilter() }
            .map { MonthlyTxn(it) }

        return analytics.MonthlyTotalsAggregator.aggregateByYear(scoped).also {
            cachedAggregates = it
            cachedAggregatesKey = key
        }
    }

    /**
     * Update yearlyData with actual transaction amounts for realized values.
     * Layer 1 (planned) comes from Budget Planning API (cached in cachedBudgetPlans)
     * Layer 2 (realized) comes from actual transactions
     */
    private fun updateYearlyDataWithActualTransactions() {
        if (isNoAccountLinked) return  // Don't overwrite demo data
        // Get unique years from transactions
        val yearsWithTransactions = allTransactions.map { it.year }.toSet()
        console.log("📊 Dashboard: Updating yearlyData with actual transactions for years: $yearsWithTransactions")

        // Get years from rolling window (for left pane) + selected year and previous (for right pane)
        val rollingWindowYears = getRolling12MonthWindow().map { it.year }.toSet()
        val yearsToUpdate = (rollingWindowYears + selectedYear + (selectedYear - 1)).toSet()

        val aggregates = computeMonthlyAggregates()

        // Update all required years
        yearsToUpdate.forEach { year ->
            val monthlyTotals = analytics.MonthlyTotalsAggregator.monthsOf(aggregates, year)

            val existingData = yearlyData[year]?.toMutableMap() ?: mutableMapOf()

            (1..12).forEach { month ->
                val existing = existingData[month]
                val totals = monthlyTotals[month - 1]
                val realizedIncome = totals.income
                val realizedExpense = totals.expenses
                val actualSavings = realizedIncome - realizedExpense

                existingData[month] = MonthlyFinanceData(
                    month = month,
                    expectedIncome = existing?.expectedIncome ?: 0.0,
                    confirmedIncome = realizedIncome,  // From actual transactions
                    plannedExpenses = existing?.plannedExpenses ?: 0.0,
                    realizedExpenses = realizedExpense,  // From actual transactions
                    targetSavings = savingsTarget / 100.0 * realizedIncome,  // Target % × actual income
                    actualSavings = actualSavings,  // Calculated from actual transactions
                    goalAllocations = totals.goalAllocations,
                    smoothedMonthlyIncome = smoothedMonthlyIncome,
                )
            }

            yearlyData[year] = existingData
            console.log("📊 Dashboard: Year $year - Total realized income: \$${monthlyTotals.sumOf { it.income }.toInt()}, expenses: \$${monthlyTotals.sumOf { it.expenses }.toInt()}")
        }
    }

    /**
     * Map user-friendly budget category names to DISPLAY category names.
     * This maps the DefaultExpenseCategories from Budget Planning to the same display names
     * used by getEffectiveDisplayCategory() for actual spending.
     * IMPORTANT: Must return the same strings as getEffectiveDisplayCategory()!
     */
    private fun mapBudgetCategoryToPlaidCategory(budgetCategory: String): String {
        // Delegates to shared CategoryUtils — extracts primary from detailed codes so budget
        // keys match getEffectiveDisplayCategory() keys used for transaction grouping.
        return utils.CategoryUtils.mapBudgetCategoryToDisplayCategory(budgetCategory)
    }

    fun render(): HTMLElement {
        console.log("[Dashboard] render() called - START")
        window.asDynamic().DASHBOARD_RENDER_CALLED = true
        // Install the halo keyframes so the first-visit ⓘ pulse works.
        GuideOverlay.ensureStylesInstalled()

        // Until the first transaction load lands, show a skeleton rather than the full
        // dashboard chrome filled with zeroes — an all-zero dashboard reads as "broken",
        // not "loading". loadTransactionData() flips dashboardDataReady and re-renders,
        // which swaps this for the real thing.
        if (!dashboardDataReady) {
            val skeleton = renderLoadingSkeleton()
            container = skeleton
            return skeleton
        }

        val dashboardContainer = document.createElement("div") as HTMLElement
        dashboardContainer.className = "dashboard-redesigned"
        container = dashboardContainer

        // render() runs MORE THAN ONCE per visit: the first pass paints with no data,
        // then performDashboardRefresh() re-renders once the loaders land. Each pass
        // builds a brand new container, so any grid from the previous pass is bound to
        // an element that is no longer on the page. Forget it here, or initGridStack()
        // sees a non-null `grid`, skips, and the container the user is actually looking
        // at never gets a Gridstack instance -- widgets then lay themselves out with no
        // engine, which reads as "my sizes were rejected and the order is scrambled".
        grid = null

        // Start loading goals for recommendations
        if (!goalsLoaded) {
            goalsViewModel.onStateChanged = {
                goalsLoaded = true
                if (useGridLayout) {
                    renderWidgetContent("recommendation")
                } else {
                    // Re-render the recommendation section in the fixed layout
                    val recEl = container?.querySelector("#recommendation-panel") as? HTMLElement
                    recEl?.let {
                        it.innerHTML = renderRecommendationPanel()
                        setupRecommendationEventListeners(container!!)
                    }
                }
            }
            goalsViewModel.startObserving()
        }

        val isMobile = ResponsiveUtils.isMobile()

        dashboardContainer.innerHTML = """
            <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: transparent; min-height: 100dvh;">
                <!-- Top Navigation Bar -->
                ${renderTopNav(isMobile)}

                ${if (isNoAccountLinked) """
                <!-- Demo Mode Banner -->
                <div style="
                    background: linear-gradient(135deg, rgba(0,217,255,0.12) 0%, rgba(0,184,217,0.08) 100%);
                    border-bottom: 1px solid rgba(0,217,255,0.25);
                    padding: 12px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 12px;
                ">
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <span style="
                            background: rgba(0,217,255,0.2);
                            color: ${WebTheme.current.accent};
                            border: 1px solid rgba(0,217,255,0.4);
                            border-radius: 4px;
                            padding: 2px 8px;
                            font-size: 11px;
                            font-weight: 700;
                            letter-spacing: 0.5px;
                            text-transform: uppercase;
                        ">${StringKey.DASHBOARD_DEMO_BADGE.tr()}</span>
                        <div>
                            <span style="color: ${t.textPrimary}; font-size: 14px; font-weight: 600;">${StringKey.DASHBOARD_DEMO_CONNECT_TITLE.tr()}</span>
                            <span style="color: ${t.textSecondary}; font-size: 13px; margin-left: 8px;">${StringKey.DASHBOARD_DEMO_CONNECT_DESC.tr()}</span>
                        </div>
                    </div>
                    <button id="demo-connect-btn" style="
                        background: linear-gradient(135deg, ${t.accent} 0%, #00b8d9 100%);
                        color: ${t.background};
                        border: none;
                        border-radius: 20px;
                        padding: 8px 20px;
                        font-size: 13px;
                        font-weight: 600;
                        cursor: pointer;
                        white-space: nowrap;
                    ">${StringKey.DASHBOARD_DEMO_CONNECT_CTA.tr()}</button>
                </div>
                """ else ""}

                <!-- Mobile Menu Overlay (hidden by default) -->
                ${MarketingMenu.mobileOverlayHtml()}

                ${if (useGridLayout) renderGridLayoutHtml(isMobile) else renderFixedLayoutHtml(isMobile)}
            </div>
        """

        setupEventListeners(dashboardContainer)

        if (useGridLayout) {
            // Initialize GridStack after DOM is ready
            window.setTimeout({
                initGridStack()
            }, 0)
        }

        return dashboardContainer
    }

    /** Render the fixed two-panel layout (standard tier) */
    private fun renderFixedLayoutHtml(isMobile: Boolean): String {
        val gap = if (isMobile) "16px" else "24px"
        val padding = if (isMobile) "16px" else "24px 32px"
        return """
            <!-- Main Content - Fixed Layout -->
            <div style="display: flex; flex-direction: ${if (isMobile) "column" else "row"}; gap: $gap; padding: $padding;">
                ${if (isMobile) {
                    """
                    <div style="width: 100%;">${renderRightPanel(true)}</div>
                    <div style="width: 100%;">${renderLeftPanel()}</div>
                    """
                } else {
                    """
                    <div id="dashboard-left-panel" style="flex: 0 0 280px; min-width: 240px; max-width: 320px; position: sticky; top: 80px; align-self: flex-start;">
                        ${renderLeftPanel()}
                    </div>
                    <div id="dashboard-right-panel" style="flex: 1; min-width: 500px;">
                        ${renderRightPanel(false)}
                    </div>
                    """
                }}
            </div>
        """
    }

    /** Render the grid-based customizable layout (Pro tier) */
    private fun renderGridLayoutHtml(isMobile: Boolean): String {
        return """
            <!-- Global month indicator -->
            <div style="padding: ${if (isMobile) "8px 16px 0" else "8px 32px 0"};">${renderMonthStepper()}</div>
            <!-- Customize toolbar -->
            <div style="padding: ${if (isMobile) "8px 16px" else "8px 32px"};">
                <button id="toolbar-toggle" data-guide="customize-toolbar" style="
                    display: inline-flex; align-items: center; gap: 6px;
                    padding: 8px 14px; border: 1px solid ${t.border}; border-radius: 8px;
                    background: ${t.card}; color: ${t.textSecondary}; cursor: pointer; font-size: 13px;
                    transition: all 0.15s; margin-bottom: 4px;
                " onmouseover="this.style.borderColor='${t.accent}';this.style.color='${t.textPrimary}'" onmouseout="this.style.borderColor='${t.border}';this.style.color='${t.textSecondary}'">
                    <svg id="toolbar-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" style="transition: transform 0.2s;">
                        <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    ${if (LocaleManager.getCurrentLocale().code == "fr") "Personnaliser" else "Customize"}
                </button>
                <span id="pinned-layouts-bar" data-guide="layouts-switch" style="display: inline-flex; gap: 6px; margin-left: 8px; vertical-align: middle; flex-wrap: wrap;"></span>

                <!-- Collapsible panel -->
                <div id="toolbar-panel" style="display: none; padding: 16px 0 12px; border-bottom: 1px solid ${t.border}; margin-bottom: 8px;">
                    <!-- Widgets section -->
                    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 10px;">
                        <span style="color: ${t.textSecondary}; font-size: 13px; font-weight: 600; letter-spacing: 0.3px; border-left: 3px solid ${t.accent}; padding-left: 8px;">Widgets</span>
                        <button id="btn-add-all-widgets" style="
                            padding: 4px 10px; border: 1px solid ${t.border}; border-radius: 5px;
                            background: transparent; color: ${t.textSecondary}; cursor: pointer; font-size: 10px;
                            transition: all 0.15s;
                        " onmouseover="this.style.color='${t.textPrimary}';this.style.borderColor='${t.textSecondary}'" onmouseout="this.style.color='${t.textSecondary}';this.style.borderColor='${t.border}'">${if (LocaleManager.getCurrentLocale().code == "fr") "Tout afficher" else "Add All"}</button>
                        <button id="btn-remove-all-widgets" style="
                            padding: 4px 10px; border: 1px solid ${t.border}; border-radius: 5px;
                            background: transparent; color: ${t.textSecondary}; cursor: pointer; font-size: 10px;
                            transition: all 0.15s;
                        " onmouseover="this.style.color='${t.textPrimary}';this.style.borderColor='${t.textSecondary}'" onmouseout="this.style.color='${t.textSecondary}';this.style.borderColor='${t.border}'">${if (LocaleManager.getCurrentLocale().code == "fr") "Tout retirer" else "Remove All"}</button>
                    </div>
                    <div id="palette-items" style="display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 16px;">
                        ${renderPaletteItems()}
                    </div>

                    <!-- Layouts section -->
                    <div style="display: flex; align-items: center; gap: 12px; margin-bottom: 12px;">
                        <span style="color: ${t.textSecondary}; font-size: 13px; font-weight: 600; letter-spacing: 0.3px; border-left: 3px solid ${t.accent}; padding-left: 8px;">Layouts</span>
                        <button id="btn-default-layout" style="
                            padding: 4px 10px; border: 1px solid ${t.border}; border-radius: 5px;
                            background: transparent; color: ${t.textSecondary}; cursor: pointer; font-size: 10px;
                            transition: all 0.15s;
                        " onmouseover="this.style.color='${t.textPrimary}';this.style.borderColor='${t.textSecondary}'" onmouseout="this.style.color='${t.textSecondary}';this.style.borderColor='${t.border}'">${if (LocaleManager.getCurrentLocale().code == "fr") "Par d\u00e9faut" else "Default Layout"}</button>
                        <button id="btn-save-layout" data-guide="layouts-save" style="
                            padding: 4px 10px; border: 1px solid ${t.border}; border-radius: 5px;
                            background: transparent; color: ${t.textSecondary}; cursor: pointer; font-size: 10px;
                            transition: all 0.15s;
                        " onmouseover="this.style.color='${t.textPrimary}';this.style.borderColor='${t.textSecondary}'" onmouseout="this.style.color='${t.textSecondary}';this.style.borderColor='${t.border}'">+ ${if (LocaleManager.getCurrentLocale().code == "fr") "Sauvegarder" else "Save Layout"}</button>
                    </div>
                    <div id="saved-layouts-list"></div>
                </div>
            </div>

            <!-- Full-width GridStack Grid -->
            <div style="padding: ${if (isMobile) "0 8px" else "0 24px"};">
                <div class="grid-stack" id="dashboard-grid" data-guide="grid-canvas"></div>
            </div>
        """
    }

    // --- Fixed layout helper functions (standard tier) ---

    /** Render the account selector card for fixed layout */
    private fun renderAccountSelector(): String {
        if (allAccounts.isEmpty()) return ""
        return """
            <div data-guide="account-selector" style="background: ${t.card}; border-radius: 12px; padding: 16px; border: 1px solid ${t.border}; margin-bottom: 16px; box-shadow: ${t.cardShadow};">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
                    <h3 style="color: ${t.textPrimary}; margin: 0; font-size: 14px; font-weight: 500;">${StringKey.DASHBOARD_ACCOUNTS.tr()}</h3>
                    <span style="color: ${t.textSecondary}; font-size: 11px;">${StringKey.DASHBOARD_SELECTED_COUNT.tr(selectedAccountIds.size.toString(), allAccounts.size.toString())}</span>
                </div>
                <div id="accountSelector" style="display: flex; flex-direction: column; gap: 4px;">
                    ${renderAccountSelectorContent()}
                </div>
            </div>
        """
    }

    /** Render the left panel: account selector + chart type buttons */
    private fun renderLeftPanel(): String {
        val chartTypeButtons = ChartType.values().joinToString("") { type ->
            val isSelected = type == selectedChartType
            """
                <button data-chart-type="${type.name}" style="
                    display: flex; align-items: center; gap: 8px; padding: 10px 12px;
                    border: 1px solid ${if (isSelected) t.chipSelectedBorder else t.chipUnselectedBorder}; border-radius: 8px;
                    background: ${if (isSelected) t.chipSelectedBg else t.card};
                    color: ${if (isSelected) t.accent else t.textSecondary}; cursor: pointer; font-size: 13px;
                    width: 100%; text-align: left; transition: all 0.2s;
                ">
                    <span style="display: flex; align-items: center;">${getChartTypeIconSvg(type)}</span>
                    <span>${getChartTypeDisplayName(type)}</span>
                </button>
            """
        }
        return """
            ${renderAccountSelector()}
            <div data-guide="chart-type-selector" style="background: ${t.card}; border-radius: 12px; padding: 16px; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                <div style="display: flex; flex-direction: column; gap: 6px;">$chartTypeButtons</div>
            </div>
        """
    }

    /** Render the currently selected chart */
    private fun renderChart(): String {
        return when (selectedChartType) {
            ChartType.SAVINGS_LINE -> renderSavingsLineChart()
            ChartType.BAR_CHART -> renderBarChartPlaceholder()
            ChartType.PIE_CHART -> renderPieChartPlaceholder()
            ChartType.EXPENSE_TIMELINE -> renderTimelinePlaceholder()
            ChartType.PREV_YEAR_COMPARISON -> renderTripleBarChart(selectedYear - 1, isComparison = true)
            ChartType.NEEDS_WANTS_FLOW -> renderNeedsWantsFlowSurface(720, 360)
        }
    }

    /** Render the right panel: focal savings, year chart, selected chart, recommendations */
    private fun renderRightPanel(isMobile: Boolean = false): String {
        val currentYear = Date().getFullYear()
        val minYear = getMinYearFromData()
        val maxYear = currentYear
        return """
            <div style="display: flex; flex-direction: column; gap: 16px;">
                ${renderMonthStepper()}
                <div style="height: 180px;">${renderFocalSavingsDisplay()}</div>
                <div data-guide="monthly-bars" style="background: ${t.card}; border-radius: 12px; padding: ${if (isMobile) "16px" else "20px"}; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                    <div style="display: flex; align-items: center; gap: 8px; margin: 0 0 16px 0;">
                        <button id="yearPrev" style="
                            background: transparent; border: none;
                            color: ${if (selectedYear > minYear) "#3b82f6" else "rgba(136,136,136,0.3)"};
                            font-size: 16px; cursor: ${if (selectedYear > minYear) "pointer" else "not-allowed"};
                            padding: 2px 6px; display: flex; align-items: center;
                        " ${if (selectedYear <= minYear) "disabled" else ""}>◀</button>
                        <span id="yearLabel" style="color: ${t.textPrimary}; font-size: 16px; font-weight: 600; min-width: 50px; text-align: center;">${displayYearLabel(selectedYear)}</span>
                        <button id="yearNext" style="
                            background: transparent; border: none;
                            color: ${if (selectedYear < maxYear) "#3b82f6" else "rgba(136,136,136,0.3)"};
                            font-size: 16px; cursor: ${if (selectedYear < maxYear) "pointer" else "not-allowed"};
                            padding: 2px 6px; display: flex; align-items: center;
                        " ${if (selectedYear >= maxYear) "disabled" else ""}>▶</button>
                    </div>
                    <div id="classic-triple-bar">${renderTripleBarChart(selectedYear)}</div>
                </div>
                <div data-guide="active-chart" style="background: ${t.card}; border-radius: 12px; padding: ${if (isMobile) "16px" else "20px"}; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                    <div id="selectedChartArea">${renderChart()}</div>
                </div>
                <div data-guide="recommendation-panel" id="recommendation-panel">${renderRecommendationPanel()}</div>
            </div>
        """
    }

    /** Render the sidebar widget palette */
    private fun renderPaletteItems(): String {
        return widgetDefinitions.joinToString("") { widget ->
            val desc = widgetDescription(widget.id).replace("\"", "&quot;")
            """
            <div class="palette-item"
                 data-widget-id="${widget.id}"
                 id="palette-${widget.id}"
                 title="$desc"
                 style="
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 8px 14px;
                    border: 1px solid ${t.chipUnselectedBorder};
                    border-radius: 8px;
                    background: ${t.card};
                    color: ${t.chipUnselectedText};
                    cursor: pointer;
                    font-size: 12px;
                    transition: all 0.15s;
                    user-select: none;
                    white-space: nowrap;
                 "
                 onmouseover="this.style.borderColor='${t.accent}'; this.style.color='${t.textPrimary}'; this.style.background='${t.chipUnselectedBg}'"
                 onmouseout="this.style.borderColor='${t.chipUnselectedBorder}'; this.style.color='${t.chipUnselectedText}'; this.style.background='${t.card}'">
                <span style="font-size: 14px;">${widget.icon}</span>
                ${widget.title}
                <span style="color: #555; font-size: 12px; margin-left: 2px;">+</span>
            </div>
            """
        }
    }


    /** One-time migration: copy old un-prefixed layout keys to the new prefixed keys */
    private fun migrateOldLayoutKeys() {
        if (localStorage.getItem("dashboard_layouts_migrated") != null) return
        val keys = listOf("dashboard_layout", "dashboard_layouts", "dashboard_active_layout", "dashboard_pinned_layouts")
        keys.forEach { key ->
            val old = localStorage.getItem(key)
            if (old != null && localStorage.getItem(layoutKey(key)) == null) {
                // Copy to desktop_ prefix (old keys were always desktop)
                localStorage.setItem("desktop_$key", old)
            }
        }
        localStorage.setItem("dashboard_layouts_migrated", "1")
    }

    /** Ensure apiService is authenticated before server calls */
    private fun ensureApiAuth(): Boolean {
        val sharedToken = ApiService.getSharedAuthToken()
        val sharedUserId = ApiService.getSharedUserId()
        if (sharedToken == null || sharedUserId == null) return false
        if (!apiService.isAuthenticated()) {
            apiService.restoreSession(sharedToken, sharedUserId)
        }
        return apiService.isAuthenticated()
    }

    /** Sync layout data to the server (fire-and-forget) */
    private fun syncLayoutsToServer() {
        if (isDemoMode || !ensureApiAuth()) return
        GlobalScope.launch {
            try {
                val layouts = localStorage.getItem(layoutKey("dashboard_layouts"))
                val active = localStorage.getItem(layoutKey("dashboard_active_layout"))
                val pinned = localStorage.getItem(layoutKey("dashboard_pinned_layouts"))
                val autoSave = localStorage.getItem(layoutKey("dashboard_layout"))
                val locked = localStorage.getItem(layoutKey("dashboard_locked_layouts"))
                if (layouts != null) apiService.setSetting(layoutKey("dashboard_layouts"), layouts)
                if (active != null) apiService.setSetting(layoutKey("dashboard_active_layout"), active)
                if (pinned != null) apiService.setSetting(layoutKey("dashboard_pinned_layouts"), pinned)
                if (autoSave != null) apiService.setSetting(layoutKey("dashboard_layout"), autoSave)
                if (locked != null) apiService.setSetting(layoutKey("dashboard_locked_layouts"), locked)
            } catch (e: Exception) {
                console.log("Dashboard: Failed to sync layouts to server: ${e.message}")
            }
        }
    }

    /** Load layout data from server into localStorage (if localStorage is empty) */
    private suspend fun loadLayoutsFromServer() {
        if (isDemoMode || !ensureApiAuth()) return
        try {
            val keys = listOf("dashboard_layouts", "dashboard_active_layout", "dashboard_pinned_layouts", "dashboard_layout", "dashboard_locked_layouts")
            keys.forEach { key ->
                val prefixedKey = layoutKey(key)
                if (localStorage.getItem(prefixedKey) == null) {
                    val serverValue = apiService.getSetting(prefixedKey)
                    if (serverValue != null) {
                        localStorage.setItem(prefixedKey, serverValue)
                    }
                }
            }
        } catch (e: Exception) {
            console.log("Dashboard: Failed to load layouts from server: ${e.message}")
        }
    }

    // Debounce timer for server sync
    private var syncTimerId: Int? = null
    private fun debouncedSyncToServer() {
        syncTimerId?.let { window.clearTimeout(it) }
        syncTimerId = window.setTimeout({ syncLayoutsToServer() }, 2000)
    }

    /** Initialize GridStack, restore layout from localStorage or use defaults */
    private fun initGridStack() {
        val gridEl = container?.querySelector("#dashboard-grid") as? HTMLElement ?: return

        // One dashboard visit can schedule this more than once: render() arms it on a
        // timer, and every loader that settles while `grid` is still null triggers
        // another full re-render (performDashboardRefresh). Running the populate path
        // twice on the SAME container added every widget a second time — including a
        // second `recommendation` at its 12-column default, i.e. a full-width tile in
        // a layout the user had already narrowed.
        //
        // The question is strictly "does THIS element already have a grid", never
        // "does this component have a grid anywhere". Those differ, because render()
        // builds a fresh container on its second pass: an earlier grid is bound to a
        // detached element, and short-circuiting on it left the visible container with
        // no engine at all — widgets laid out by CSS alone, sizes and order lost.
        DashboardGrid.existing(gridEl)?.let { grid = it; return }

        migrateOldLayoutKeys()

        // float: true so a dragged widget LANDS where the user drops it
        // (doesn't snap toward the most-compact position, which felt like
        // widgets were "running away" from the cursor). Compaction is
        // applied via gravityCompactExcept() after drag/remove/resize —
        // a vertical-only pass that fills gaps without the global
        // repack that Gridstack's built-in compact() performs.
        val g = DashboardGrid.attach(gridEl, js("""{
            column: 12,
            cellHeight: 60,
            float: true,
            animate: true,
            handleClass: 'gs-item-drag-handle',
            acceptWidgets: '.palette-item',
            removable: false,
            margin: 8,
            resizable: { handles: 'all' }
        }"""))
        if (g == null) {
            console.error("Dashboard: could not attach Gridstack to #dashboard-grid — leaving the grid unbuilt")
            return
        }
        grid = g

        // Make palette pills draggable into the grid. 'clone' keeps the pill in
        // place; on drop, the 'dropped' handler below converts the dropped clone
        // into a proper widget using our own renderer.
        js("GridStack.setupDragIn('.palette-item', { helper: 'clone', appendTo: 'body' })")

        // localStorage is only a cache of the layout; the copy that follows the user
        // between browsers lives on the server. On a browser that has never shown this
        // dashboard every layout key is empty and the fetch that fills them is still in
        // flight, so restoring right now shows the default set of widgets instead of the
        // user's own.
        //
        // Showing the wrong layout for a moment would be survivable. Silently DESTROYING
        // the user's next arrangement is not, and that is what followed: `activeLayoutName`
        // was still null, so every saveLayout() from then on wrote the auto-save key and
        // left the named layout untouched. The moment the fetch landed, the next visit
        // read `dashboard_active_layout` from it, preferred that named layout, and replayed
        // it over everything the user had just arranged — sizes back to their defaults,
        // order back to the old one, and a reload doesn't help because the good arrangement
        // was never written anywhere that restore looks.
        //
        // So the fetch is part of the restore decision, not a background nicety: with
        // nothing local to restore from, wait for it. Anything local (including the demo
        // and logged-out cases, where the fetch is a no-op) restores immediately as before.
        if (DashboardLayoutStore.hasLocalLayout(
                autoSave = localStorage.getItem(layoutKey("dashboard_layout")),
                namedLayouts = localStorage.getItem(layoutKey("dashboard_layouts")),
                activeName = localStorage.getItem(layoutKey("dashboard_active_layout")),
            )
        ) {
            populateGrid(gridEl, g)
        } else {
            GlobalScope.launch {
                loadLayoutsFromServer()
                populateGrid(gridEl, g)
            }
        }
    }

    /**
     * Restore the persisted layout onto [g] and wire up every listener the grid needs.
     *
     * Split out of [initGridStack] because it can run one turn later than the attach,
     * while the server copy of the layout is being fetched.
     */
    private fun populateGrid(gridEl: HTMLElement, g: GridStack) {
        // render() may have rebuilt the container while the fetch was in flight. That new
        // container gets (or already got) its own grid from its own initGridStack, so
        // populating this one would fill a grid nobody is looking at — and its widget
        // lookups below, which go through `container`, would find nothing.
        if (container?.querySelector("#dashboard-grid") !== gridEl) return

        val isMobile = ResponsiveUtils.isMobile()

        // Restore active layout name and pinned layouts
        activeLayoutName = localStorage.getItem(layoutKey("dashboard_active_layout"))
        localStorage.getItem(layoutKey("dashboard_pinned_layouts"))?.let { raw ->
            try {
                val arr = JSON.parse<Array<String>>(raw)
                pinnedLayoutNames.addAll(arr)
            } catch (_: Exception) {}
        }
        localStorage.getItem(layoutKey("dashboard_locked_layouts"))?.let { raw ->
            try {
                val arr = JSON.parse<Array<String>>(raw)
                lockedLayoutNames.addAll(arr)
            } catch (_: Exception) {}
        }
        // Check if current layout is locked
        isLayoutLocked = activeLayoutName != null && activeLayoutName in lockedLayoutNames

        // Restore layout: prefer active named layout, fallback to auto-save, then defaults
        val namedLayoutJson = if (activeLayoutName != null) {
            val allLayouts = localStorage.getItem(layoutKey("dashboard_layouts"))
            if (allLayouts != null) {
                try {
                    val parsed = JSON.parse<dynamic>(allLayouts)
                    val layout = parsed[activeLayoutName!!]
                    if (layout != null) JSON.stringify(layout) else null
                } catch (_: Exception) { null }
            } else null
        } else null

        val savedLayout = namedLayoutJson ?: localStorage.getItem(layoutKey("dashboard_layout"))
        if (savedLayout != null) {
            try {
                val parsed = JSON.parse<Array<dynamic>>(savedLayout)
                restoreLayoutItems(parsed)
            } catch (e: Exception) {
                console.log("Dashboard: Failed to restore layout, using defaults: ${e.message}")
                localStorage.removeItem(layoutKey("dashboard_layout"))
                addAllDefaultWidgets()
            }
        } else {
            addAllDefaultWidgets()
        }

        // Update palette visibility based on active widgets
        updatePaletteVisibility()

        // Render content into all active widgets and scale to fit
        activeWidgetIds.forEach { renderWidgetContent(it) }
        window.setTimeout({ activeWidgetIds.forEach { scaleWidgetContent(it) } }, 200)

        // Render layouts list
        renderLayoutList()

        // Listen for grid changes to save layout
        grid?.on("change") { _, _ ->
            saveLayout()
        }

        // Listen for removed items
        grid?.on("removed") { _, items ->
            val arr = items.unsafeCast<Array<dynamic>>()
            arr.forEach { item ->
                val id = item.id as? String
                if (id != null) {
                    activeWidgetIds.remove(id)
                    updatePaletteVisibility()
                }
            }
            // Removing widgets is the one way the grid legitimately reaches zero.
            saveLayout(allowEmpty = true)
        }

        // Drag-and-drop from the palette: the 'dropped' event passes
        // (event, previousWidget, newWidget) — the Kotlin typed binding only
        // supports 2-arg handlers, so we attach via dynamic to read newWidget.
        // The dropped node is a raw clone of our palette pill, so we swap it
        // for a real widget at the drop coordinates.
        g.asDynamic().on("dropped") { _: dynamic, _: dynamic, newWidget: dynamic ->
            val droppedEl = newWidget.el as? HTMLElement
            val widgetId = droppedEl?.getAttribute("data-widget-id")
            val widget = widgetDefinitions.firstOrNull { it.id == widgetId }
            val dropX = (newWidget.x as? Int) ?: 0
            val dropY = (newWidget.y as? Int) ?: 0
            if (droppedEl != null) grid?.removeWidget(droppedEl, true)
            if (widget != null && widget.id !in activeWidgetIds) {
                addWidgetToGrid(widget, x = dropX, y = dropY)
                renderWidgetContent(widget.id)
                updatePaletteVisibility()
                saveLayout()
            }
            Unit
        }

        // Listen for resize to re-render chart content, scale, AND close
        // any gap the resize left above the widget. compact() runs after
        // the re-render so the visual flow is: resize → content reflows
        // → neighbours rise to fill the freed space.
        //
        // Gridstack's resizestop event signature is (event, element) —
        // a SINGLE element, not an array of items. The previous handler
        // unsafeCast'd it to Array and silently iterated nothing, so
        // the re-render for adaptive widget content never fired and
        // the chart kept its initial layout on every resize. Diag log
        // confirmed widgetColumnWidth returned the new size (e.g. 4/12)
        // but only on the initial render — resizes produced no second
        // call because this handler ate the event without working.
        // Predictable vertical reflow. Snapshot the layout order BEFORE the
        // resize, because Gridstack scrambles it live (a pushed widget can jump
        // sideways or reorder — the "random" feel). On stop, reflowAroundFixed
        // re-places every other widget straight up around the resized one, in
        // that original order: growing pushes them straight down and keeps their
        // order, shrinking floats them straight back up. x never changes.
        grid?.on("resizestart") { _, _ ->
            resizeOrderSnapshot = snapshotLayoutOrder()
        }

        grid?.on("resizestop") { _, element ->
            val el = element.unsafeCast<HTMLElement?>()
            val id = el?.getAttribute("gs-id")
            reflowAroundFixed(id, resizeOrderSnapshot)
            if (id != null) {
                window.setTimeout({
                    renderWidgetContent(id)
                    scaleWidgetContent(id)
                }, 100)
            }
            saveLayout()
        }

        // Drag dropped — single-element signature.
        //
        // gravityCompactExcept(id) instead of grid.compact() because
        // Gridstack's compact() is a GLOBAL repack that re-homes every
        // non-dropped widget to the topmost-leftmost free cell. That
        // teleports unrelated widgets across the grid (e.g. dropping
        // Accounts at (3,6) once moved savings-streak from (3,3) →
        // (10,2)) which fights the user's mental model of "I only
        // moved one thing." Vertical-gravity moves widgets up only
        // when they have empty cells directly above in their own
        // column range.
        grid?.on("dragstop") { _, element ->
            val el = element.unsafeCast<HTMLElement?>()
            val id = el?.getAttribute("gs-id")
            gravityCompactExcept(id)
            if (id != null) {
                window.setTimeout({ renderWidgetContent(id) }, 100)
            }
            saveLayout()
        }

        // Setup palette button listeners and click-to-add
        setupPaletteButtons()
        setupPaletteClickToAdd()

        // Listen for the demo-mode beats the tour dispatches during
        // specific steps. Self-contained: only fires when GuideOverlay
        // (with ?demo=1 active) emits the event. No-op on normal
        // user sessions.
        kotlinx.browser.document.addEventListener("wiz3r-demo-resize-widget", { _ ->
            performGridCanvasDemoResize()
        })
        kotlinx.browser.document.addEventListener("wiz3r-demo-switch-layout", { _ ->
            performLayoutsSwitchDemo()
        })
        kotlinx.browser.document.addEventListener("wiz3r-demo-open-customize", { _ ->
            performOpenCustomizeDemo()
        })
        kotlinx.browser.document.addEventListener("wiz3r-demo-close-customize", { _ ->
            performCloseCustomizeDemo()
        })

        // Apply lock state and mobile touch behavior
        applyLockState()
        if (isMobile && !isLayoutLocked) {
            setupMobileLongPressHandles()
        }

        // Initial sync: push existing layouts to server (so other browsers can find them)
        if (localStorage.getItem(layoutKey("dashboard_layouts")) != null) {
            debouncedSyncToServer()
        }
    }

    /** Add a widget to the GridStack grid */
    private fun addWidgetToGrid(
        widgetDef: WidgetDef,
        x: Int? = null,
        y: Int? = null,
        w: Int? = null,
        h: Int? = null
    ) {
        val contentHtml = buildWidgetContentHtml(widgetDef)

        // Build DOM element for content
        val widgetEl = kotlinx.browser.document.createElement("div") as HTMLElement
        widgetEl.classList.add("grid-stack-item")
        val contentDiv = kotlinx.browser.document.createElement("div") as HTMLElement
        contentDiv.classList.add("grid-stack-item-content")
        contentDiv.innerHTML = contentHtml
        widgetEl.appendChild(contentDiv)

        // Pass positions as JS number properties on the element's gridstackNode
        // by using addWidget with an options object that includes the 'el' property
        val opts = js("{}")
        opts.el = widgetEl
        opts.id = widgetDef.id
        opts.w = w ?: widgetDef.defaultW
        opts.h = h ?: widgetDef.defaultH
        opts.minW = widgetDef.minW
        opts.minH = widgetDef.minH
        widgetDef.maxH?.let { opts.maxH = it }
        if (x != null) opts.x = x
        if (y != null) opts.y = y
        if (x == null && y == null) opts.autoPosition = true

        try {
            grid?.addWidget(opts)
            activeWidgetIds.add(widgetDef.id)
            attachWidgetRemoveListener(widgetEl, widgetDef.id)
        } catch (e: Exception) {
            console.log("Dashboard: Failed to add widget '${widgetDef.id}': ${e.message}")
        }
    }

    /** Tour-anchor id for a given widget, so the Dashboard tour can spotlight widgets
     *  in Pro (Gridstack) mode. Same ids as the fixed-layout anchors — only one
     *  layout is active at a time, so no collision. */
    private fun tourAnchorForWidget(widgetId: String): String =
        when (widgetId) {
            "savings-rate" -> "savings-rate-card"
            "triple-bar" -> "monthly-bars"
            "accounts" -> "account-selector"
            "category-spending" -> "active-chart"
            "recommendation" -> "recommendation-panel"
            "savings-trend" -> "savings-trend"
            "expense-timeline" -> "expense-timeline"
            else -> ""
        }

    /** Build the inner HTML for a widget card (header + content area) */
    private fun buildWidgetContentHtml(widgetDef: WidgetDef): String {
        return """
            <div style="display: flex; flex-direction: column; height: 100%;">
                <div class="gs-item-drag-handle" style="
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px 20px;
                    background: ${if (t.isDark) "rgba(255,255,255,0.02)" else "rgba(0,0,0,0.02)"};
                    border-bottom: 1px solid ${t.border};
                    flex-shrink: 0;
                    user-select: none;
                    -webkit-user-select: none;
                ">
                    <span style="color: ${t.textSecondary}; font-size: 12px; font-weight: 500; display: flex; align-items: center; gap: 6px;">${widgetDef.icon} ${widgetDef.title}</span>
                    <div style="display: flex; align-items: center; gap: 4px;">
                        <button class="widget-remove-btn" data-widget-id="${widgetDef.id}" style="
                            background: none;
                            border: none;
                            color: ${t.textSecondary};
                            cursor: pointer;
                            font-size: 14px;
                            padding: 0 4px;
                            line-height: 1;
                            transition: color 0.2s;
                        " onmouseover="this.style.color='${t.expenseRed}'" onmouseout="this.style.color='${t.textSecondary}'">&times;</button>
                    </div>
                </div>
                <div id="widget-content-${widgetDef.id}" ${tourAnchorForWidget(widgetDef.id).let { if (it.isNotEmpty()) "data-guide=\"$it\"" else "" }} style="
                    flex: 1;
                    overflow: hidden;
                    padding: 6px;
                    container-type: size;
                "></div>
            </div>
        """
    }

    /** Attach click listener to a widget's remove button */
    private fun attachWidgetRemoveListener(el: HTMLElement, widgetId: String) {
        val removeBtn = el.querySelector(".widget-remove-btn[data-widget-id='$widgetId']")
        removeBtn?.addEventListener("click", { event ->
            event.stopPropagation()
            // Find the grid-stack-item parent
            val gridItem = (removeBtn as HTMLElement).closest(".grid-stack-item")
            if (gridItem != null) {
                grid?.removeWidget(gridItem)
                // Close the gap left by the removed widget. No exclusion
                // needed — the widget is gone, all remaining widgets are
                // candidates to rise into the freed space.
                gravityCompactExcept(null)
                activeWidgetIds.remove(widgetId)
                updatePaletteVisibility()
                saveLayout(allowEmpty = true) // this may have been the last widget
                // Auto-open the customize panel so user sees the widget is available
                val panel = container?.querySelector("#toolbar-panel") as? HTMLElement
                val chevron = container?.querySelector("#toolbar-chevron") as? HTMLElement
                if (panel?.style?.display == "none") {
                    panel.style.display = "block"
                    chevron?.style?.transform = "rotate(180deg)"
                }
            }
        })
    }

    /**
     * Restore a saved layout using GridStack's load() API.
     * This adds all widgets in one batch so positions are respected exactly.
     * Content HTML is injected after load since GridStack escapes it.
     */
    /**
     * Numeric value out of a `dynamic`, or null when it genuinely isn't a number.
     *
     * `value as? Number` is NOT safe here: a plain JS number arriving through a
     * dynamic does not reliably satisfy Kotlin/JS's runtime check, so the cast
     * yields null for perfectly good data and any `?:` fallback fires. Going via
     * the string form is boring and correct for the integers a grid layout holds.
     */
    private fun dynNumber(value: dynamic): Double? {
        if (value == null || value == undefined) return null
        return value.toString().toDoubleOrNull()
    }

    private fun restoreLayoutItems(items: Array<dynamic>) {
        isRestoringLayout = true // Prevent auto-save from corrupting layout mid-restore
        activeWidgetIds.clear()

        // Build a load-ready array with positions but no content
        val loadArray = js("[]")
        items.forEach { item ->
            val id = item.id as? String ?: return@forEach
            val widgetDef = widgetDefinitions.find { it.id == id } ?: return@forEach
            val obj = js("{}")
            obj.id = id
            obj.x = item.x
            obj.y = item.y
            // The saved geometry is passed through UNCHANGED unless we can both
            // read it and prove it is too short.
            //
            // An earlier version wrote
            //     obj.w = maxOf(<read w> ?: widgetDef.defaultW, widgetDef.minW)
            // so any failure to read the saved value silently substituted
            // defaultW — 12 columns for this dashboard — and the widget came back
            // full-width after leaving the tab. Width was never the thing that
            // needed clamping; only height was, to protect widgets whose minH has
            // since been raised (Gridstack's load() honours minH for user resizes
            // but not for loaded geometry). So: never rewrite width, and only
            // clamp height when the saved value actually parsed.
            //
            // maxH clamps the SAME way and under the same rule: a saved height
            // above a widget's maxH is lowered, but only from a value we actually
            // read. There is still no route from restore to defaultW/defaultH.
            obj.w = item.w
            val savedH = dynNumber(item.h)?.toInt()
            obj.h = if (savedH != null) {
                val ceiling = widgetDef.maxH?.coerceAtLeast(widgetDef.minH) ?: Int.MAX_VALUE
                savedH.coerceIn(widgetDef.minH, ceiling)
            } else item.h
            obj.minW = widgetDef.minW
            obj.minH = widgetDef.minH
            widgetDef.maxH?.let { obj.maxH = it }
            loadArray.push(obj)
        }

        // Use GridStack's load() — it handles batch positioning in one pass
        grid?.load(loadArray.unsafeCast<Array<dynamic>>(), true)

        // Now inject content HTML and wire up listeners for each widget
        items.forEach { item ->
            val id = item.id as? String ?: return@forEach
            val widgetDef = widgetDefinitions.find { it.id == id } ?: return@forEach
            val el = container?.querySelector(".grid-stack-item[gs-id='$id']") as? HTMLElement ?: return@forEach
            val contentDiv = el.querySelector(".grid-stack-item-content") as? HTMLElement ?: return@forEach
            contentDiv.innerHTML = buildWidgetContentHtml(widgetDef)
            attachWidgetRemoveListener(el, id)
            activeWidgetIds.add(id)
        }
        isRestoringLayout = false
    }

    /** Get the default layout as a JS array (same format as grid.save()) */
    private fun getDefaultLayoutItems(): Array<dynamic> {
        val arr = js("[]")
        val defaults = listOf(
            arrayOf("triple-bar", 0, 0, 7, 8),
            arrayOf("savings-rate", 7, 0, 2, 3),
            arrayOf("accounts", 9, 0, 3, 3),
            arrayOf("recommendation", 7, 3, 5, 4),
            arrayOf("expense-dist", 7, 7, 5, 6),
            arrayOf("category-spending", 0, 8, 7, 5),
            arrayOf("savings-trend", 0, 13, 6, 6),
            arrayOf("expense-timeline", 6, 13, 6, 6)
        )
        defaults.forEach { d ->
            val obj = js("{}")
            obj.id = d[0]
            obj.x = d[1]
            obj.y = d[2]
            obj.w = d[3]
            obj.h = d[4]
            arr.push(obj)
        }
        return arr.unsafeCast<Array<dynamic>>()
    }

    /** Add all widgets with their default positions */
    private fun addAllDefaultWidgets() {
        activeWidgetIds.clear()
        widgetDefinitions.forEach { widget ->
            addWidgetToGrid(widget)
        }
    }

    /** Lowest row the grid currently occupies (max y + h across live nodes); 0 if empty. */
    private fun gridBottomRow(): Int {
        val engine = grid?.asDynamic()?.engine ?: return 0
        val nodes = engine.nodes.unsafeCast<Array<dynamic>>()
        var bottom = 0
        for (n in nodes) {
            val ny = (n.y as? Number)?.toInt() ?: 0
            val nh = (n.h as? Number)?.toInt() ?: 0
            if (ny + nh > bottom) bottom = ny + nh
        }
        return bottom
    }

    /**
     * "Add All": add every widget not already on the grid, flowing left-to-right
     * in 12-column rows starting BELOW the current content. Existing widgets are
     * never moved and never removed — this appends, it does not reset to a
     * default layout (which is what the old handler did, silently dropping any
     * widget the default doesn't include).
     */
    private fun addMissingWidgetsAtBottom() {
        val missing = widgetDefinitions.filter { it.id !in activeWidgetIds }
        if (missing.isEmpty()) return
        val columns = 12
        // Batch: suppress per-add autosaves so the whole append persists once.
        isRestoringLayout = true
        var y = gridBottomRow()
        var x = 0
        var rowH = 0
        for (def in missing) {
            val w = def.defaultW.coerceAtMost(columns)
            val h = def.defaultH
            if (x + w > columns) { x = 0; y += rowH; rowH = 0 } // wrap to the next row
            addWidgetToGrid(def, x = x, y = y, w = w, h = h)
            x += w
            if (h > rowH) rowH = h
        }
        isRestoringLayout = false
        missing.forEach { renderWidgetContent(it.id) }
        updatePaletteVisibility()
        saveLayout()
    }

    /**
     * Vertical-gravity compaction. Each non-excluded widget falls "up"
     * to the highest y where its rect doesn't overlap any other widget.
     * Widgets never move horizontally — a widget at (3,5) can become
     * (3,2) but never (10,2). Used on dragstop instead of Gridstack's
     * compact() because compact() repacks globally (every widget gets
     * re-homed to the topmost-leftmost free slot), which fights the
     * user's mental model of "I only moved one thing — leave the
     * others alone unless they have empty space DIRECTLY above them".
     *
     * Iteration order: top-down by (y, x). When a widget is moved up,
     * later widgets see its new position, so a column-stack collapses
     * cleanly without needing a second pass.
     *
     * The [excludeId] (the just-dropped widget) is never moved AND is
     * still considered for collision — other widgets can't rise into
     * its rect.
     */
    /**
     * Demo-mode dramatic beat for the grid-canvas tour step. Picks a
     * specific target widget (savings-trend, deliberately the most
     * visually interesting chart) and resizes it in INCREMENTAL steps
     * (instead of one snap-update) so the on-camera effect looks like
     * a finger dragging the corner handle.
     *
     * Two prerequisites the simple version (before this rewrite) was
     * missing:
     *
     *  1. Resize must be ENABLED so Gridstack actually renders the
     *     corner handles. applyLockState() turns resize OFF when
     *     ResponsiveUtils.isMobile() (< 768 px) — which is the case
     *     in the 720 x 1280 demo capture window. Without forcing it
     *     on, the handles never render even with our CSS overrides.
     *  2. The resize itself must be GRADUAL — Gridstack's CSS
     *     transition animates each update(), but a single snap from
     *     7→4 cols still reads as instant. Step-by-step in 3 ticks
     *     × ~280 ms each gives the "watch it drag in" beat.
     *
     * After the dance: re-enable the prior lock state via
     * applyLockState() so we don't leak demo state into normal use.
     *
     * Hardened against the no-target case (savings-trend isn't in
     * every saved layout) — silently no-ops if the widget isn't on
     * the grid right now.
     */
    private fun performGridCanvasDemoResize() {
        val g = grid ?: return
        // triple-bar = Monthly Overview, the default-layout's biggest
        // widget (7x8 in Main Layout). Shrinking it on camera reads
        // as a real "I want this smaller" interaction AND leaves the
        // dashboard with more breathing room for the rest of the tour.
        val targetWidgetId = "triple-bar"
        val gridItem = container?.querySelector("[gs-id='$targetWidgetId']") as? HTMLElement
            ?: return
        val node = gridItem.asDynamic().gridstackNode ?: return
        val origW = (node.w as? Number)?.toInt() ?: return
        val origH = (node.h as? Number)?.toInt() ?: return
        val shrunkW = (origW - 3).coerceAtLeast(3)
        val shrunkH = (origH - 3).coerceAtLeast(3)
        if (shrunkW == origW && shrunkH == origH) return  // already minimal — skip

        // Scroll the page to the very top — not just "scroll the
        // widget into view". The tour's own scrollIntoView centers
        // the grid container, leaving the top-row widgets above the
        // viewport. Calling window.scrollTo(0,0) puts the header +
        // customize toolbar + top of grid all in frame at once,
        // which is the camera angle that lets the resize beat read
        // cleanly. Smooth so it pans rather than snaps.
        window.scrollTo(js("{top: 0, left: 0, behavior: 'smooth'}"))

        // Force-enable resize so handles render even on mobile-width
        // capture windows. Restored at the end via applyLockState().
        g.enableResize(true)
        gridItem.classList.add("gs-edit-active")

        // TEMP DIAG so the user's console tells us exactly what's
        // present — when gs-edit-active alone didn't visibly light
        // the handles we needed to know whether (a) Gridstack hadn't
        // rendered them as DOM elements, or (b) they exist but our
        // CSS wasn't winning the cascade.
        val handlesNow = gridItem.querySelectorAll(".ui-resizable-handle")
        console.log("🎬 demo grid-canvas: target=${targetWidgetId} class=gs-edit-active added · handle DOM elements: ${handlesNow.length}")

        // Belt-and-suspenders: force visibility inline so it can't
        // lose to any stylesheet or library inline-style override.
        // Targets the per-corner classes so the visible cyan brackets
        // (not the invisible edge strips) light up.
        for (i in 0 until handlesNow.length) {
            val h = handlesNow.item(i) as? HTMLElement ?: continue
            h.style.setProperty("opacity", "1", "important")
            h.style.setProperty("display", "block", "important")
        }

        // Fake cursor — appended INSIDE the target widget so it's
        // glued to the SE corner via CSS (position: absolute,
        // bottom/right offsets). When Gridstack's CSS transition
        // animates the widget's width/height, the cursor moves with
        // the corner automatically — no JS tracking, no lag, no
        // "catching up" effect.
        val cursor = (document.createElement("div") as HTMLElement).apply {
            classList.add("wiz3r-demo-cursor")
            gridItem.appendChild(this)
        }

        // Build a 6-step ramp: 3 shrink ticks, hold, 3 restore ticks.
        // Each tick is ~280 ms — slightly longer than Gridstack's
        // default CSS transition so consecutive ticks aren't visually
        // chopped off.
        fun update(w: Int, h: Int) {
            val opts = js("({})")
            opts.w = w
            opts.h = h
            g.asDynamic().update(gridItem, opts)
            // Cursor follows automatically — it's a child of gridItem
            // with absolute SE-corner positioning. No JS update needed.
        }
        val midW1 = (origW - 1).coerceAtLeast(shrunkW)
        val midW2 = (origW - 2).coerceAtLeast(shrunkW)
        val midH1 = (origH - 1).coerceAtLeast(shrunkH)
        val midH2 = (origH - 2).coerceAtLeast(shrunkH)

        val startDelay = 1500
        val tickMs = 280
        // Shrink (3 ticks) → hold 900 ms → restore (3 ticks).
        //
        // Restore is non-negotiable for two reasons:
        //   1. User feedback: leaving the widget shrunk reads as
        //      "broken" mid-demo, not as a deliberate beat
        //   2. Without restore, the widget's current size becomes
        //      next run's "original" — back-to-back demos shrink
        //      smaller and smaller each time until they hit the
        //      coerceAtLeast(3) floor.
        // The restore ALSO writes back to the saved layout via the
        // existing change handler, so a fresh page load doesn't show
        // an unexpectedly-shrunk widget either.
        window.setTimeout({ update(midW1, midH1) }, startDelay + tickMs)
        window.setTimeout({ update(midW2, midH2) }, startDelay + tickMs * 2)
        window.setTimeout({ update(shrunkW, shrunkH) }, startDelay + tickMs * 3)
        // Hold visibly so the cut lands clean on camera.
        // Restore
        window.setTimeout({ update(midW2, midH2) }, startDelay + tickMs * 3 + 900)
        window.setTimeout({ update(midW1, midH1) }, startDelay + tickMs * 4 + 900)
        window.setTimeout({
            update(origW, origH)
            // Tear down cursor + gs-edit-active + handle styling +
            // restore lock state.
            cursor.remove()
            gridItem.classList.remove("gs-edit-active")
            val finalHandles = gridItem.querySelectorAll(".ui-resizable-handle")
            for (i in 0 until finalHandles.length) {
                val h = finalHandles.item(i) as? HTMLElement ?: continue
                h.style.removeProperty("opacity")
                h.style.removeProperty("display")
            }
            applyLockState()
        }, startDelay + tickMs * 5 + 900)
    }

    /**
     * Demo-mode beat for the layouts-switch tour step. Two-phase:
     *   1. Click the OTHER pinned-layout chip → dashboard re-renders
     *      into the alternate arrangement. Camera catches the swap.
     *   2. After ~2.5 s (long enough to see the new layout), click the
     *      ORIGINAL chip → dashboard returns to the starting layout.
     *
     * The swap-and-return arc is so the subsequent widget steps
     * (savings-rate-card, monthly-bars, …) anchor on the SAME layout
     * the tour started with — otherwise the tour would jump from the
     * Quick Check layout's widgets to the Main Layout's widgets and
     * findVisibleTarget would skip every step.
     *
     * No-ops if there are < 2 pinned chips (real users may have one).
     */
    private fun performLayoutsSwitchDemo() {
        val chips = container?.querySelectorAll(".pinned-layout-btn") ?: return
        if (chips.length < 2) return
        val original = activeLayoutName ?: return
        var otherChip: HTMLElement? = null
        var originalChip: HTMLElement? = null
        for (i in 0 until chips.length) {
            val chip = chips.item(i) as? HTMLElement ?: continue
            val name = chip.getAttribute("data-layout-name") ?: continue
            if (name != original && otherChip == null) otherChip = chip
            else if (name == original && originalChip == null) originalChip = chip
        }
        if (otherChip == null) return

        // Phase 1: swap
        otherChip.click()
        // Phase 2: return to the starting layout after a hold beat
        if (originalChip != null) {
            window.setTimeout({
                // Re-query in case re-render replaced the chip element.
                val current = container?.querySelector(
                    ".pinned-layout-btn[data-layout-name='${original.replace("'", "\\'")}']",
                ) as? HTMLElement
                (current ?: originalChip)?.click()
            }, 2500)
        }
    }

    /**
     * Demo-mode beat for the customize-toolbar tour step. Opens the
     * Customize panel (sets display from "none" to "block") so the
     * camera sees the panel slide open in sync with the bubble landing
     * on the Customize button. The pre-tour setup in the nav-tour
     * click handler registered a restorePanelOnClose closer so the
     * panel collapses again when the tour ends.
     */
    private fun performOpenCustomizeDemo() {
        val panel = container?.querySelector("#toolbar-panel") as? HTMLElement ?: return
        val chevron = container?.querySelector("#toolbar-chevron") as? HTMLElement
        if (panel.style.display == "none") {
            panel.style.display = "block"
            chevron?.style?.transform = "rotate(180deg)"
        }
    }

    /**
     * Counterpart to performOpenCustomizeDemo. Fired by the
     * layouts-switch tour step (and any later step that wants a
     * clean toolbar) so the panel collapses, leaving just the
     * Customize button + pinned-layout chips visible on top.
     */
    private fun performCloseCustomizeDemo() {
        val panel = container?.querySelector("#toolbar-panel") as? HTMLElement ?: return
        val chevron = container?.querySelector("#toolbar-chevron") as? HTMLElement
        if (panel.style.display != "none") {
            panel.style.display = "none"
            chevron?.style?.transform = ""
        }
    }

    /** Layout order captured at resizestart, so reflow can restore it after Gridstack scrambles it. */
    private var resizeOrderSnapshot: Map<String, Pair<Int, Int>> = emptyMap()

    /** Each widget's (y, x) right now — the order key the reflow sorts by. */
    private fun snapshotLayoutOrder(): Map<String, Pair<Int, Int>> {
        val g = grid ?: return emptyMap()
        val nodes = g.asDynamic().engine?.nodes.unsafeCast<Array<dynamic>>() ?: return emptyMap()
        val m = mutableMapOf<String, Pair<Int, Int>>()
        for (n in nodes) {
            val id = n.id as? String ?: continue
            m[id] = ((n.y as? Number)?.toInt() ?: 0) to ((n.x as? Number)?.toInt() ?: 0)
        }
        return m
    }

    /**
     * Predictable vertical reflow (react-grid-layout's compactType:'vertical').
     * Every widget except [fixedId] is placed as HIGH as it can go without
     * overlapping the fixed widget or an already-placed one, processed in the
     * pre-resize [order] (so their relative order is preserved, not whatever
     * Gridstack scrambled it into), and its x never changes. Growing the fixed
     * widget therefore pushes the others straight down keeping their order;
     * shrinking floats them straight back up into the freed space.
     */
    private fun reflowAroundFixed(fixedId: String?, order: Map<String, Pair<Int, Int>>) {
        val g = grid ?: return
        val nodes = (g.asDynamic().engine?.nodes.unsafeCast<Array<dynamic>>() ?: return).toList()
        fun idOf(n: dynamic) = n.id as? String
        fun ix(n: dynamic) = (n.x as? Number)?.toInt() ?: 0
        fun iy(n: dynamic) = (n.y as? Number)?.toInt() ?: 0
        fun iw(n: dynamic) = (n.w as? Number)?.toInt() ?: 1
        fun ih(n: dynamic) = (n.h as? Number)?.toInt() ?: 1

        val placed = nodes.filter { idOf(it) == fixedId }.toMutableList()
        val others = nodes.filter { idOf(it) != fixedId }.sortedWith(
            compareBy(
                { order[idOf(it)]?.first ?: iy(it) },
                { order[idOf(it)]?.second ?: ix(it) },
            ),
        )
        g.batchUpdate(true)
        for (w in others) {
            val wx = ix(w); val ww = iw(w); val wh = ih(w)
            var ty = 0
            var guard = 0
            while (guard++ < 500) {
                var maxBottom = -1
                for (p in placed) {
                    val hOverlap = !(wx + ww <= ix(p) || ix(p) + iw(p) <= wx)
                    val vOverlap = !(ty + wh <= iy(p) || iy(p) + ih(p) <= ty)
                    if (hOverlap && vOverlap && iy(p) + ih(p) > maxBottom) maxBottom = iy(p) + ih(p)
                }
                if (maxBottom < 0) break
                ty = maxBottom
            }
            if (iy(w) != ty) {
                val opts = js("{}"); opts.y = ty
                g.update(w.el, opts)
            }
            w.y = ty // keep the local node in sync for the widgets placed after it
            placed.add(w)
        }
        g.batchUpdate(false)
    }

    private fun gravityCompactExcept(excludeId: String?) {
        DashboardGrid.compactVertically(grid ?: return, excludeId)
    }

    /**
     * The grid's current geometry as a plain `[{id,x,y,w,h}]` array, or null when it
     * cannot be trusted enough to write over the last known-good layout.
     *
     * [describe] names the destination for the log lines ("the saved layout", "the
     * layout 'Alt'"); [allowEmpty] belongs to the callers that just removed a widget,
     * which is the only way the grid legitimately reaches zero. Everywhere else an
     * empty grid means a torn-down or not-yet-populated one, and writing that erases
     * the layout for good.
     *
     * Geometry comes from [DashboardGrid.geometry], never from `GridStack.save()` —
     * see there for why the library's own save format cannot be persisted as-is.
     */
    private fun collectLayoutItems(describe: String, allowEmpty: Boolean = false): Array<dynamic>? {
        val g = grid ?: return null
        val layoutArray = js("[]")
        DashboardGrid.geometry(g).forEach { item ->
            val id = item.id
            if (id == null) {
                // A widget dropped here is a widget missing from the next restore —
                // it would come back from the palette at its default width instead of
                // the size the user left it. Never let that happen quietly.
                console.error("Dashboard: a widget has no id and was left out of $describe")
                return@forEach
            }
            val obj = js("{}")
            obj.id = id
            obj.x = item.x
            obj.y = item.y
            obj.w = item.w
            obj.h = item.h
            layoutArray.push(obj)
        }
        val items = layoutArray.unsafeCast<Array<dynamic>>()
        DashboardLayoutStore.rejectionReason(items, allowEmpty)?.let { why ->
            console.error(
                "Dashboard: refusing to write $describe because $why — " +
                    "keeping the last known-good layout instead",
            )
            return null
        }
        return items
    }

    /** Save current grid layout to localStorage (and to the active named layout if any) */
    private fun saveLayout(allowEmpty: Boolean = false) {
        if (isRestoringLayout) return // Don't auto-save during restore operations
        val layoutArray = collectLayoutItems("the saved layout", allowEmpty) ?: return
        val json = JSON.stringify(layoutArray)
        val activeName = activeLayoutName

        val allLayouts = localStorage.getItem(layoutKey("dashboard_layouts"))?.let {
            try { JSON.parse<dynamic>(it) } catch (_: Exception) { js("{}") }
        } ?: js("{}")
        val storedNamed: String? = if (activeName != null) {
            val entry: dynamic = allLayouts[activeName]
            if (entry == null || entry == undefined) null else JSON.stringify(entry)
        } else null

        // Nothing to record: every destination already holds exactly this layout.
        // Returning here stops a burst of identical `change` events from scheduling
        // a server sync each.
        if (
            DashboardLayoutStore.isNoOpSave(
                layoutJson = json,
                storedAutoSave = localStorage.getItem(layoutKey("dashboard_layout")),
                storedNamed = storedNamed,
                hasActiveName = activeName != null,
            )
        ) {
            return
        }

        localStorage.setItem(layoutKey("dashboard_layout"), json)

        // Also persist changes to the active named layout so they aren't lost
        if (activeName != null) {
            allLayouts[activeName] = JSON.parse<dynamic>(json)
            localStorage.setItem(layoutKey("dashboard_layouts"), JSON.stringify(allLayouts))
        }
        debouncedSyncToServer()
    }

    /** Update palette item visibility based on which widgets are on the grid */
    private fun updatePaletteVisibility() {
        widgetDefinitions.forEach { widget ->
            val paletteItem = container?.querySelector("#palette-${widget.id}") as? HTMLElement
            if (widget.id in activeWidgetIds) {
                paletteItem?.style?.display = "none"
            } else {
                paletteItem?.style?.display = "flex"
            }
        }
    }

    /** Setup palette and layout control buttons */
    private fun setupPaletteButtons() {
        container?.querySelector("#btn-add-all-widgets")?.addEventListener("click", {
            // Append the widgets not already shown, below the current layout.
            // Deliberately does NOT reset to the default layout or touch the
            // active named layout — existing widgets and their positions stay put.
            addMissingWidgetsAtBottom()
        })

        container?.querySelector("#btn-remove-all-widgets")?.addEventListener("click", {
            activeLayoutName = null
            localStorage.removeItem(layoutKey("dashboard_active_layout"))
            isRestoringLayout = true
            grid?.removeAll()
            activeWidgetIds.clear()
            isRestoringLayout = false
            updatePaletteVisibility()
            saveLayout(allowEmpty = true) // "remove all" means an empty dashboard
            renderLayoutList()
        })

        container?.querySelector("#btn-default-layout")?.addEventListener("click", {
            activeLayoutName = null
            localStorage.removeItem(layoutKey("dashboard_active_layout"))
            localStorage.removeItem(layoutKey("dashboard_layout"))
            isRestoringLayout = true
            grid?.removeAll()
            activeWidgetIds.clear()
            restoreLayoutItems(getDefaultLayoutItems())
            isRestoringLayout = false
            updatePaletteVisibility()
            activeWidgetIds.forEach { renderWidgetContent(it) }
            saveLayout()
            renderLayoutList()
        })

        // Save layout button
        container?.querySelector("#btn-save-layout")?.addEventListener("click", {
            showAppPrompt(
                title = StringKey.DASHBOARD_LAYOUT_NAME.tr(),
                initial = activeLayoutName ?: "",
                placeholder = StringKey.DASHBOARD_LAYOUT_NAME.tr(),
            ) { name, dialog ->
                dialog.close()
                saveNamedLayout(name)
            }
        })
    }

    /** Setup click-to-add on each palette item */
    private fun setupPaletteClickToAdd() {
        widgetDefinitions.forEach { widget ->
            val paletteItem = container?.querySelector("#palette-${widget.id}") as? HTMLElement
            paletteItem?.addEventListener("click", {
                if (widget.id !in activeWidgetIds) {
                    addWidgetToGrid(widget)
                    renderWidgetContent(widget.id)
                    updatePaletteVisibility()
                    saveLayout()
                }
            })
        }
    }

    // --- Lock & Mobile Touch ---

    /** Apply lock state: disable/enable move and resize on the grid */
    private fun applyLockState() {
        val g = grid ?: return
        if (isLayoutLocked) {
            g.enableMove(false)
            g.enableResize(false)
        } else if (ResponsiveUtils.isMobile()) {
            g.enableMove(false)
            g.enableResize(false)
        } else {
            g.enableMove(true)
            g.enableResize(true)
        }
        // Update cursor on drag handles
        val cursor = if (isLayoutLocked) "default" else "grab"
        container?.querySelectorAll(".gs-item-drag-handle")?.let { handles ->
            for (i in 0 until handles.length) {
                (handles.item(i) as? HTMLElement)?.style?.cursor = cursor
            }
        }
    }

    /** Toggle lock for a layout name */
    private fun toggleLockLayout(name: String) {
        if (name in lockedLayoutNames) {
            lockedLayoutNames.remove(name)
        } else {
            lockedLayoutNames.add(name)
        }
        saveLockedLayouts()
        // Update lock state if this is the active layout
        if (name == activeLayoutName) {
            isLayoutLocked = name in lockedLayoutNames
            applyLockState()
        }
        renderLayoutList()
    }

    /** Persist locked layout names */
    private fun saveLockedLayouts() {
        val arr = JSON.stringify(lockedLayoutNames.toTypedArray())
        localStorage.setItem(layoutKey("dashboard_locked_layouts"), arr)
        debouncedSyncToServer()
    }

    // Track which widget is currently in edit mode on mobile
    private var mobileEditActiveItem: HTMLElement? = null

    /** Deactivate edit mode on the currently active mobile widget */
    private fun deactivateMobileEdit() {
        val g = grid ?: return
        mobileEditActiveItem?.let { item ->
            item.classList.remove("gs-edit-active")
            item.style.outline = ""
            item.style.outlineOffset = ""
        }
        mobileEditActiveItem = null
        g.enableMove(false)
        g.enableResize(false)
    }

    /** On mobile: long-press on widget header toggles edit mode (handles + drag) */
    private fun setupMobileLongPressHandles() {
        val g = grid ?: return
        // Hide resize handles until a widget is long-pressed into edit mode. The rule
        // is viewport-scoped and installed once — see MobileResizeHandles for why both
        // of those matter.
        MobileResizeHandles.install(kotlinx.browser.document)

        // Attach long-press toggle to each widget's drag handle
        container?.querySelectorAll(".gs-item-drag-handle")?.let { handles ->
            for (i in 0 until handles.length) {
                val handle = handles.item(i) as? HTMLElement ?: continue
                var longPressTimer: Int? = null
                val gridItem = handle.closest(".grid-stack-item") as? HTMLElement ?: continue

                handle.addEventListener("touchstart", { e ->
                    e.preventDefault() // Prevent text selection
                    longPressTimer = window.setTimeout({
                        if (mobileEditActiveItem == gridItem) {
                            // Already active — deactivate (toggle off)
                            deactivateMobileEdit()
                        } else {
                            // Deactivate any other widget first
                            deactivateMobileEdit()
                            // Activate this widget
                            gridItem.classList.add("gs-edit-active")
                            g.enableMove(true)
                            g.enableResize(true)
                            gridItem.style.outline = "2px solid rgba(0,217,255,0.4)"
                            gridItem.style.outlineOffset = "-2px"
                            mobileEditActiveItem = gridItem
                        }
                    }, 500)
                })
                handle.addEventListener("touchend", {
                    longPressTimer?.let { window.clearTimeout(it) }
                })
                handle.addEventListener("touchmove", {
                    longPressTimer?.let { window.clearTimeout(it) }
                })
            }
        }
    }

    // --- Named Layout Management ---

    /** Get all named layouts from localStorage */
    private fun getNamedLayouts(): Map<String, Array<dynamic>> {
        val raw = localStorage.getItem(layoutKey("dashboard_layouts")) ?: return emptyMap()
        return try {
            val parsed = JSON.parse<dynamic>(raw)
            val result = mutableMapOf<String, Array<dynamic>>()
            val keys = js("Object.keys(parsed)").unsafeCast<Array<String>>()
            keys.forEach { key ->
                result[key] = parsed[key].unsafeCast<Array<dynamic>>()
            }
            result
        } catch (_: Exception) { emptyMap() }
    }

    /** Save the current grid state under a named layout */
    private fun saveNamedLayout(name: String) {
        val layoutArray = collectLayoutItems("the named layout '$name'") ?: return

        val allLayouts = localStorage.getItem(layoutKey("dashboard_layouts"))?.let {
            try { JSON.parse<dynamic>(it) } catch (_: Exception) { js("{}") }
        } ?: js("{}")
        allLayouts[name] = layoutArray
        localStorage.setItem(layoutKey("dashboard_layouts"), JSON.stringify(allLayouts))

        activeLayoutName = name
        localStorage.setItem(layoutKey("dashboard_active_layout"), name)
        renderLayoutList()
        debouncedSyncToServer()
    }

    /** Load a named layout: clear grid, restore the saved widgets */
    private fun loadNamedLayout(name: String) {
        val layouts = getNamedLayouts()
        val layout = layouts[name] ?: return

        // Guard BEFORE removeAll — removeAll triggers "removed" events which call saveLayout()
        isRestoringLayout = true
        grid?.removeAll()
        activeWidgetIds.clear()
        restoreLayoutItems(layout.unsafeCast<Array<dynamic>>())

        updatePaletteVisibility()
        activeWidgetIds.forEach { renderWidgetContent(it) }

        activeLayoutName = name
        isLayoutLocked = name in lockedLayoutNames
        localStorage.setItem(layoutKey("dashboard_active_layout"), name)
        applyLockState()
        renderLayoutList()
    }

    /** Delete a named layout after confirmation */
    private fun deleteNamedLayout(name: String) {
        showAppConfirm(
            title = StringKey.DASHBOARD_LAYOUT_DELETE_CONFIRM.tr().replace("%s", name),
            confirmLabel = StringKey.ACTION_DELETE.tr(),
            destructive = true,
        ) { deleteNamedLayoutConfirmed(name) }
    }

    private fun deleteNamedLayoutConfirmed(name: String) {
        val allLayouts = localStorage.getItem(layoutKey("dashboard_layouts"))?.let {
            try { JSON.parse<dynamic>(it) } catch (_: Exception) { js("{}") }
        } ?: return
        js("delete allLayouts[name]")
        localStorage.setItem(layoutKey("dashboard_layouts"), JSON.stringify(allLayouts))

        if (activeLayoutName == name) {
            activeLayoutName = null
            localStorage.removeItem(layoutKey("dashboard_active_layout"))
        }
        pinnedLayoutNames.remove(name)
        lockedLayoutNames.remove(name)
        savePinnedLayouts()
        saveLockedLayouts()
        renderLayoutList()
        debouncedSyncToServer()
    }

    /** Render the layout list in the sidebar */
    private fun renderLayoutList() {
        val listEl = container?.querySelector("#saved-layouts-list") as? HTMLElement ?: return
        val layouts = getNamedLayouts()

        if (layouts.isEmpty()) {
            listEl.innerHTML = """<div style="color: ${t.textSecondary}; font-size: 11px; font-style: italic; padding: 4px 0;">No saved layouts</div>"""
            return
        }

        listEl.innerHTML = """<div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px;">""" + layouts.keys.joinToString("") { name ->
            val isActive = name == activeLayoutName
            val isPinned = name in pinnedLayoutNames
            val isLocked = name in lockedLayoutNames
            val pinColor = if (isPinned) t.accent else t.textSecondary
            val pinFill = if (isPinned) "currentColor" else "none"
            val lockColor = if (isLocked) t.proBadge else t.textSecondary
            """
            <div style="
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 10px 14px;
                margin-bottom: 6px;
                border-radius: 8px;
                background: ${if (isActive) t.chipSelectedBg else t.chipUnselectedBg};
                border: 1px solid ${if (isActive) t.chipSelectedBorder else t.border};
                transition: all 0.2s;
            ">
                <span class="layout-load-btn" data-layout-name="${name}" style="
                    flex: 1;
                    color: ${if (isActive) t.accent else t.textPrimary};
                    font-size: 13px;
                    font-weight: 500;
                    cursor: pointer;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                " onmouseover="this.style.color='${t.textPrimary}'" onmouseout="this.style.color='${if (isActive) t.accent else t.textPrimary}'">
                    ${if (isActive) "&#x25B6; " else ""}$name
                </span>
                <button class="layout-pin-btn" data-layout-name="${name}" title="${if (isPinned) "Unpin" else "Pin to toolbar"}" style="
                    background: none;
                    border: 1px solid ${if (isPinned) t.chipSelectedBorder else t.border};
                    border-radius: 4px;
                    color: $pinColor;
                    cursor: pointer;
                    padding: 3px 5px;
                    line-height: 1;
                    transition: all 0.2s;
                    display: flex; align-items: center;
                " onmouseover="this.style.borderColor='${t.accent}';this.style.color='${t.accent}'" onmouseout="this.style.borderColor='${if (isPinned) t.chipSelectedBorder else t.border}';this.style.color='$pinColor'"><svg width="12" height="12" viewBox="0 0 24 24" fill="$pinFill" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4v6l-2 4v2h10v-2l-2-4V4"/><line x1="12" y1="16" x2="12" y2="21"/><line x1="8" y1="4" x2="16" y2="4"/></svg></button>
                <button class="layout-lock-btn" data-layout-name="${name}" title="${if (isLocked) "Unlock" else "Lock layout"}" style="
                    background: none;
                    border: 1px solid ${if (isLocked) "rgba(245,158,11,0.3)" else t.border};
                    border-radius: 4px;
                    color: $lockColor;
                    cursor: pointer;
                    padding: 3px 5px;
                    line-height: 1;
                    transition: all 0.2s;
                    display: flex; align-items: center;
                " onmouseover="this.style.borderColor='${t.proBadge}';this.style.color='${t.proBadge}'" onmouseout="this.style.borderColor='${if (isLocked) "rgba(245,158,11,0.3)" else t.border}';this.style.color='$lockColor'"><svg width="12" height="12" viewBox="0 0 24 24" fill="${if (isLocked) "currentColor" else "none"}" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${if (isLocked) """<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path fill="none" d="M7 11V7a5 5 0 0 1 10 0v4"/>""" else """<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 9.9-1"/>"""}</svg></button>
                <button class="layout-delete-btn" data-layout-name="${name}" title="Delete" style="
                    background: none;
                    border: 1px solid ${t.border};
                    border-radius: 4px;
                    color: ${t.textSecondary};
                    cursor: pointer;
                    padding: 3px 5px;
                    line-height: 1;
                    transition: all 0.2s;
                    display: flex; align-items: center;
                " onmouseover="this.style.color='${t.expenseRed}';this.style.borderColor='${t.expenseRed}'" onmouseout="this.style.color='${t.textSecondary}';this.style.borderColor='${t.border}'">&times;</button>
            </div>
            """
        } + "</div>"

        // Attach listeners
        listEl.querySelectorAll(".layout-load-btn").let { nodes ->
            for (i in 0 until nodes.length) {
                val el = nodes.item(i) as? HTMLElement ?: continue
                el.addEventListener("click", {
                    val layoutName = el.getAttribute("data-layout-name") ?: return@addEventListener
                    loadNamedLayout(layoutName)
                })
            }
        }
        listEl.querySelectorAll(".layout-pin-btn").let { nodes ->
            for (i in 0 until nodes.length) {
                val el = nodes.item(i) as? HTMLElement ?: continue
                el.addEventListener("click", { event ->
                    event.stopPropagation()
                    val layoutName = el.getAttribute("data-layout-name") ?: return@addEventListener
                    togglePinLayout(layoutName)
                })
            }
        }
        listEl.querySelectorAll(".layout-lock-btn").let { nodes ->
            for (i in 0 until nodes.length) {
                val el = nodes.item(i) as? HTMLElement ?: continue
                el.addEventListener("click", { event ->
                    event.stopPropagation()
                    val layoutName = el.getAttribute("data-layout-name") ?: return@addEventListener
                    toggleLockLayout(layoutName)
                })
            }
        }
        listEl.querySelectorAll(".layout-delete-btn").let { nodes ->
            for (i in 0 until nodes.length) {
                val el = nodes.item(i) as? HTMLElement ?: continue
                el.addEventListener("click", { event ->
                    event.stopPropagation()
                    val layoutName = el.getAttribute("data-layout-name") ?: return@addEventListener
                    deleteNamedLayout(layoutName)
                })
            }
        }

        renderPinnedLayoutsBar()
    }

    /** Persist pinned layout names to localStorage */
    private fun savePinnedLayouts() {
        val arr = JSON.stringify(pinnedLayoutNames.toTypedArray())
        localStorage.setItem(layoutKey("dashboard_pinned_layouts"), arr)
        debouncedSyncToServer()
    }

    /** Toggle pin state for a layout */
    private fun togglePinLayout(name: String) {
        if (name in pinnedLayoutNames) {
            pinnedLayoutNames.remove(name)
        } else {
            pinnedLayoutNames.add(name)
        }
        savePinnedLayouts()
        renderLayoutList()
    }

    /** Render pinned layout buttons on the toolbar row (visible when Customize is collapsed) */
    private fun renderPinnedLayoutsBar() {
        val bar = container?.querySelector("#pinned-layouts-bar") as? HTMLElement ?: return
        val existingLayouts = getNamedLayouts()
        // Only show pins for layouts that still exist
        val validPins = pinnedLayoutNames.filter { it in existingLayouts }

        if (validPins.isEmpty()) {
            bar.innerHTML = ""
            return
        }

        bar.innerHTML = validPins.joinToString("") { name ->
            val isActive = name == activeLayoutName
            """<button class="pinned-layout-btn" data-layout-name="${name}" style="
                display: inline-flex; align-items: center; gap: 4px;
                padding: 5px 12px; border: 1px solid ${if (isActive) t.chipSelectedBorder else t.chipUnselectedBorder};
                border-radius: 6px; background: ${if (isActive) t.chipSelectedBg else t.card};
                color: ${if (isActive) t.accent else t.chipUnselectedText}; cursor: pointer; font-size: 12px;
                transition: all 0.15s; white-space: nowrap;
            " onmouseover="this.style.borderColor='${t.accent}';this.style.color='${t.textPrimary}'"
              onmouseout="this.style.borderColor='${if (isActive) t.chipSelectedBorder else t.chipUnselectedBorder}';this.style.color='${if (isActive) t.accent else t.chipUnselectedText}'">$name</button>"""
        }

        bar.querySelectorAll(".pinned-layout-btn").let { nodes ->
            for (i in 0 until nodes.length) {
                val el = nodes.item(i) as? HTMLElement ?: continue
                el.addEventListener("click", {
                    val layoutName = el.getAttribute("data-layout-name") ?: return@addEventListener
                    loadNamedLayout(layoutName)
                })
            }
        }
    }

    /** Setup toolbar collapse/expand toggle */
    private fun setupToolbarToggle() {
        val toggle = container?.querySelector("#toolbar-toggle") as? HTMLElement ?: return
        val panel = container?.querySelector("#toolbar-panel") as? HTMLElement ?: return
        val chevron = container?.querySelector("#toolbar-chevron") as? HTMLElement

        toggle.addEventListener("click", {
            val isOpen = panel.style.display != "none"
            panel.style.display = if (isOpen) "none" else "block"
            chevron?.style?.transform = if (isOpen) "rotate(0deg)" else "rotate(180deg)"
        })
    }

    /** Render content into a specific widget's content div */
    /** Read the widget's current grid column width (1–12). Used to drive
     *  per-widget adaptive content — e.g. the Monthly Overview chart
     *  drops empty future months when the user shrinks it.
     *
     *  Approach: pure DOM measurement. Computes the column count from
     *  the ratio of the widget's offsetWidth to the .grid-stack
     *  container's offsetWidth, rounded against 12 columns. No
     *  Gridstack-API calls (cellWidth(), gridstackNode.w, gs-w attr),
     *  because every one of those returned a stale or null value in
     *  this Kotlin/JS dynamic-interop setup and the adaptation never
     *  triggered. A pure box-model read is robust against any
     *  Gridstack version + interop quirk.
     */
    /**
     * How many list rows fit a widget's content box at a fixed row height:
     * (measured height − [headerPx]) / [rowPx], clamped to [[min], [available]].
     * This is what lets a list widget show MORE entries when it's tall and fewer
     * when short, without changing the font size. Re-evaluated on every
     * resize because renderWidgetContent re-runs on resizestop.
     */
    private fun fitRows(widgetId: String, available: Int, rowPx: Int, headerPx: Int, min: Int = 1): Int {
        val el = container?.querySelector("#widget-content-$widgetId") as? HTMLElement
        val h = el?.clientHeight?.takeIf { it > 0 } ?: 260
        return ((h - headerPx) / rowPx).coerceIn(min, maxOf(min, available))
    }

    private fun widgetColumnWidth(widgetId: String): Int {
        val gridItem = container?.querySelector("#widget-content-$widgetId")
            ?.closest(".grid-stack-item") as? HTMLElement
        val gridStackEl = container?.querySelector(".grid-stack") as? HTMLElement
        if (gridItem != null && gridStackEl != null) {
            val widgetWPx = gridItem.offsetWidth.toDouble()
            val gridWPx = gridStackEl.offsetWidth.toDouble()
            if (gridWPx > 0.0 && widgetWPx > 0.0) {
                return kotlin.math.round(widgetWPx * 12.0 / gridWPx).toInt().coerceIn(1, 12)
            }
        }
        return widgetDefinitions.firstOrNull { it.id == widgetId }?.defaultW ?: 12
    }

    /** Companion to [widgetColumnWidth] — current grid row height of a
     *  widget. Divides the rendered pixel height by the grid cellHeight
     *  (60 px, set in the GridStack.init config). Same pure box-model
     *  approach for the same reasons. Used by any widget whose layout
     *  decision cares about vertical room — e.g. Monthly Overview moves
     *  the legend from below to the right when the widget is short. */
    private fun widgetRowHeight(widgetId: String): Int {
        val gridItem = container?.querySelector("#widget-content-$widgetId")
            ?.closest(".grid-stack-item") as? HTMLElement
        if (gridItem != null) {
            val widgetHPx = gridItem.offsetHeight.toDouble()
            // cellHeight is the value passed to GridStack.init above (60).
            // Hard-coded here rather than re-reading via the API surface
            // (which has been unreliable) — if you change cellHeight at
            // init, mirror it here.
            val cellHPx = 60.0
            if (widgetHPx > 0.0) {
                return kotlin.math.round(widgetHPx / cellHPx).toInt().coerceAtLeast(1)
            }
        }
        return widgetDefinitions.firstOrNull { it.id == widgetId }?.defaultH ?: 5
    }

    private fun renderWidgetContent(widgetId: String) {
        val contentEl = container?.querySelector("#widget-content-$widgetId") as? HTMLElement ?: return

        when (widgetId) {
            "savings-rate" -> {
                contentEl.innerHTML = renderFocalSavingsDisplay()
                animateFocalSavingsDisplay()
            }
            "accounts" -> {
                contentEl.innerHTML = renderAccountSelectorContent()
                setupAccountSelectorListeners()
            }
            "contexts" -> {
                contentEl.innerHTML = renderContextSelectorContent()
                setupContextSelectorListeners()
            }
            "triple-bar" -> {
                contentEl.innerHTML = renderTripleBarWidgetContent(
                    widgetWidthCols = widgetColumnWidth("triple-bar"),
                    widgetHeightRows = widgetRowHeight("triple-bar"),
                )
                setupTripleBarWidgetListeners()
            }
            "category-spending" -> {
                contentEl.innerHTML = renderBarChartPlaceholder()
                container?.let { cont ->
                    val widgetEl = cont.querySelector("#widget-content-category-spending") as? HTMLElement
                    widgetEl?.let {
                        setupCategoryBarHoverEffects(it)
                        it.querySelector("#legend-category-budget")?.addEventListener("click", {
                            showCategoryBudget = !showCategoryBudget
                            settingsManager.setChartSeriesVisibility("categoryBudget", showCategoryBudget)
                            renderWidgetContent("category-spending")
                        })
                    }
                }
            }
            "expense-dist" -> {
                val box = widgetPixelBox("expense-dist")
                contentEl.innerHTML = renderPieChartPlaceholder(
                    widgetWidthCols = widgetColumnWidth("expense-dist"),
                    widgetHeightRows = widgetRowHeight("expense-dist"),
                    widthPx = box?.first,
                    heightPx = box?.second,
                )
                container?.let { cont ->
                    val widgetEl = cont.querySelector("#widget-content-expense-dist") as? HTMLElement
                    widgetEl?.let { setupPieLegendHoverEffects(it) }
                }
            }
            "savings-trend" -> {
                contentEl.innerHTML = renderSavingsLineChart()
                container?.let { setupSavingsTrendTooltips(it) }
            }
            "expense-timeline" -> {
                contentEl.innerHTML = renderTimelinePlaceholder()
                container?.let { setupExpenseTimelineTooltips(it) }
            }
            "recommendation" -> {
                contentEl.innerHTML = renderRecommendationPanel()
                container?.let { setupRecommendationEventListeners(it) }
            }
            "cashflow-mtd" -> { contentEl.innerHTML = renderCashFlowMtdWidget() }
            "budget-health" -> { contentEl.innerHTML = renderBudgetHealthWidget() }
            "budget-categories-count" -> { contentEl.innerHTML = renderBudgetCategoriesCountWidget() }
            "savings-streak" -> { contentEl.innerHTML = renderSavingsStreakWidget() }
            "yoy-month" -> { contentEl.innerHTML = renderYoyMonthWidget() }
            "category-movers" -> { contentEl.innerHTML = renderCategoryMoversWidget() }
            "largest-txns" -> {
                contentEl.innerHTML = renderLargestTransactionsWidget()
                contentEl.querySelector("[data-nav-to-transactions]")?.addEventListener("click", {
                    navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                })
            }
            "top-merchants" -> {
                contentEl.innerHTML = renderTopMerchantsWidget()
                contentEl.querySelector("[data-nav-to-transactions]")?.addEventListener("click", {
                    navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                })
            }
            "needs-wants-flow" -> {
                contentEl.innerHTML = renderNeedsWantsFlowWidget()
                attachNeedsWantsFlowHandlers(contentEl)
            }
        }
    }

    /**
     * Scale widget content to fit within the widget container.
     * Charts use responsive SVGs (width/height 100% + preserveAspectRatio).
     * Non-chart content uses CSS transform: scale() to shrink proportionally.
     */
    private fun scaleWidgetContent(widgetId: String) {
        val contentEl = container?.querySelector("#widget-content-$widgetId") as? HTMLElement ?: return
        // Reset any previous scaling
        contentEl.style.transform = ""
        contentEl.style.width = ""
        contentEl.style.height = ""
        contentEl.style.transformOrigin = "top left"

        // Charts already use responsive SVGs — no scaling needed
        val chartWidgets = setOf("savings-trend", "expense-timeline", "expense-dist", "triple-bar", "category-spending")
        if (widgetId in chartWidgets) return

        // For non-chart widgets: scale content to fit container
        window.requestAnimationFrame {
            val containerHeight = contentEl.clientHeight.toDouble()
            val containerWidth = contentEl.clientWidth.toDouble()
            if (containerHeight <= 0 || containerWidth <= 0) return@requestAnimationFrame

            // Measure natural content size
            contentEl.style.setProperty("overflow", "visible")
            contentEl.style.height = "auto"
            val naturalHeight = contentEl.scrollHeight.toDouble()
            val naturalWidth = contentEl.scrollWidth.toDouble()
            contentEl.style.setProperty("overflow", "hidden")
            contentEl.style.height = ""

            if (naturalHeight <= containerHeight && naturalWidth <= containerWidth) return@requestAnimationFrame

            val scaleY = if (naturalHeight > containerHeight) containerHeight / naturalHeight else 1.0
            val scaleX = if (naturalWidth > containerWidth) containerWidth / naturalWidth else 1.0
            val scale = minOf(scaleX, scaleY)

            val minScale = when (widgetId) {
                "savings-rate" -> 0.6
                "accounts" -> 0.65
                "recommendation" -> 0.7
                else -> 0.5
            }

            val finalScale = maxOf(scale, minScale)
            if (finalScale < 1.0) {
                contentEl.style.transform = "scale($finalScale)"
                contentEl.style.transformOrigin = "top left"
                contentEl.style.width = "${(100.0 / finalScale).toInt()}%"
                contentEl.style.height = "${(100.0 / finalScale).toInt()}%"
            }
        }
    }

    /** Render account selector without the outer card wrapper (used inside widget) */
    private fun renderAccountSelectorContent(): String {
        if (allAccounts.isEmpty()) {
            return """<div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; text-align: center; padding: 16px;">No accounts linked</div>"""
        }

        val accountButtons = allAccounts.joinToString("") { account ->
            val isSelected = account.accountId in selectedAccountIds
            val displayName = models.AccountDisplayName.primary(
                customName = account.customName,
                officialName = account.officialName,
                name = account.name,
                mask = account.mask,
            )
            """
                <button data-account-id="${account.accountId}" style="
                    padding: 6px 12px;
                    border: 1px solid ${if (isSelected) t.chipSelectedBorder else t.chipUnselectedBorder};
                    border-radius: 16px;
                    background: ${if (isSelected) t.chipSelectedBg else t.chipUnselectedBg};
                    color: ${if (isSelected) t.accent else t.chipUnselectedText};
                    cursor: pointer;
                    font-size: ${DashType.BODY};
                    transition: all 0.2s;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    max-width: 100%;
                ">${displayName}</button>
            """
        }

        return """
            <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">${StringKey.DASHBOARD_SELECTED_COUNT.tr(selectedAccountIds.size.toString(), allAccounts.size.toString())}</span>
            </div>
            <div id="accountSelector" style="display: flex; flex-direction: column; gap: 6px;">
                $accountButtons
            </div>
        """
    }

    /** Render context selector chips (shown only when user has contexts) */
    private fun renderContextSelectorContent(): String {
        if (allContexts.isEmpty()) return ""

        val isAllMode = selectedContextIds.isEmpty()

        val allChip = """
            <button data-context-id="all" style="
                padding: 6px 14px;
                border: 1px solid ${if (isAllMode) t.chipSelectedBorder else t.chipUnselectedBorder};
                border-radius: 16px;
                background: ${if (isAllMode) t.chipSelectedBg else t.chipUnselectedBg};
                color: ${if (isAllMode) t.accent else t.chipUnselectedText};
                cursor: pointer;
                font-size: ${DashType.BODY};
                transition: all 0.2s;
            ">${StringKey.CONTEXT_ALL.tr()}</button>
        """

        val contextChips = allContexts.joinToString("") { ctx ->
            val isSelected = ctx.id in selectedContextIds
            """
                <button data-context-id="${ctx.id}" style="
                    padding: 6px 14px;
                    border: 1px solid ${if (isSelected) ctx.color else t.chipUnselectedBorder};
                    border-radius: 16px;
                    background: ${if (isSelected) "${ctx.color}26" else t.chipUnselectedBg};
                    color: ${if (isSelected) t.textPrimary else t.chipUnselectedText};
                    cursor: pointer;
                    font-size: ${DashType.BODY};
                    transition: all 0.2s;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                "><span style="width: 8px; height: 8px; border-radius: 50%; background: ${if (isSelected) ctx.color else t.textSecondary}; flex-shrink: 0;"></span>${ctx.name}</button>
            """
        }

        val hint = when {
            selectedContextIds.size == 2 -> """<div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-top: 6px;">${StringKey.CONTEXT_SHOWING_BOTH.tr()}</div>"""
            selectedContextIds.size >= 3 -> """<div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-top: 6px;">${StringKey.CONTEXT_SHOWING_ALL_N.tr(selectedContextIds.size.toString())}</div>"""
            else -> ""
        }

        return """
            <div id="contextSelector" style="display: flex; flex-direction: column; gap: 6px;">
                $allChip
                $contextChips
            </div>
            $hint
        """
    }

    /** Setup account selector click listeners */
    private fun setupAccountSelectorListeners() {
        allAccounts.forEach { account ->
            container?.querySelector("[data-account-id='${account.accountId}']")?.addEventListener("click", {
                if (account.accountId in selectedAccountIds) {
                    if (selectedAccountIds.size > 1) {
                        selectedAccountIds.remove(account.accountId)
                    }
                } else {
                    selectedAccountIds.add(account.accountId)
                }
                val deselected = allAccounts.map { it.accountId }.filter { it !in selectedAccountIds }
                GlobalScope.launch {
                    try { ApiService().setSetting("deselected_account_ids", deselected.joinToString(",")) } catch (_: Exception) {}
                }
                updateYearlyDataWithActualTransactions()
                refreshDashboard()
            })
        }
    }

    private fun saveContextSelection() {
        val value = if (selectedContextIds.isEmpty()) "" else selectedContextIds.joinToString(",")
        GlobalScope.launch {
            try { ApiService().setSetting("selected_context_ids", value) }
            catch (_: Exception) {}
        }
    }

    /** Setup context selector click listeners */
    private fun setupContextSelectorListeners() {
        // "All" chip
        container?.querySelector("[data-context-id='all']")?.addEventListener("click", {
            selectedContextIds.clear()
            saveContextSelection()
            updateYearlyDataWithActualTransactions()
            refreshDashboard()
        })
        // Individual context chips
        allContexts.forEach { ctx ->
            container?.querySelector("[data-context-id='${ctx.id}']")?.addEventListener("click", {
                if (selectedContextIds.isEmpty()) {
                    selectedContextIds.add(ctx.id)
                } else if (ctx.id in selectedContextIds) {
                    selectedContextIds.remove(ctx.id)
                } else {
                    selectedContextIds.add(ctx.id)
                }
                saveContextSelection()
                updateYearlyDataWithActualTransactions()
                refreshDashboard()
            })
        }
    }

    /** Render triple-bar chart widget content with year selector */
    private fun renderTripleBarWidgetContent(widgetWidthCols: Int = 12, widgetHeightRows: Int = 5): String {
        val currentYear = Date().getFullYear()
        val minYear = getMinYearFromData()
        val maxYear = currentYear

        // Flex column over the whole widget content so the chart claims
        // remaining vertical space after the year nav (which is fixed-
        // height). Crucial for the adaptive layout — without it the SVG
        // either collapses to its natural aspect-ratio height (legend
        // pushed off when widget is wide) or overflows (when widget is
        // short). Inside the chart slot, renderTripleBarChart further
        // distributes space between the SVG and the legend.
        return """
            <div style="display: flex; flex-direction: column; height: 100%; min-height: 0;">
                <div style="display: flex; align-items: center; gap: clamp(6px, 1.5cqw, 14px); margin: 0 0 clamp(6px, 1.5cqh, 14px) 0; flex: 0 0 auto;">
                    <button id="yearPrev" style="
                        background: transparent;
                        border: none;
                        color: ${if (selectedYear > minYear) "#3b82f6" else "rgba(136, 136, 136, 0.3)"};
                        font-size: clamp(14px, min(3cqw, 5cqh), 26px);
                        cursor: ${if (selectedYear > minYear) "pointer" else "not-allowed"};
                        padding: 2px 6px;
                        display: flex;
                        align-items: center;
                    " ${if (selectedYear <= minYear) "disabled" else ""}>&#9664;</button>
                    <span id="yearLabel" style="color: ${t.textPrimary}; font-size: clamp(14px, min(3cqw, 5cqh), 26px); font-weight: 600; min-width: 50px; text-align: center;">${displayYearLabel(selectedYear)}</span>
                    <button id="yearNext" style="
                        background: transparent;
                        border: none;
                        color: ${if (selectedYear < maxYear) "#3b82f6" else "rgba(136, 136, 136, 0.3)"};
                        font-size: clamp(14px, min(3cqw, 5cqh), 26px);
                        cursor: ${if (selectedYear < maxYear) "pointer" else "not-allowed"};
                        padding: 2px 6px;
                        display: flex;
                        align-items: center;
                    " ${if (selectedYear >= maxYear) "disabled" else ""}>&#9654;</button>
                </div>
                <div style="flex: 1 1 0; min-height: 0; display: flex; flex-direction: column;">
                    ${renderTripleBarChart(selectedYear, widgetWidthCols = widgetWidthCols, widgetHeightRows = widgetHeightRows)}
                </div>
            </div>
        """
    }

    /** Setup year selector and legend listeners in the triple-bar widget */
    private fun setupTripleBarWidgetListeners() {
        val cont = container ?: return
        val currentYear = Date().getFullYear()
        val minYear = getMinYearFromData()
        val maxYear = currentYear

        cont.querySelector("#yearPrev")?.addEventListener("click", {
            if (selectedYear > minYear) {
                selectedYear -= 1
                focusedMonth = null
                loadBudgetPlansForYear(selectedYear)
                loadBudgetPlansForYear(selectedYear - 1)
                updateYearlyDataWithActualTransactions()
                refreshDashboard()
            }
        })

        cont.querySelector("#yearNext")?.addEventListener("click", {
            if (selectedYear < maxYear) {
                selectedYear += 1
                focusedMonth = null
                loadBudgetPlansForYear(selectedYear)
                loadBudgetPlansForYear(selectedYear - 1)
                updateYearlyDataWithActualTransactions()
                refreshDashboard()
            }
        })

        // Legend toggle buttons
        cont.querySelector("#legend-income")?.addEventListener("click", {
            showIncome = !showIncome
            settingsManager.setChartSeriesVisibility("income", showIncome)
            refreshDashboard()
        })
        cont.querySelector("#legend-expenses")?.addEventListener("click", {
            showExpenses = !showExpenses
            settingsManager.setChartSeriesVisibility("expenses", showExpenses)
            refreshDashboard()
        })
        cont.querySelector("#legend-savings")?.addEventListener("click", {
            showSavings = !showSavings
            settingsManager.setChartSeriesVisibility("savings", showSavings)
            refreshDashboard()
        })
        cont.querySelector("#legend-budget")?.addEventListener("click", {
            showBudget = !showBudget
            settingsManager.setChartSeriesVisibility("budget", showBudget)
            refreshDashboard()
        })

        // Triple-bar tooltips and click handlers
        setupTripleBarTooltips(cont, "#tripleBarTooltip", "#tripleBarChart", ".triple-bar-group")
    }

    /** Setup savings trend chart tooltips */
    private fun setupSavingsTrendTooltips(cont: HTMLElement) {
        val savingsTooltip = cont.querySelector("#savingsChartTooltip") as? HTMLElement
        val savingsSvg = cont.querySelector("#savingsChartSvg") as? org.w3c.dom.Element
        val savingsDataPoints = cont.querySelectorAll(".savings-data-point")
        for (i in 0 until savingsDataPoints.length) {
            val point = savingsDataPoints.item(i) as? org.w3c.dom.Element
            val showTooltip = showTooltip@{ _: org.w3c.dom.events.Event ->
                val tooltipText = point?.getAttribute("data-tooltip") ?: return@showTooltip
                savingsTooltip?.let { tooltip ->
                    tooltip.textContent = tooltipText
                    tooltip.style.display = "block"
                    savingsSvg?.let { svg ->
                        val svgRect = svg.getBoundingClientRect()
                        val pointRect = point.getBoundingClientRect()
                        val tipWidth = tooltip.offsetWidth.toDouble()
                        var relativeX = pointRect.left - svgRect.left + 15
                        if (relativeX + tipWidth > svgRect.width) {
                            relativeX = pointRect.left - svgRect.left - tipWidth - 15
                        }
                        relativeX = maxOf(0.0, relativeX)
                        val relativeY = pointRect.top - svgRect.top - 10
                        tooltip.style.left = "${relativeX.toInt()}px"
                        tooltip.style.top = "${relativeY.toInt()}px"
                    }
                }
            }
            point?.addEventListener("mouseenter", showTooltip)
            point?.addEventListener("touchstart", { event ->
                event.preventDefault()
                showTooltip(event)
            })
            point?.addEventListener("mouseleave", { savingsTooltip?.style?.display = "none" })
        }

        // Target label tooltip
        val targetLabel = cont.querySelector(".savings-target-label") as? org.w3c.dom.Element
        targetLabel?.addEventListener("mouseenter", { _ ->
            val tooltipText = targetLabel.getAttribute("data-tooltip") ?: return@addEventListener
            savingsTooltip?.let { tooltip ->
                tooltip.textContent = tooltipText
                tooltip.style.display = "block"
                tooltip.style.borderColor = "#FFA500"
                savingsSvg?.let { svg ->
                    val svgRect = svg.getBoundingClientRect()
                    val labelRect = targetLabel.getBoundingClientRect()
                    val relativeX = labelRect.left - svgRect.left - 60
                    val relativeY = labelRect.top - svgRect.top - 25
                    tooltip.style.left = "${relativeX.toInt()}px"
                    tooltip.style.top = "${relativeY.toInt()}px"
                }
            }
        })
        targetLabel?.addEventListener("mouseleave", {
            savingsTooltip?.let { tooltip ->
                tooltip.style.display = "none"
                tooltip.style.borderColor = WebTheme.current.accent
            }
        })

        // Clicking a month selects it as the dashboard's focal month. One delegated
        // listener on the SVG covers both the dots and the full-column hit rects, and
        // it is re-attached on every render (the widget re-renders into innerHTML, so
        // the SVG element is always fresh — no duplicate listeners).
        savingsSvg?.addEventListener("click", { event ->
            val el = event.target as? org.w3c.dom.Element ?: return@addEventListener
            val year = el.getAttribute("data-focal-year")?.toIntOrNull() ?: return@addEventListener
            val month = el.getAttribute("data-focal-month")?.toIntOrNull() ?: return@addEventListener
            savingsTooltip?.style?.display = "none"
            selectFocalYearMonth(year, month)
        })
    }

    /** Setup expense timeline chart tooltips */
    private fun setupExpenseTimelineTooltips(cont: HTMLElement) {
        val expenseTooltip = cont.querySelector("#expenseChartTooltip") as? HTMLElement
        val expenseSvg = cont.querySelector("#expenseChartSvg") as? org.w3c.dom.Element
        val expenseDataPoints = cont.querySelectorAll(".expense-data-point")
        for (i in 0 until expenseDataPoints.length) {
            val point = expenseDataPoints.item(i) as? org.w3c.dom.Element
            val showTooltip = showTooltip@{ _: org.w3c.dom.events.Event ->
                val tooltipText = point?.getAttribute("data-tooltip") ?: return@showTooltip
                expenseTooltip?.let { tooltip ->
                    tooltip.textContent = tooltipText
                    tooltip.style.display = "block"
                    expenseSvg?.let { svg ->
                        val svgRect = svg.getBoundingClientRect()
                        val pointRect = point.getBoundingClientRect()
                        val tipWidth = tooltip.offsetWidth.toDouble()
                        var relativeX = pointRect.left - svgRect.left + 15
                        if (relativeX + tipWidth > svgRect.width) {
                            relativeX = pointRect.left - svgRect.left - tipWidth - 15
                        }
                        relativeX = maxOf(0.0, relativeX)
                        val relativeY = pointRect.top - svgRect.top - 10
                        tooltip.style.left = "${relativeX.toInt()}px"
                        tooltip.style.top = "${relativeY.toInt()}px"
                    }
                }
            }
            point?.addEventListener("mouseenter", showTooltip)
            point?.addEventListener("touchstart", { event ->
                event.preventDefault()
                showTooltip(event)
            })
            point?.addEventListener("mouseleave", { expenseTooltip?.style?.display = "none" })
        }

        // Clicking a month selects it as the dashboard's focal month — see
        // setupSavingsTrendTooltips for why this is one delegated listener on the SVG.
        expenseSvg?.addEventListener("click", { event ->
            val el = event.target as? org.w3c.dom.Element ?: return@addEventListener
            val year = el.getAttribute("data-focal-year")?.toIntOrNull() ?: return@addEventListener
            val month = el.getAttribute("data-focal-month")?.toIntOrNull() ?: return@addEventListener
            expenseTooltip?.style?.display = "none"
            selectFocalYearMonth(year, month)
        })
    }

    private fun renderTopNav(isMobile: Boolean = false): String {
        val logoSrc = if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"
        // Halo shows until the user has seen whichever tour variant they're about to get.
        val tourScreenForHalo =
            if (useGridLayout) guide.GuideScreen.DASHBOARD_GRID else guide.GuideScreen.DASHBOARD_STANDARD
        val firstVisitTour = !GuideOverlay.hasSeen(tourScreenForHalo)
        val tourHaloClass = if (firstVisitTour) "wiz3r-guide-halo" else ""
        val tourTooltip = StringKey.TOUR_OPEN_TOOLTIP.tr()
        return if (isMobile) {
            // Mobile navigation with hamburger menu
            """
            <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: sticky; top: 0; z-index: 100;">
                <img id="dashboard-logo" src="$logoSrc" alt="wiz≡r" style="height: 32px; cursor: pointer;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; align-items: center; gap: 12px;">
                    <span id="nav-tour" class="$tourHaloClass" style="width: 26px; height: 26px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <button id="hamburgerMenu" data-guide="menu-button" style="background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer; padding: 4px;">☰</button>
                </div>
            </div>
            """
        } else {
            // Desktop navigation
            """
            <div style="display: flex; align-items: center; padding: 16px 32px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: fixed; top: 0; left: 0; right: 0; z-index: 100; transition: padding 0.25s ease;">
                <img id="dashboard-logo" src="$logoSrc" alt="wiz≡r" style="height: 56px; cursor: pointer; margin-right: 40px; transition: height 0.25s ease;" title="${StringKey.TOOLTIP_GO_HOME.tr()}">
                <div style="display: flex; gap: 8px; flex: 1;">
                    <span id="nav-activity" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                    <span id="nav-dashboard" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: ${t.navBackground}; color: ${t.textPrimary};">${StringKey.NAV_DASHBOARD.tr()}</span>
                    <span id="nav-goals" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_GOALS.tr()}</span>
                    <span id="nav-budget" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_BUDGET.tr()}</span>
                </div>
                <div style="display: flex; align-items: center; gap: 12px;">
                    <span id="nav-tour" class="$tourHaloClass" style="width: 28px; height: 28px; border-radius: 50%; background: ${t.navBackground}; color: ${t.accent}; border: 1px solid ${t.border}; display: inline-flex; align-items: center; justify-content: center; font-size: 15px; font-weight: 700; cursor: pointer; font-family: serif; user-select: none;" title="$tourTooltip">i</span>
                    <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border}; transition: all 0.2s;" title="${StringKey.TOOLTIP_CHANGE_LANGUAGE.tr()}">${languageToggleLabel()}</span>
                    <span id="nav-guide" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 14px; cursor: pointer; border: 1px solid ${t.border};" title="${StringKey.TOOLTIP_HOW_IT_WORKS.tr()}">❓</span>
                    <span id="hamburgerMenuDesktop" data-guide="menu-button" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; border: 1px solid ${t.border};">&#9776;</span>
                </div>
            </div>
            <div style="height: 80px;"></div>
            """
        }
    }

    // renderAccountSelector moved into renderAccountSelectorContent() for widget system

    // renderLeftPanel and renderChart removed - replaced by GridStack widget system

    private fun renderSavingsLineChart(): String {
        val now = Date()
        val currentYear = now.getFullYear()
        val currentMonth = now.getMonth() + 1

        // Determine display mode based on selected year
        // Current year: rolling 12-month window
        // Past year: full calendar year (Jan-Dec)
        val isCurrentYear = selectedYear == currentYear

        val (window, lastIndexWithData) = if (isCurrentYear) {
            // Rolling 12-month window for current year
            val w = getRolling12MonthWindow() // 13 months: 12 data + 1 future
            Pair(w, getCurrentMonthIndexInWindow()) // Index 11 is current month
        } else {
            // Full calendar year for past years (Jan-Dec of selectedYear)
            val w = (1..12).map { YearMonth(selectedYear, it) }
            Pair(w, 11) // All 12 months have data for past years
        }

        // Calculate savings percentages for each month
        val savingsData = window.mapIndexed { index, ym ->
            if (index <= lastIndexWithData) {
                // Past or current month: show data (or 0 if no data)
                getDataForYearMonth(ym)?.savingsPercentage ?: 0.0
            } else {
                null // Future month: no dot
            }
        }

        // Clamp to ±200% for scale/Y calculation; raw values kept for tooltip display
        val chartClamp = 200.0
        val clampedSavingsData = savingsData.map { it?.coerceIn(-chartClamp, chartClamp) }

        // Match triple-bar chart layout for aligned month spacing
        val barGroupWidth = 30 // barWidth(8)*3 + barGap(2)*2
        val barGroupGap = 12
        val monthStep = barGroupWidth + barGroupGap // 42
        val barPadding = 20
        val barChartWidth = monthStep * 12 - barGroupGap // 492
        val paddingLeft = 45
        val paddingRight = paddingLeft - barPadding + barPadding // keep symmetric Y-axis label space
        val width = barChartWidth + paddingLeft + paddingRight
        val height = 220
        val paddingTop = 20
        val paddingBottom = 30
        val chartWidth = barChartWidth
        val chartHeight = height - paddingTop - paddingBottom

        // Find min and max values for scaling - support negative percentages (use clamped data)
        val dataValues = clampedSavingsData.filterNotNull()
        val dataMin = dataValues.minOrNull() ?: 0.0
        val dataMax = dataValues.maxOrNull() ?: 0.0

        // Calculate nice Y-axis range that includes 0, target, and all data
        // Round to nice intervals (multiples of 25 or 50)
        // Fit the axis to the data (plus the target and a little headroom) rather
        // than a fixed -25→50 band, so the line isn't squished into a sliver.
        // The step is picked so the labels stay round.
        val lo = minOf(0.0, dataMin)
        val hi = maxOf(dataMax, savingsTarget)
        val axisStep = when {
            (hi - lo) <= 35.0 -> 10.0
            (hi - lo) <= 80.0 -> 25.0
            else -> 50.0
        }
        val minValue = kotlin.math.floor(lo / axisStep) * axisStep
        val maxValue = kotlin.math.ceil((hi + axisStep * 0.4) / axisStep) * axisStep
        val valueRange = maxValue - minValue

        // Helper to convert value to Y coordinate
        fun valueToY(value: Double): Int {
            val normalized = (value - minValue) / valueRange
            return (paddingTop + chartHeight - (normalized * chartHeight)).toInt()
        }

        // Calculate X positions aligned with triple-bar chart months
        val numMonths = window.size
        val xSegments = (numMonths - 1).coerceAtLeast(1).toDouble()

        fun monthX(index: Int): Int {
            return if (numMonths == 12) {
                // Align exactly with triple-bar chart month centers
                paddingLeft + index * monthStep + barGroupWidth / 2
            } else {
                // 13-month rolling window: spread evenly
                paddingLeft + (barGroupWidth / 2) + (index * (chartWidth - barGroupWidth) / xSegments).toInt()
            }
        }

        // Width of one month's column, used for the full-height click targets.
        val monthSlotWidth = if (numMonths > 1) {
            (monthX(numMonths - 1) - monthX(0)).toDouble() / (numMonths - 1)
        } else chartWidth.toDouble()

        // Generate path points only for months with data (not null), using clamped Y positions
        val pointsList = clampedSavingsData.mapIndexedNotNull { index, value ->
            if (value != null) {
                val x = monthX(index)
                val y = valueToY(value)
                Pair(x, y)
            } else null
        }
        val points = pointsList.joinToString(" ") { "${it.first},${it.second}" }

        // Generate fill area path - fill to 0% line, not bottom of chart
        val zeroY = valueToY(0.0)
        val fillPoints = if (pointsList.isNotEmpty()) {
            val firstX = pointsList.first().first
            val lastX = pointsList.last().first
            "M${firstX},$zeroY " +
                pointsList.joinToString(" ") { "L${it.first},${it.second}" } +
                " L${lastX},$zeroY Z"
        } else ""

        // Month labels - show all months in the window; bold + bright the selected one.
        val (selLabelYear, selLabelMonth) = effectiveDisplayYearMonth()
        val monthLabels = window.mapIndexed { index, ym ->
            val x = monthX(index)
            val label = ym.toLabel()
            val sel = ym.year == selLabelYear && ym.month == selLabelMonth
            val fill = if (sel) t.textPrimary else t.textSecondary
            val weight = if (sel) "700" else "400"
            """<text x="$x" y="${height - 5}" fill="$fill" font-size="9" text-anchor="middle" font-weight="$weight" pointer-events="none">$label</text>"""
        }.joinToString("")

        // Full-column click targets — one per month that has a point. Clicking makes
        // that month the dashboard's focal month; the window is rolling, so the point
        // carries its own YEAR (Aug–Dec belong to the previous one). Painted under the
        // fill/line/dots so the dots keep owning the hover tooltip, while a click
        // anywhere in the column still lands. Months with no point (the future tail,
        // same null rule the dots use) get no target and stay unselectable.
        val monthHitAreas = savingsData.mapIndexedNotNull { index, value ->
            if (value == null) null else {
                val ym = window[index]
                val rx = (monthX(index) - monthSlotWidth / 2).toInt()
                """<rect x="$rx" y="$paddingTop" width="${monthSlotWidth.toInt()}" height="${chartHeight + paddingBottom}" fill="transparent" class="savings-month-hit" style="cursor: pointer;" data-focal-year="${ym.year}" data-focal-month="${ym.month}" />"""
            }
        }.joinToString("")

        // Y-axis labels - generate based on actual range
        val yAxisValues = mutableListOf<Int>()
        var yVal = minValue.toInt()
        while (yVal <= maxValue.toInt()) {
            yAxisValues.add(yVal)
            yVal += axisStep.toInt()
        }

        val yLabels = yAxisValues.map { value ->
            val y = valueToY(value.toDouble())
            val color = if (value < 0) t.expenseRed else t.textSecondary
            """<text x="${paddingLeft - 8}" y="$y" fill="$color" font-size="10" text-anchor="end" dominant-baseline="middle">${value}%</text>"""
        }.joinToString("")

        // Zero line (if we have negative values)
        val zeroLine = if (minValue < 0) {
            """<line x1="$paddingLeft" y1="$zeroY" x2="${width - paddingRight}" y2="$zeroY" stroke="${t.border}" stroke-width="1" />"""
        } else ""

        // Target line at configured percentage
        val targetY = valueToY(savingsTarget)

        // Chart title depends on display mode
        val chartTitle = if (isCurrentYear) StringKey.CHART_SAVINGS_TREND_ROLLING.tr() else StringKey.CHART_SAVINGS_TREND_YEAR.tr(selectedYear.toString())

        return """
            <div style="width: 100%; height: 100%; position: relative; display: flex; flex-direction: column; box-sizing: border-box;">
                <div style="text-align: center; margin-bottom: 8px; flex: 0 0 auto;">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$chartTitle</span>
                </div>
                <!-- Instant tooltip container -->
                <div id="savingsChartTooltip" style="
                    position: absolute;
                    display: none;
                    background: ${if (t.isDark) "rgba(20,20,20,0.95)" else t.card};
                    border: 1px solid ${t.accent};
                    border-radius: 6px;
                    padding: 6px 10px;
                    color: ${t.textPrimary};
                    font-size: 12px;
                    font-weight: 500;
                    pointer-events: none;
                    z-index: 100;
                    white-space: nowrap;
                    box-shadow: ${if (t.isDark) "0 2px 8px rgba(0,0,0,0.3)" else "0 2px 8px rgba(0,0,0,0.12)"};
                "></div>
                <svg width="100%" height="100%" viewBox="0 0 $width $height" preserveAspectRatio="xMidYMax meet" id="savingsChartSvg" style="flex: 1; min-height: 0; display: block;">
                    <!-- Zero line (baseline) -->
                    $zeroLine

                    <!-- Target line with dashed stroke -->
                    <line x1="$paddingLeft" y1="$targetY" x2="${width - paddingRight}" y2="$targetY"
                        stroke="#FFA500" stroke-dasharray="4,4" stroke-opacity="0.6" />

                    <!-- Fill gradient area -->
                    <defs>
                        <linearGradient id="savingsGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" style="stop-color:${t.accent};stop-opacity:0.3" />
                            <stop offset="100%" style="stop-color:${t.accent};stop-opacity:0.05" />
                        </linearGradient>
                        <linearGradient id="savingsGradientNeg" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" style="stop-color:${t.expenseRed};stop-opacity:0.05" />
                            <stop offset="100%" style="stop-color:${t.expenseRed};stop-opacity:0.3" />
                        </linearGradient>
                    </defs>
                    <!-- Month click targets (under the plot, so the dots keep the hover) -->
                    $monthHitAreas

                    ${if (fillPoints.isNotEmpty()) """<path d="$fillPoints" fill="url(#savingsGradient)" style="pointer-events: none;" />""" else ""}

                    <!-- Line -->
                    ${if (points.isNotEmpty()) """<polyline points="$points" fill="none" stroke="${t.accent}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;" />""" else ""}

                    <!-- Data points with data attributes for instant tooltips -->
                    ${savingsData.mapIndexedNotNull { index, value ->
                        if (value != null) {
                            val clampedValue = value.coerceIn(-chartClamp, chartClamp)
                            val x = monthX(index)
                            val y = valueToY(clampedValue) // clamped Y position
                            val ym = window[index]
                            val monthLabel = ym.toFullLabel()
                            val valueFormatted = value.roundToInt() // real value in tooltip
                            val pointColor = if (value < 0) t.expenseRed else t.accent
                            // The focal month's dot is drawn larger, matching the bold X-axis label.
                            val sel = ym.year == selLabelYear && ym.month == selLabelMonth
                            // Only draw visible dot if value is within ±200% range
                            val visibleDot = if (kotlin.math.abs(value) <= chartClamp) {
                                """<circle cx="$x" cy="$y" r="${if (sel) 8 else 5}" fill="$pointColor" stroke="${t.background}" stroke-width="2" style="pointer-events: none;" />"""
                            } else ""
                            """<circle cx="$x" cy="$y" r="20" fill="transparent" class="savings-data-point" style="cursor: pointer;" data-tooltip="$monthLabel: $valueFormatted%" data-focal-year="${ym.year}" data-focal-month="${ym.month}" />$visibleDot"""
                        } else null
                    }.joinToString("")}

                    <!-- Target label -->
                    <text class="savings-target-label" x="${width - paddingRight + 5}" y="$targetY" fill="#FFA500" font-size="10" dominant-baseline="middle" style="cursor: pointer;" data-tooltip="${StringKey.CHART_TARGET_TOOLTIP.tr(savingsTarget.roundToInt().toString())}">${StringKey.CHART_TARGET_LABEL.tr()}</text>

                    <!-- Axis labels -->
                    $monthLabels
                    $yLabels
                </svg>
                <div style="display: flex; justify-content: center; gap: 16px; margin-top: 8px;">
                    <span style="display: flex; align-items: center; gap: 6px; color: ${t.textSecondary}; font-size: 11px;">
                        <span style="width: 12px; height: 3px; background: ${t.accent}; border-radius: 2px;"></span>
                        ${StringKey.CHART_ACTUAL_SAVINGS_PERCENT.tr()}
                    </span>
                    <span style="display: flex; align-items: center; gap: 6px; color: ${t.textSecondary}; font-size: 11px;">
                        <span style="width: 12px; height: 0; border-top: 2px dashed #FFA500;"></span>
                        ${StringKey.CHART_TARGET_PERCENT.tr(savingsTarget.toInt().toString())}
                    </span>
                </div>
            </div>
        """
    }

    private fun renderBarChartPlaceholder(): String {
        // focusedMonth wins; otherwise the last complete month (see effectiveDisplayYearMonth).
        val (displayYear, displayMonth) = effectiveDisplayYearMonth()
        val monthName = monthNames[displayMonth - 1]

        val filteredSpending = getCategorySpendingForMonth(displayYear, displayMonth)
        if (!transactionsLoaded || filteredSpending.isEmpty()) {
            return """
                <div style="text-align: center; color: ${t.textSecondary};">
                    <div style="margin-bottom: 12px;"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${t.border}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21v-16a2 2 0 0 1 2 -2h10a2 2 0 0 1 2 2v16l-3 -2l-2 2l-2 -2l-2 2l-2 -2l-3 2m4 -14h6m-6 4h6m-2 4h2"/></svg></div>
                    <div>${StringKey.CHART_CATEGORY_SPENDING_MONTH.tr(monthName)}</div>
                    <div style="font-size: 12px; color: ${t.textSecondary}; margin-top: 8px;">${if (transactionsLoaded) StringKey.CHART_NO_SPENDING_YET.tr() else StringKey.CHART_LOADING_TRANSACTIONS.tr()}</div>
                </div>
            """
        }

        // Get planned budgets for the display month
        val plannedBudgets = getPlannedBudgetsForMonth(selectedYear, displayMonth)
        val hasBudgetData = plannedBudgets.isNotEmpty()

        // Render actual category spending bar chart with planned comparison
        val sortedCategories = filteredSpending.entries.sortedByDescending { it.value }.take(8)

        // Max value should consider both actual and planned amounts (only include planned if budget visible)
        val maxActual = sortedCategories.maxOfOrNull { it.value } ?: 1.0
        val maxPlanned = if (showCategoryBudget) sortedCategories.maxOfOrNull { (cat, _) -> plannedBudgets[cat] ?: 0.0 } else null
        val maxValue = maxOf(maxActual, maxPlanned ?: 0.0, 1.0)

        val bars = sortedCategories.mapIndexed { index, (category, actualAmount) ->
            val plannedAmount = plannedBudgets[category] ?: 0.0
            val color = utils.CategoryUtils.getCategoryColor(categoryDisplayToColorCode[category] ?: categoryDisplayToPlaidCode[category] ?: category, userCategoryColors)
            val displayName = utils.CategoryUtils.getEffectiveDisplayCategory(category, category)

            // Calculate bar widths as percentages
            val plannedWidth = if (plannedAmount > 0) (plannedAmount / maxValue * 100).toInt() else 0
            val actualWidth = (actualAmount / maxValue * 100).toInt()

            // Determine comparison text and status
            val (comparisonText, statusColor) = when {
                plannedAmount <= 0 -> "" to t.textSecondary // No budget set
                actualAmount <= plannedAmount -> {
                    val percentUnder = ((plannedAmount - actualAmount) / plannedAmount * 100).roundToInt()
                    StringKey.CHART_UNDER_FORECAST.tr(percentUnder.toString(), plannedAmount.roundToInt().toString()) to t.incomeGreen // Green
                }
                else -> {
                    val percentOver = ((actualAmount - plannedAmount) / plannedAmount * 100).roundToInt()
                    StringKey.CHART_OVER_FORECAST.tr(plannedAmount.roundToInt().toString(), percentOver.toString()) to t.expenseRed // Red
                }
            }

            // Bar design: faint background (budget), dimmer fill (within budget), bright fill (excess)
            val isOverBudget = showCategoryBudget && actualAmount > plannedAmount && plannedAmount > 0
            val withinBudgetWidth = if (isOverBudget) plannedWidth else actualWidth
            val excessWidth = if (isOverBudget) ((actualAmount - plannedAmount) / maxValue * 100).toInt() else 0

            // Within-budget fill: dimmed on dark (55% factor), full color on light
            val dimmerColor = if (t.isDark && color.startsWith("#") && color.length == 7) {
                val r = (color.substring(1, 3).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                val g = (color.substring(3, 5).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                val b = (color.substring(5, 7).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}"
            } else color
            // Excess fill: full color on dark (bright), darkened on light
            val excessColor = if (!t.isDark && color.startsWith("#") && color.length == 7) {
                val r = (color.substring(1, 3).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                val g = (color.substring(3, 5).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                val b = (color.substring(5, 7).toInt(16) * 0.55).toInt().coerceIn(0, 255)
                "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}"
            } else color

            """
                <div class="category-bar-row" data-category="$category" style="display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; cursor: pointer; padding: 4px 8px; border-radius: 6px; transition: background 0.2s;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <div class="category-name" style="width: 72px; font-size: 10px; color: ${t.textSecondary}; text-align: right; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; transition: all 0.2s ease-out;" title="$displayName">$displayName</div>
                        <div style="flex: 1; height: 16px; background: ${if (t.isDark) "#060606" else "#EEEAE1"}; border-radius: 4px; overflow: hidden; position: relative;">
                            ${if (showCategoryBudget && plannedAmount > 0) """
                                <!-- Budget background bar (almost black, hint of color) -->
                                <div style="position: absolute; left: 0; top: 0; width: ${plannedWidth}%; height: 100%; background: ${color}; opacity: 0.15; border-radius: 4px;"></div>
                            """ else ""}
                            <!-- Dimmer fill = actual within budget, plain dimmer when no budget context -->
                            <div class="category-bar-fill" style="position: absolute; left: 0; top: 0; width: ${withinBudgetWidth}%; height: 100%; background: ${if (showCategoryBudget && hasBudgetData) dimmerColor else color}; border-radius: 4px; transition: all 0.2s;"></div>
                            ${if (isOverBudget && excessWidth > 0) """
                                <!-- Excess fill: bright on dark, dark on light -->
                                <div style="position: absolute; left: ${withinBudgetWidth}%; top: 0; width: ${excessWidth}%; height: 100%; background: $excessColor; border-radius: 0 4px 4px 0;"></div>
                            """ else ""}
                        </div>
                        <div class="category-value" style="width: 50px; font-size: 10px; color: ${t.textSecondary}; text-align: left; transition: all 0.2s ease-out;">$${actualAmount.roundToInt()}</div>
                    </div>
                    ${if (showCategoryBudget && hasBudgetData && plannedAmount > 0) """
                        <div class="category-comparison" style="display: none; margin-left: 88px; font-size: 9px; color: $statusColor; transition: opacity 0.2s;">$comparisonText</div>
                    """ else ""}
                </div>
            """
        }.joinToString("")

        // Chart title shows current month
        val chartTitle = StringKey.CHART_CATEGORY_SPENDING_MONTH.tr(monthName)

        // Legend for planned vs actual
        val legend = if (hasBudgetData) """
            <div style="display: flex; justify-content: center; gap: 16px; margin-top: 12px; padding-top: 8px; border-top: 1px solid ${t.border};">
                <span id="legend-category-budget" style="display: flex; align-items: center; gap: 6px; color: ${if (showCategoryBudget) t.textSecondary else t.border}; font-size: 10px; cursor: pointer; ${if (!showCategoryBudget) "text-decoration: line-through;" else ""}">
                    <span style="width: 12px; height: 8px; background: ${if (showCategoryBudget) "rgba(0,217,255,0.15)" else t.border}; border-radius: 2px;"></span>
                    ${StringKey.CHART_BUDGET_LEGEND.tr()}
                </span>
                <span style="display: flex; align-items: center; gap: 6px; color: ${t.textSecondary}; font-size: 10px;">
                    <span style="width: 12px; height: 8px; background: ${t.accentStrong}; border-radius: 2px;"></span>
                    ${StringKey.CHART_ACTUAL_LEGEND.tr()}
                </span>
                <span style="display: flex; align-items: center; gap: 6px; color: ${t.textSecondary}; font-size: 10px;">
                    <span style="width: 12px; height: 8px; background: ${t.accent}; border-radius: 2px;"></span>
                    ${StringKey.CHART_OVER_BUDGET_LEGEND.tr()}
                </span>
            </div>
        """ else ""

        return """
            <div style="width: 100%;">
                <div style="text-align: center; margin-bottom: 16px;">
                    <span style="color: ${t.textSecondary}; font-size: 12px;">$chartTitle</span>
                </div>
                $bars
                $legend
            </div>
        """
    }

    // Expense Distribution's adaptive layout — uses the same SizeBucket
    // vocabulary as Monthly Overview. Simpler decision: just pie vs
    // legend orientation. When the widget is wide enough to fit both
    // side-by-side, render them in a row; otherwise stack the legend
    // below the pie so neither gets cramped.
    private enum class PieOrientation { SIDE_BY_SIDE, STACKED }
    private data class PieLayout(val orientation: PieOrientation)

    private fun pieLayoutFor(cols: Int, rows: Int, wPx: Double? = null, hPx: Double? = null): PieLayout {
        // Pick the orientation that yields the BIGGER pie, from the widget's real
        // pixel box — grid columns alone can't tell a wide widget from a tall one.
        // Estimate the pie diameter each way (legend eats ~45% of width beside the
        // pie, or ~40% of height below it):
        //   SIDE_BY_SIDE  d ≈ min(0.55·W, H)     — wins when landscape
        //   STACKED       d ≈ min(W, 0.60·H)     — wins when portrait
        val orientation = if (wPx != null && hPx != null && wPx > 0.0 && hPx > 0.0) {
            val sideBySide = minOf(0.55 * wPx, hPx)
            val stacked = minOf(wPx, 0.60 * hPx)
            // Under ~400px there is no arrangement where a readable pie and a
            // readable legend both fit on one row, whatever the maths prefers.
            // The TikTok capture runs a 444px-wide window, which lands here.
            when {
                wPx < 400.0 -> PieOrientation.STACKED
                stacked > sideBySide -> PieOrientation.STACKED
                else -> PieOrientation.SIDE_BY_SIDE
            }
        } else {
            // No pixel box yet (first paint) — fall back to the column heuristic.
            if (cols < 4) PieOrientation.STACKED else PieOrientation.SIDE_BY_SIDE
        }
        return PieLayout(orientation)
    }

    /** The widget's rendered content box in raw pixels (width to height), or null
     *  if it isn't laid out yet. Unlike [widgetColumnWidth]/[widgetRowHeight] it
     *  does NOT quantise to grid units, so a caller can reason about the true
     *  aspect ratio — the pie chart uses it to pick side-by-side vs stacked. */
    private fun widgetPixelBox(widgetId: String): Pair<Double, Double>? {
        val gridItem = container?.querySelector("#widget-content-$widgetId")
            ?.closest(".grid-stack-item") as? HTMLElement ?: return null
        val w = gridItem.offsetWidth.toDouble()
        val h = gridItem.offsetHeight.toDouble()
        return if (w > 0.0 && h > 0.0) w to h else null
    }

    private fun renderPieChartPlaceholder(widgetWidthCols: Int = 6, widgetHeightRows: Int = 5, widthPx: Double? = null, heightPx: Double? = null): String {
        val isCurrentYear = selectedYear == Date().getFullYear()
        // focusedMonth wins; otherwise the last complete month (see effectiveDisplayYearMonth).
        val (effectiveYear, effectiveMonth) = effectiveDisplayYearMonth()
        val pillsHtml = renderPieChartPills()

        // Branch data source on time-range toggle
        val filteredSpending = if (pieRollingMode) {
            getFilteredCategorySpendingForDisplayWindow()
        } else {
            getCategorySpendingForMonth(effectiveYear, effectiveMonth)
        }
        if (!transactionsLoaded || filteredSpending.isEmpty()) {
            return """
                <div style="width: 100%;">
                    $pillsHtml
                    <div style="text-align: center; color: ${t.textSecondary};">
                        <div style="margin-bottom: 12px;"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${t.border}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 3.2a9 9 0 1 0 10.8 10.8a1 1 0 0 0 -1 -1h-6.8a2 2 0 0 1 -2 -2v-7a.9 .9 0 0 0 -1 -.8"/><path d="M15 3.5a9 9 0 0 1 5.5 5.5h-4.5a1 1 0 0 1 -1 -1v-4.5"/></svg></div>
                        <div>${StringKey.DASHBOARD_EXPENSE_DISTRIBUTION.tr()}</div>
                        <div style="font-size: 12px; color: ${t.textSecondary}; margin-top: 8px;">${if (transactionsLoaded) StringKey.CHART_NO_DATA_ACCOUNTS.tr() else StringKey.CHART_LOADING_TRANSACTIONS.tr()}</div>
                    </div>
                </div>
            """
        }

        // Render pie chart as SVG
        // take(8) matches iOS (.prefix(8)) and Android (.take(8)); total from displayed slices only so they fill 360°
        val sortedCategories = filteredSpending.entries.sortedByDescending { it.value }.take(8)
        val total = sortedCategories.sumOf { it.value }
        val size = 220
        val cx = size / 2
        val cy = size / 2
        val radius = 80

        var currentAngle = -90.0 // Start at top
        val slices = sortedCategories.mapIndexed { index, (category, amount) ->
            val percentage = amount / total
            val angle = percentage * 360
            val startAngle = currentAngle
            val endAngle = currentAngle + angle

            val largeArc = if (angle > 180) 1 else 0
            val x1 = cx + radius * kotlin.math.cos(startAngle * kotlin.math.PI / 180)
            val y1 = cy + radius * kotlin.math.sin(startAngle * kotlin.math.PI / 180)
            val x2 = cx + radius * kotlin.math.cos(endAngle * kotlin.math.PI / 180)
            val y2 = cy + radius * kotlin.math.sin(endAngle * kotlin.math.PI / 180)

            currentAngle = endAngle
            val color = utils.CategoryUtils.getCategoryColor(categoryDisplayToColorCode[category] ?: categoryDisplayToPlaidCode[category] ?: category, userCategoryColors)

            // Add class, data-index for linking to legend, and style for hover transform
            """<path class="pie-slice" data-index="$index" d="M$cx,$cy L${x1.toInt()},${y1.toInt()} A$radius,$radius 0 $largeArc,1 ${x2.toInt()},${y2.toInt()} Z" fill="$color" opacity="0.85" stroke="${t.card}" stroke-width="2" stroke-linejoin="round" style="cursor: pointer; transform-origin: ${cx}px ${cy}px; transition: transform 0.2s ease-out, opacity 0.2s;"/>"""
        }.joinToString("")

        // Legend shows percentages ONLY (no dollar amounts - those are in Category Spending chart)
        val legend = sortedCategories.mapIndexed { index, (category, amount) ->
            val color = utils.CategoryUtils.getCategoryColor(categoryDisplayToColorCode[category] ?: categoryDisplayToPlaidCode[category] ?: category, userCategoryColors)
            val displayName = utils.CategoryUtils.getEffectiveDisplayCategory(category, category)
            val percentage = (amount / total * 100).toInt()
            """
                <div class="pie-legend-item" data-index="$index" style="display: flex; align-items: center; gap: 6px; font-size: 10px; cursor: pointer; padding: 4px 8px; border-radius: 4px; transform-origin: left center; transition: all 0.2s ease-out;">
                    <span class="pie-legend-color" data-category="$category" style="width: 8px; height: 8px; background: $color; border-radius: 50%; transition: transform 0.2s;"></span>
                    <span class="pie-legend-name" style="color: ${t.textSecondary}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; transition: all 0.2s ease-out;" title="$displayName">$displayName</span>
                    <span class="pie-legend-value" style="color: ${t.textSecondary}; transition: all 0.2s ease-out;">$percentage%</span>
                </div>
            """
        }.joinToString("")

        // Chart title reflects the active range
        val chartTitle = if (pieRollingMode) {
            if (isCurrentYear) StringKey.CHART_EXPENSE_DIST_ROLLING.tr()
            else StringKey.CHART_EXPENSE_DIST_YEAR.tr(selectedYear.toString())
        } else {
            val monthNames = listOf(
                StringKey.MONTH_JANUARY.tr(), StringKey.MONTH_FEBRUARY.tr(), StringKey.MONTH_MARCH.tr(),
                StringKey.MONTH_APRIL.tr(), StringKey.MONTH_MAY_FULL.tr(), StringKey.MONTH_JUNE.tr(),
                StringKey.MONTH_JULY.tr(), StringKey.MONTH_AUGUST.tr(), StringKey.MONTH_SEPTEMBER.tr(),
                StringKey.MONTH_OCTOBER.tr(), StringKey.MONTH_NOVEMBER.tr(), StringKey.MONTH_DECEMBER.tr()
            )
            val monthName = monthNames.getOrNull(effectiveMonth - 1) ?: ""
            StringKey.CHART_EXPENSE_DIST_YEAR.tr("$monthName $selectedYear")
        }

        // Adaptive layout — orientation decided by pieLayoutFor.
        val layout = pieLayoutFor(widgetWidthCols, widgetHeightRows, widthPx, heightPx)

        // Pie SVG block (same in both orientations). Removed the legacy
        // max-height: 300px cap so the pie can scale up to fill the
        // available vertical space when the widget is tall — same fix
        // pattern as the Monthly Overview's flex restructure.
        val pieSvg = """
            <svg id="pieChartSvg" viewBox="0 0 $size $size" preserveAspectRatio="xMidYMid meet" style="overflow: visible; width: 100%; height: 100%; display: block;">
                $slices
                <circle cx="$cx" cy="$cy" r="40" fill="${t.card}"/>
            </svg>
        """
        // No flex-shrink: 0 here. It let the legend hold its full intrinsic
        // width, squeeze the pie's flex: 1 1 0 box to nothing, and then spill
        // past the card edge on a narrow widget — a pie chart with no pie and
        // a legend floating outside its own border.
        // margin: auto 0 is doing real work — it centres the list while it fits
        // and collapses to 0 once it doesn't, so an overflowing legend starts at
        // its first row instead of being clipped at BOTH ends the way
        // justify-content: center clips it. Overflow is owned by the wrapper.
        val legendBlock = """
            <div style="display: flex; flex-direction: column; gap: 6px; min-width: 0; margin: auto 0;">
                $legend
            </div>
        """

        // Outer container is a flex column. Pills + title are fixed-
        // height; the pie+legend container takes the rest (flex: 1)
        // so the SVG scales to fill it. Inside, orientation flips
        // between flex row (SIDE_BY_SIDE) and flex column (STACKED).
        val pieLegendContainer = when (layout.orientation) {
            PieOrientation.SIDE_BY_SIDE -> """
                <div style="flex: 1 1 0; min-height: 180px; display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 16px; overflow: hidden;">
                    <div style="flex: 1 1 0; min-width: 0; min-height: 160px; height: 100%; display: flex; align-items: center; justify-content: center;">
                        $pieSvg
                    </div>
                    <div style="flex: 0 0 46%; min-width: 0; align-self: stretch; overflow-y: auto; overflow-x: hidden; padding-right: 4px; display: flex; flex-direction: column;">
                        $legendBlock
                    </div>
                </div>
            """
            PieOrientation.STACKED -> """
                <div style="flex: 1 1 0; min-height: 200px; display: flex; flex-direction: column; align-items: center; gap: 10px; overflow: hidden;">
                    <div style="flex: 3 1 0; min-height: 150px; width: 100%; display: flex; align-items: center; justify-content: center;">
                        $pieSvg
                    </div>
                    <div style="flex: 2 1 0; min-height: 0; width: 100%; overflow-y: auto; display: flex; flex-direction: column; align-items: center;">
                        $legendBlock
                    </div>
                </div>
            """
        }

        return """
            <div style="display: flex; flex-direction: column; height: 100%; min-height: 0; width: 100%;">
                <div style="flex: 0 0 auto;">$pillsHtml</div>
                <div style="flex: 0 0 auto; text-align: center; margin-bottom: 12px;">
                    <span style="color: ${t.textSecondary}; font-size: 12px;">$chartTitle</span>
                </div>
                $pieLegendContainer
            </div>
        """
    }

    /** Re-render only the pie chart (used after pill toggle). */
    private fun rerenderPieChart() {
        if (useGridLayout) {
            renderWidgetContent("expense-dist")
        } else if (selectedChartType == ChartType.PIE_CHART) {
            val chartArea = container?.querySelector("#selectedChartArea") as? HTMLElement
            chartArea?.innerHTML = renderChart()
            chartArea?.let { setupPieLegendHoverEffects(it) }
        }
    }

    /** Pills above the pie chart that toggle between rolling-12-months and selected-month. */
    private fun renderPieChartPills(): String {
        fun pillStyle(active: Boolean) = "padding: 4px 10px; border: 1px solid ${if (active) t.chipSelectedBorder else t.chipUnselectedBorder}; border-radius: 12px; background: ${if (active) t.chipSelectedBg else t.card}; color: ${if (active) t.accent else t.textSecondary}; cursor: pointer; font-size: 10px; transition: all 0.2s;"
        return """
            <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin-bottom: 8px;">
                <button id="pie-pill-rolling" style="${pillStyle(pieRollingMode)}">${StringKey.CHART_PILL_ROLLING_12MO.tr()}</button>
                <button id="pie-pill-month" style="${pillStyle(!pieRollingMode)}">${StringKey.CHART_PILL_SELECTED_MONTH.tr()}</button>
            </div>
        """
    }

    private fun renderTimelinePlaceholder(): String {
        if (!transactionsLoaded) {
            return """
                <div style="text-align: center; color: ${t.textSecondary};">
                    <div style="margin-bottom: 12px;"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${t.border}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M3 8a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -8"/><path d="M18 12h.01"/><path d="M6 12h.01"/></svg></div>
                    <div>${StringKey.DASHBOARD_EXPENSE_TIMELINE.tr()}</div>
                    <div style="font-size: 12px; color: ${t.textSecondary}; margin-top: 8px;">${StringKey.CHART_LOADING_TRANSACTIONS.tr()}</div>
                </div>
            """
        }

        val now = Date()
        val currentYear = now.getFullYear()
        val isCurrentYear = selectedYear == currentYear

        // Get display window based on selected year
        val (window, lastIndexWithData) = getDisplayWindow()

        // Get expense data for each month in the window
        val expenseData = window.mapIndexed { index, ym ->
            if (index <= lastIndexWithData) {
                // Past or current month: show data (or 0 if no data)
                getDataForYearMonth(ym)?.realizedExpenses ?: 0.0
            } else {
                null // Future month: no dot
            }
        }

        // Check if we have any data
        val hasData = expenseData.filterNotNull().any { it > 0 }
        if (!hasData) {
            return """
                <div style="text-align: center; color: ${t.textSecondary};">
                    <div style="margin-bottom: 12px;"><svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="${t.border}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0"/><path d="M3 8a2 2 0 0 1 2 -2h14a2 2 0 0 1 2 2v8a2 2 0 0 1 -2 2h-14a2 2 0 0 1 -2 -2l0 -8"/><path d="M18 12h.01"/><path d="M6 12h.01"/></svg></div>
                    <div>${StringKey.DASHBOARD_EXPENSE_TIMELINE.tr()}</div>
                    <div style="font-size: 12px; color: ${t.textSecondary}; margin-top: 8px;">${StringKey.CHART_NO_EXPENSE_DATA.tr()}</div>
                </div>
            """
        }

        // Match triple-bar chart layout: same viewBox width, padding, and month spacing
        val barGroupWidth = 30 // barWidth(8)*3 + barGap(2)*2
        val barGroupGap = 12
        val monthStep = barGroupWidth + barGroupGap // 42
        val barPadding = 20
        val barChartWidth = monthStep * 12 - barGroupGap // 492
        val width = barChartWidth + barPadding * 2 // 532
        val height = 180
        val paddingTop = 20
        val paddingBottom = 30
        val chartWidth = barChartWidth
        val chartHeight = height - paddingTop - paddingBottom

        // Calculate X positions aligned with triple-bar chart months
        // Triple-bar month center = barPadding + (month-1)*monthStep + barGroupWidth/2
        val numMonths = window.size
        val xSegments = (numMonths - 1).coerceAtLeast(1).toDouble()

        // For 12-month windows (past year), align exactly with bar chart months
        // For 13-month windows (rolling), spread evenly across the same width
        fun monthX(index: Int): Int {
            return if (numMonths == 12) {
                // Align exactly with triple-bar chart month centers
                barPadding + index * monthStep + barGroupWidth / 2
            } else {
                // 13-month rolling window: spread evenly
                barPadding + (barGroupWidth / 2) + (index * (chartWidth - barGroupWidth) / xSegments).toInt()
            }
        }

        // Width of one month's column, used for the full-height click targets.
        val monthSlotWidth = if (numMonths > 1) {
            (monthX(numMonths - 1) - monthX(0)).toDouble() / (numMonths - 1)
        } else chartWidth.toDouble()

        // Max expense from past/current months only
        val maxExpense = expenseData.filterNotNull().maxOrNull()?.takeIf { it > 0 } ?: 1.0

        // Generate line path only for months with data (past/current)
        val pointsList = expenseData.mapIndexedNotNull { index, expense ->
            if (expense != null) {
                val x = monthX(index)
                val y = paddingTop + chartHeight - (expense / maxExpense * chartHeight)
                Pair(x, y.toInt())
            } else null
        }
        val points = pointsList.joinToString(" ") { "${it.first},${it.second}" }

        // Fill area path
        val fillPoints = if (pointsList.isNotEmpty()) {
            val firstX = pointsList.first().first
            val lastX = pointsList.last().first
            "M${firstX},${paddingTop + chartHeight} " +
                pointsList.joinToString(" ") { "L${it.first},${it.second}" } +
                " L${lastX},${paddingTop + chartHeight} Z"
        } else ""

        // Month labels - show all months in the window; bold + bright the selected one.
        val (selLabelYear, selLabelMonth) = effectiveDisplayYearMonth()
        val monthLabels = window.mapIndexed { index, ym ->
            val x = monthX(index)
            val label = ym.toLabel()
            val sel = ym.year == selLabelYear && ym.month == selLabelMonth
            val fill = if (sel) t.textPrimary else t.textSecondary
            val weight = if (sel) "700" else "400"
            """<text x="$x" y="${height - 5}" fill="$fill" font-size="9" text-anchor="middle" font-weight="$weight" pointer-events="none">$label</text>"""
        }.joinToString("")

        // Full-column click targets — see renderSavingsLineChart for the rationale.
        // Only months with a point (same null rule the dots use) get one, so the
        // future tail of the rolling window stays unselectable, and each carries its
        // own YEAR because the window reaches back into the previous one.
        val monthHitAreas = expenseData.mapIndexedNotNull { index, expense ->
            if (expense == null) null else {
                val ym = window[index]
                val rx = (monthX(index) - monthSlotWidth / 2).toInt()
                """<rect x="$rx" y="$paddingTop" width="${monthSlotWidth.toInt()}" height="${chartHeight + paddingBottom}" fill="transparent" class="expense-month-hit" style="cursor: pointer;" data-focal-year="${ym.year}" data-focal-month="${ym.month}" />"""
            }
        }.joinToString("")

        // Chart title depends on display mode
        val chartTitle = if (isCurrentYear) StringKey.CHART_EXPENSE_TIMELINE_ROLLING.tr() else StringKey.CHART_EXPENSE_TIMELINE_YEAR.tr(selectedYear.toString())

        val dollarSign = "$"
        return """
            <div style="width: 100%; height: 100%; position: relative; display: flex; flex-direction: column; box-sizing: border-box;">
                <div style="text-align: center; margin-bottom: 8px; flex: 0 0 auto;">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$chartTitle</span>
                </div>
                <!-- Instant tooltip container -->
                <div id="expenseChartTooltip" style="
                    position: absolute;
                    display: none;
                    background: ${if (t.isDark) "rgba(20,20,20,0.95)" else t.card};
                    border: 1px solid ${t.expenseRed};
                    border-radius: 6px;
                    padding: 6px 10px;
                    color: ${t.textPrimary};
                    font-size: 12px;
                    font-weight: 500;
                    pointer-events: none;
                    z-index: 100;
                    white-space: nowrap;
                    box-shadow: ${if (t.isDark) "0 2px 8px rgba(0,0,0,0.3)" else "0 2px 8px rgba(0,0,0,0.12)"};
                "></div>
                <svg width="100%" height="100%" viewBox="0 0 $width $height" preserveAspectRatio="xMidYMax meet" style="flex: 1; min-height: 0; display: block;" id="expenseChartSvg">
                    <defs>
                        <linearGradient id="expenseGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" style="stop-color:${t.expenseRed};stop-opacity:0.3" />
                            <stop offset="100%" style="stop-color:${t.expenseRed};stop-opacity:0.05" />
                        </linearGradient>
                    </defs>
                    <!-- Month click targets (under the plot, so the dots keep the hover) -->
                    $monthHitAreas
                    ${if (fillPoints.isNotEmpty()) """<path d="$fillPoints" fill="url(#expenseGradient)" style="pointer-events: none;" />""" else ""}
                    ${if (points.isNotEmpty()) """<polyline points="$points" fill="none" stroke="${t.expenseRed}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="pointer-events: none;" />""" else ""}
                    ${expenseData.mapIndexedNotNull { index, expense ->
                        if (expense != null) {
                            val x = monthX(index)
                            val y = paddingTop + chartHeight - (expense / maxExpense * chartHeight)
                            val ym = window[index]
                            val monthLabel = ym.toFullLabel()
                            val expenseFormatted = expense.toInt()
                            // The focal month's dot is drawn larger, matching the bold X-axis label.
                            val sel = ym.year == selLabelYear && ym.month == selLabelMonth
                            """<circle cx="$x" cy="${y.toInt()}" r="20" fill="transparent" class="expense-data-point" style="cursor: pointer;" data-tooltip="$monthLabel: $dollarSign$expenseFormatted" data-focal-year="${ym.year}" data-focal-month="${ym.month}" /><circle cx="$x" cy="${y.toInt()}" r="${if (sel) 7 else 4}" fill="${t.expenseRed}" stroke="${t.background}" stroke-width="${if (sel) 2 else 1}" style="pointer-events: none;" />"""
                        } else null
                    }.joinToString("")}
                    $monthLabels
                </svg>
            </div>
        """
    }

    /** Check if context-filtered data has expenses but no income (expenses-only mode). */
    private fun isContextExpensesOnly(): Boolean {
        if (selectedContextIds.isEmpty()) return false
        val (year, month) = effectiveDisplayYearMonth()
        val data = (yearlyData[year] ?: return false)[month] ?: return false
        return data.confirmedIncome == 0.0 && data.realizedExpenses > 0.0
    }

    /** Get total expenses for focal display in expenses-only mode. */
    private fun getFocalExpensesTotal(): Double {
        val (year, month) = effectiveDisplayYearMonth()
        return (yearlyData[year] ?: return 0.0)[month]?.realizedExpenses ?: 0.0
    }

    private fun renderFocalSavingsDisplay(): String {
        // Shared container styles: fill the widget and establish a CSS size-query
        // container so inner font sizes can scale with min(cqw, cqh) to preserve
        // aspect ratio as the widget is resized.
        val containerStyle = """
            text-align: center;
            height: 100%;
            width: 100%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 8px;
            container-type: size;
        """

        // Check for expenses-only mode when contexts are filtered
        if (isContextExpensesOnly()) {
            val total = getFocalExpensesTotal()
            val formatted = "$${total.roundToInt().asDynamic().toLocaleString()}"
            return """
                <div id="focalSavingsDisplay" data-guide="savings-rate-card" style="$containerStyle">
                    <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-bottom: 4px;">${StringKey.CONTEXT_EXPENSES_ONLY.tr()}</div>
                    <div style="color: ${t.expenseRed}; font-size: clamp(28px, min(22cqw, 42cqh), 200px); font-weight: 700; line-height: 1.05; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">$formatted</div>
                </div>
            """
        }

        val percentage = getFocalSavingsPercentage()
        val monthName = getFocalMonthName()
        val isYearly = isYearlyAverage()

        val gradientStyle = savingsGradientStyle(percentage)

        val labelText = when {
            isYearly -> StringKey.DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR.tr(selectedYear.toString())
            monthName != null -> StringKey.DASHBOARD_SAVINGS_FOR_MONTH.tr(monthName)
            else -> StringKey.DASHBOARD_SAVINGS_CURRENT_MONTH.tr()
        }

        return """
            <div id="focalSavingsDisplay" data-guide="savings-rate-card" style="$containerStyle">
                <div id="focalGradientContainer" style="display: inline-flex; align-items: baseline; justify-content: center; gap: 0.05em; line-height: 1; $gradientStyle">
                    <span id="focalPercentage" style="
                        font-size: clamp(40px, min(34cqw, 60cqh), 320px);
                        font-weight: 700;
                        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    ">0</span>
                    <span style="
                        font-size: clamp(22px, min(19cqw, 34cqh), 180px);
                        font-weight: 600;
                    ">%</span>
                </div>
                <div id="focalProgressBar" style="
                    width: clamp(80px, 50cqw, 520px);
                    height: clamp(3px, 0.8cqh, 8px);
                    margin: clamp(6px, 2cqh, 18px) auto 0;
                    background: ${t.border}; border-radius: 2px; overflow: hidden;
                ">
                    <div id="focalProgressFill" style="
                        width: 0%; height: 100%;
                        background: ${t.border};
                        transition: width 0.05s linear;
                    "></div>
                </div>
                <div style="display:flex; align-items:center; justify-content:center; gap:6px; margin-top: clamp(4px, 1.5cqh, 14px);">
                    <div id="focalLabel" style="
                        font-size: ${DashType.LABEL};
                        color: ${t.textSecondary};
                    ">$labelText</div>
                    <span title="${StringKey.DASHBOARD_SAVINGS_SMOOTHED_INFO.tr()}" style="display:inline-flex; align-items:center; justify-content:center; width:15px; height:15px; flex-shrink:0; border-radius:50%; border:1px solid #9999A6; color:#9999A6; font-size:10px; font-family:Georgia,'Times New Roman',serif; font-style:italic; font-weight:600; cursor:help; user-select:none;">i</span>
                </div>
            </div>
        """
    }

    // renderRightPanel removed - replaced by GridStack widget system

    /**
     * Gradient savings-rate color: yellow → cyan growing from right → radiant cyan.
     * Returns a CSS color string. For SVG text (which can't use background-clip),
     * we return a solid interpolated color based on where the gradient midpoint sits.
     */
    private fun savingsGradientColor(pct: Double): String {
        val target = savingsTarget
        val yR = 136; val yG = 136; val yB = 136     // #888888 grey
        // Target colour: bright cyan on dark, the logo's navy (#24689C) on light.
        val cR: Int; val cG: Int; val cB: Int
        if (t.isDark) { cR = 0; cG = 217; cB = 255 } else { cR = 36; cG = 104; cB = 156 }
        // cyanFraction: 0 at -5%, 1 at target%
        val cyanFraction = ((pct + 5.0) / (target + 5.0)).coerceIn(0.0, 1.0)
        val (r, g, b) = when {
            pct <= -5.0 -> Triple(yR, yG, yB)
            pct < target -> {
                // Weighted blend: more cyan as fraction grows
                val t = cyanFraction
                Triple(
                    (yR + (cR - yR) * t).toInt(),
                    (yG + (cG - yG) * t).toInt(),
                    (yB + (cB - yB) * t).toInt()
                )
            }
            else -> {
                // target → target*2+: cyan gets brighter (mix toward white)
                val t = ((pct - target) / target).coerceIn(0.0, 1.0) * 0.4
                Triple(
                    (cR + (255 - cR) * t).toInt(),
                    (cG + (255 - cG) * t).toInt(),
                    (cB + (255 - cB) * t).toInt()
                )
            }
        }
        return "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}"
    }

    /**
     * CSS style for savings gradient text (HTML elements that support background-clip).
     * <= 0%: solid yellow. 0-20%: narrow gradient band sweeps right-to-left separating
     * yellow (left) from cyan (right). >= 20%: full radiant cyan with glow.
     */
    private fun savingsGradientStyle(pct: Double): String {
        val target = savingsTarget
        // The savings number's brand colour: bright cyan on the dark theme, but the
        // logo's navy (#24689C) on light — the cyan washes out on the beige.
        val light = !t.isDark
        val brand = if (light) "#24689C" else "#00D9FF"
        if (pct <= 0.0) {
            return "color: ${t.textSecondary};"
        }
        if (pct >= target) {
            if (light) return "color: $brand;" // no glow on light — it only muddies the beige
            val glowIntensity = ((pct - target) / target).coerceIn(0.0, 1.0)
            val g0 = glowIntensity * 0.4
            val r = (0 + (255 - 0) * g0).toInt()
            val g = (217 + (255 - 217) * g0).toInt()
            val b = 255
            val hex = "#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}"
            val glowPx = (glowIntensity * 4 + 1).toInt()
            return "color: $hex; text-shadow: 0 0 ${glowPx}px $hex;"
        }
        // 0 → target: narrow band sweeps left-to-right (brand on left, grey on right)
        val progress = (pct / target).coerceIn(0.0, 1.0)
        val bandWidth = 12
        val bandCenter = (progress * 100).toInt() // 0 at 0%, 100 at 20%
        val bandStart = (bandCenter - bandWidth / 2).coerceIn(0, 100)
        val bandEnd = (bandCenter + bandWidth / 2).coerceIn(0, 100)
        return "background: linear-gradient(to right, $brand ${bandStart}%, ${t.textSecondary} ${bandEnd}%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;"
    }

    // Monthly Overview's per-widget layout system. The decision table
    // is the value of this widget: SCALE / DROP / REORGANIZE pulled out
    // of the user's screenshots and turned into one place to edit.
    //
    // Pattern is meant to be re-used: each adaptive widget defines its
    // own LayoutMode (data class with the levers it cares about) and
    // its own decision function `(cols, rows, …): LayoutMode`. The
    // shared scaffolding is colBucket / rowBucket / SizeBucket.

    private enum class TripleBarMonths { ALL_12, PAST_AND_CURRENT }
    private enum class TripleBarLegend { BELOW, RIGHT, HIDDEN }
    private data class TripleBarLayout(
        val months: TripleBarMonths,
        val legend: TripleBarLegend,
        val showMonthLabels: Boolean,
        val showSavingsPct: Boolean,
    )

    /** Decision table for Monthly Overview.
     *
     *  Reads directly off the table the user sketched:
     *
     *    cols ≥ 9 (wide)   tall: SCALE — all 12, legend BELOW, full labels
     *                      short: REORGANIZE — 12 stays, legend RIGHT
     *
     *    cols 5-8 (med)    tall: DROP — past months, legend BELOW
     *                      short: DROP + REORGANIZE — past months, legend RIGHT
     *
     *    cols < 5 (narrow) tall: DROP — past months, legend BELOW
     *                      short: HIDDEN labels, tooltip-only (last resort)
     *
     *  Past years always render all 12 (every month has real data, no
     *  point dropping them).
     */
    private fun tripleBarLayoutFor(cols: Int, rows: Int, isViewingCurrentYear: Boolean): TripleBarLayout {
        val c = colBucket(cols)
        val r = rowBucket(rows)
        val months = when {
            !isViewingCurrentYear -> TripleBarMonths.ALL_12
            // Lowered from c == LARGE (cols >= 9). Even at 6-7 cols the
            // widget has plenty of horizontal room for 12 bars, and the
            // dynamic chartWidth below scales the canvas to whatever
            // the widget's aspect actually is, so the bars fill the
            // available width. Only truly narrow widgets (cols < 6)
            // benefit from dropping future months.
            cols >= 6 -> TripleBarMonths.ALL_12
            else -> TripleBarMonths.PAST_AND_CURRENT
        }
        val legend = when {
            // Very compact in both dimensions: nothing fits well — drop
            // the legend; the bars' colour coding is recoverable from
            // the tooltip on hover/tap.
            c == SizeBucket.COMPACT && r == SizeBucket.COMPACT -> TripleBarLegend.HIDDEN
            // Short widgets (any reasonable width): legend moves to the
            // right column — fills the area future-month placeholders
            // would otherwise waste.
            r == SizeBucket.COMPACT -> TripleBarLegend.RIGHT
            // Default: legend under the chart (existing behaviour).
            else -> TripleBarLegend.BELOW
        }
        val showMonthLabels = !(c == SizeBucket.COMPACT && r == SizeBucket.COMPACT)
        val showSavingsPct = !(c == SizeBucket.COMPACT && r == SizeBucket.COMPACT)
        return TripleBarLayout(months, legend, showMonthLabels, showSavingsPct)
    }

    /**
     * @param widgetWidthCols current Gridstack column span (1–12) of the
     *   widget this chart is drawn in.
     * @param widgetHeightRows current Gridstack row span (1+). Together
     *   they drive [tripleBarLayoutFor] which picks the rendering mode.
     */
    private fun renderTripleBarChart(year: Int, isComparison: Boolean = false, widgetWidthCols: Int = 12, widgetHeightRows: Int = 5): String {
        val barWidth = 8
        val barGap = 2
        val groupWidth = (barWidth * 3) + (barGap * 2)
        val groupGap = 12
        val chartHeight = 120
        val padding = 20
        val dollarSign = "$"
        val chartId = if (isComparison) "tripleBarChartPrev" else "tripleBarChart"
        val tooltipId = if (isComparison) "tripleBarTooltipPrev" else "tripleBarTooltip"
        val dataPointClass = if (isComparison) "triple-bar-group-prev" else "triple-bar-group"
        // The month currently driving every widget — its X-axis label is drawn
        // bold + bright so it's obvious which month is selected. Only on the
        // live chart (not the prev-year comparison) and only for its own year.
        val (selYear, selMonth) = effectiveDisplayYearMonth()
        val emphasizedMonth = if (!isComparison && year == selYear) selMonth else -1

        // Get the correct year's data
        val yearData = yearlyData[year] ?: emptyMap()

        // Adaptive layout — see tripleBarLayoutFor's docstring for the
        // SCALE / DROP / REORGANIZE / HIDDEN decision table.
        val currentYear = Date().getFullYear().toInt()
        val currentMonth = Date().getMonth().toInt() + 1 // Date.getMonth is 0-indexed
        val isViewingCurrentYear = (year == currentYear)
        val layout = tripleBarLayoutFor(widgetWidthCols, widgetHeightRows, isViewingCurrentYear)
        val monthsToDisplay = when (layout.months) {
            TripleBarMonths.PAST_AND_CURRENT -> (1..currentMonth).toList()
            TripleBarMonths.ALL_12 -> (1..12).toList()
        }

        // chartWidth is DYNAMIC and matches the widget's actual pixel
        // aspect ratio. Background: with the old fixed 12-slot canvas
        // (chartWidth ≈ 468, height ≈ 170 → aspect ≈ 2.75:1), a wide+
        // short widget (e.g. 12×2 cells ≈ aspect 8:1) would have its
        // SVG scaled DOWN by preserveAspectRatio="xMidYMid meet" to
        // fit the limiting dimension (height) — leaving large empty
        // bands on the left and right of the bars.
        //
        // Solution: compute chartWidth so the SVG viewBox aspect ratio
        // matches the widget's grid-cell aspect ratio. preserveAspect
        // Ratio then has nothing to "fix" — the SVG renders edge-to-
        // edge horizontally regardless of monthsToDisplay.size.
        // Grid cells are ~80px wide × 60px tall (GridStack.init).
        val pxPerCol = 80.0
        val pxPerRow = 60.0
        val widgetPxAspect = (widgetWidthCols * pxPerCol) /
            (widgetHeightRows.coerceAtLeast(1) * pxPerRow)
        val chartHeightWithLabels = (chartHeight + 50).toDouble()
        val targetChartWidth = (chartHeightWithLabels * widgetPxAspect).toInt()
        // Floor: each visible month needs at least (groupWidth + small
        // gap) of canvas width or the bars cram into each other.
        val minChartWidth = (groupWidth + 8) * monthsToDisplay.size - 8
        val chartWidth = targetChartWidth.coerceAtLeast(minChartWidth)
        val monthSlotWidth = chartWidth / monthsToDisplay.size

        // Find max value for scaling (use both years for consistent scale)
        // Include exceeded amounts in max calculation to ensure bars don't overflow
        val selectedYearData = yearlyData[selectedYear] ?: emptyMap()
        val prevYearData = yearlyData[selectedYear - 1] ?: emptyMap()
        fun maxBarValue(data: MonthlyFinanceData): Double = maxOf(
            data.expectedIncome + maxOf(0.0, data.confirmedIncome - data.expectedIncome),  // Income + exceeded
            data.plannedExpenses + maxOf(0.0, data.realizedExpenses - data.plannedExpenses),  // Expense + exceeded
            data.targetSavings + maxOf(0.0, data.goalAllocations - data.targetSavings)  // Savings + exceeded
        )
        val maxValue = maxOf(
            selectedYearData.values.maxOfOrNull { maxBarValue(it) } ?: 5000.0,
            prevYearData.values.maxOfOrNull { maxBarValue(it) } ?: 5000.0
        )

        // Theme-adaptive bar colors: dark = medium fill + bright excess; light = medium fill + dark excess
        val incomeWithin   = if (t.isDark) "#2EAD2E" else "#66BB6A"
        val incomeExcess   = if (t.isDark) "#66FF66" else "#2E7D32"
        val expenseWithin  = if (t.isDark) "#B33030" else "#E57373"
        val expenseExcess  = if (t.isDark) "#FF4444" else "#B71C1C"
        val savingsWithin  = if (t.isDark) "#1A9DB5" else "#4DD0E1"
        val savingsExcess  = if (t.isDark) "#66EEFF" else "#006064"

        // Render only the months we want to display, spaced evenly across
        // the constant-width chart canvas. groupCenterOffset puts each
        // group's bars centred within its slot so the visual cadence
        // looks balanced regardless of how many months are shown.
        val groupCenterOffset = (monthSlotWidth - groupWidth) / 2
        val bars = monthsToDisplay.mapIndexed { displayIdx, month ->
            val data = yearData[month] ?: MonthlyFinanceData(month)
            val x = displayIdx * monthSlotWidth + groupCenterOffset
            val shouldShowBars = true // we only render months we want shown

            // Calculate bar heights (scale to chartHeight). Force min 1px so tiny non-zero
            // values stay visible instead of disappearing to 0 via integer truncation.
            fun scaleHeight(value: Double): Int {
                if (value <= 0.0) return 0
                return maxOf(1, (value / maxValue * chartHeight).toInt())
            }

            val incomeExpectedHeight = scaleHeight(data.expectedIncome)
            val incomeRealizedUpToPlanned = scaleHeight(minOf(data.confirmedIncome, data.expectedIncome))
            val incomeExceeded = maxOf(0.0, data.confirmedIncome - data.expectedIncome)
            val incomeExceededHeight = scaleHeight(incomeExceeded)

            val expensePlannedHeight = scaleHeight(data.plannedExpenses)
            val expenseRealizedUpToPlanned = scaleHeight(minOf(data.realizedExpenses, data.plannedExpenses))
            val expenseExceeded = maxOf(0.0, data.realizedExpenses - data.plannedExpenses)
            val expenseExceededHeight = scaleHeight(expenseExceeded)

            val savingsTargetHeight = scaleHeight(maxOf(0.0, data.targetSavings))
            val savingsActualUpToTarget = scaleHeight(minOf(maxOf(0.0, data.goalAllocations), maxOf(0.0, data.targetSavings)))
            val savingsExceeded = maxOf(0.0, data.goalAllocations - data.targetSavings)
            val savingsExceededHeight = scaleHeight(savingsExceeded)

            val savingsPct = data.savingsPercentage
            val savingsColor = savingsGradientColor(savingsPct)

            val tooltipData = "${monthNames[month - 1]}|$dollarSign${data.confirmedIncome.toInt()}|$dollarSign${data.realizedExpenses.toInt()}|$dollarSign${data.goalAllocations.toInt()} (${analytics.formatSavingsPercentValue(savingsPct)}%)"

            // Only render bars for months that should show data
            // Budget on: faint background bar at budget height + normal fill + bright excess
            // Budget off: just solid bar at actual height
            val actualIncomeHeight = (data.confirmedIncome / maxValue * chartHeight).toInt()
            val actualExpenseHeight = (data.realizedExpenses / maxValue * chartHeight).toInt()
            val actualSavingsHeight = (maxOf(0.0, data.goalAllocations) / maxValue * chartHeight).toInt()

            val barsContent = if (shouldShowBars) """
                    <!-- Income Bar (Green): Faint budget background + bright fill + dark excess -->
                    ${if (showIncome) {
                        if (showBudget && incomeExpectedHeight > 0) {
                            // With budget: faint background at budget height, medium fill, theme-adaptive excess
                            """<rect x="0" y="${chartHeight - incomeExpectedHeight}" width="$barWidth" height="$incomeExpectedHeight"
                                fill="rgba(50,205,50,0.15)" />
                            <rect x="0" y="${chartHeight - incomeRealizedUpToPlanned}" width="$barWidth" height="$incomeRealizedUpToPlanned"
                                fill="$incomeWithin" />
                            ${if (incomeExceededHeight > 0) """<rect x="0" y="${chartHeight - incomeExpectedHeight - incomeExceededHeight}" width="$barWidth" height="$incomeExceededHeight"
                                fill="$incomeExcess" />""" else ""}"""
                        } else {
                            // Without budget: just show actual income as solid bar
                            """<rect x="0" y="${chartHeight - actualIncomeHeight}" width="$barWidth" height="$actualIncomeHeight"
                                fill="${t.incomeGreen}" />"""
                        }
                    } else ""}

                    <!-- Expense Bar (Red): Faint budget background + bright fill + dark excess -->
                    ${if (showExpenses) {
                        if (showBudget && expensePlannedHeight > 0) {
                            // With budget: faint background at budget height, medium fill, theme-adaptive excess
                            """<rect x="${barWidth + barGap}" y="${chartHeight - expensePlannedHeight}" width="$barWidth" height="$expensePlannedHeight"
                                fill="rgba(255,107,107,0.15)" />
                            <rect x="${barWidth + barGap}" y="${chartHeight - expenseRealizedUpToPlanned}" width="$barWidth" height="$expenseRealizedUpToPlanned"
                                fill="$expenseWithin" />
                            ${if (expenseExceededHeight > 0) """<rect x="${barWidth + barGap}" y="${chartHeight - expensePlannedHeight - expenseExceededHeight}" width="$barWidth" height="$expenseExceededHeight"
                                fill="$expenseExcess" />""" else ""}"""
                        } else {
                            // Without budget: just show actual expenses as solid bar
                            """<rect x="${barWidth + barGap}" y="${chartHeight - actualExpenseHeight}" width="$barWidth" height="$actualExpenseHeight"
                                fill="${t.expenseRed}" />"""
                        }
                    } else ""}

                    <!-- Savings Bar (Blue): Faint budget background + bright fill + dark excess -->
                    ${if (showSavings) {
                        if (showBudget && savingsTargetHeight > 0) {
                            // With budget: faint background at target height, medium fill, theme-adaptive excess
                            """<rect x="${(barWidth + barGap) * 2}" y="${chartHeight - savingsTargetHeight}" width="$barWidth" height="$savingsTargetHeight"
                                fill="rgba(0,217,255,0.15)" />
                            <rect x="${(barWidth + barGap) * 2}" y="${chartHeight - savingsActualUpToTarget}" width="$barWidth" height="$savingsActualUpToTarget"
                                fill="$savingsWithin" />
                            ${if (savingsExceededHeight > 0) """<rect x="${(barWidth + barGap) * 2}" y="${chartHeight - savingsTargetHeight - savingsExceededHeight}" width="$barWidth" height="$savingsExceededHeight"
                                fill="$savingsExcess" />""" else ""}"""
                        } else {
                            // Without budget: just show actual savings as solid bar
                            """<rect x="${(barWidth + barGap) * 2}" y="${chartHeight - actualSavingsHeight}" width="$barWidth" height="$actualSavingsHeight"
                                fill="${t.accent}" />"""
                        }
                    } else ""}

                    <!-- Transparent hover/click area — the WHOLE month column, bars plus the
                         savings-% and month labels underneath (full viewBox height), so clicking
                         the X-axis label selects the month exactly like clicking its bars. Carries
                         its own YEAR because the comparison chart renders selectedYear - 1. -->
                    <rect class="$dataPointClass" x="${-groupCenterOffset}" y="0" width="$monthSlotWidth" height="${chartHeight + 50}"
                        fill="transparent" style="cursor: pointer;" data-tooltip="$tooltipData" data-month="$month" data-focal-year="$year" />
            """ else ""

            // Savings % label: show value if has data, "-" otherwise
            val savingsGlow = if (savingsPct >= savingsTarget) {
                val glowIntensity = ((savingsPct - savingsTarget) / savingsTarget).coerceIn(0.0, 1.0) * 4 + 1
                "filter: drop-shadow(0 0 ${glowIntensity.toInt()}px $savingsColor);"
            } else ""
            // The label text sits ON TOP of the column's click target, so it has to be
            // transparent to the pointer or it would swallow clicks aimed at the month.
            val labelPassThrough = "pointer-events: none;"
            // Savings% + month labels are conditional on the layout —
            // see tripleBarLayoutFor. Hidden in the most-compact mode
            // where there's no room for either; the user can still
            // hover/tap to see the same data in the tooltip.
            val savingsLabel = if (!layout.showSavingsPct) "" else if (shouldShowBars && data.confirmedIncome > 0) {
                """<text x="${groupWidth / 2}" y="${chartHeight + 25}" fill="$savingsColor" font-size="10" text-anchor="middle" font-weight="500" style="$labelPassThrough $savingsGlow">${analytics.formatSavingsPercentValue(savingsPct)}%</text>"""
            } else {
                """<text x="${groupWidth / 2}" y="${chartHeight + 25}" fill="${t.border}" font-size="10" text-anchor="middle" style="$labelPassThrough">-</text>"""
            }
            val monthLabel = if (layout.showMonthLabels) {
                val selected = month == emphasizedMonth
                val labelFill = if (selected) t.textPrimary else t.textSecondary
                val labelWeight = if (selected) "700" else "400"
                """<text x="${groupWidth / 2}" y="${chartHeight + 40}" fill="$labelFill" font-size="9" text-anchor="middle" font-weight="$labelWeight" style="$labelPassThrough">${monthNames[month - 1]}</text>"""
            } else ""

            """
                <g transform="translate($x, 0)">
                    $barsContent
                    $savingsLabel
                    $monthLabel
                </g>
            """
        }.joinToString("")

        // SVG block — wrapped so the OUTER container can be a flex item
        // that takes flex:1, while the SVG itself fills 100% of that
        // item via width/height percentages. The flex/sizing wrapping
        // is applied at the layout-mode return below; this block is
        // just the chart-with-tooltip content.
        val svgBlock = """
            <div style="position: relative; width: 100%; height: 100%; min-width: 0; min-height: 0;">
                <!-- Instant tooltip container -->
                <div id="$tooltipId" style="
                    position: absolute;
                    display: none;
                    background: ${if (t.isDark) "rgba(20,20,20,0.95)" else t.card};
                    border: 1px solid ${t.border};
                    border-radius: 8px;
                    padding: 10px 14px;
                    color: ${t.textPrimary};
                    font-size: 11px;
                    pointer-events: none;
                    z-index: 100;
                    white-space: nowrap;
                    box-shadow: ${if (t.isDark) "0 4px 12px rgba(0,0,0,0.4)" else "0 4px 12px rgba(0,0,0,0.12)"};
                "></div>
                <svg id="$chartId" width="100%" height="100%" viewBox="0 0 ${chartWidth + padding * 2} ${chartHeight + 50}" preserveAspectRatio="xMidYMid meet" style="display: block;">
                    <g transform="translate($padding, 0)">
                        $bars
                    </g>
                </svg>
            </div>
        """

        // Legend items — same content, different wrapper depending on
        // layout.legend. BELOW = horizontal row, wraps. RIGHT = vertical
        // stack. HIDDEN = not rendered at all.
        val legendItems = """
            <span id="legend-income" style="display: flex; align-items: center; gap: 4px; color: ${if (showIncome) t.textSecondary else t.border}; font-size: 10px; cursor: pointer; ${if (!showIncome) "text-decoration: line-through;" else ""}">
                <span style="width: 10px; height: 10px; background: ${if (showIncome) incomeWithin else t.border}; border-radius: 2px;"></span>
                ${StringKey.CHART_INCOME.tr()}
            </span>
            <span id="legend-expenses" style="display: flex; align-items: center; gap: 4px; color: ${if (showExpenses) t.textSecondary else t.border}; font-size: 10px; cursor: pointer; ${if (!showExpenses) "text-decoration: line-through;" else ""}">
                <span style="width: 10px; height: 10px; background: ${if (showExpenses) expenseWithin else t.border}; border-radius: 2px;"></span>
                ${StringKey.CHART_EXPENSES.tr()}
            </span>
            <span id="legend-savings" style="display: flex; align-items: center; gap: 4px; color: ${if (showSavings) t.textSecondary else t.border}; font-size: 10px; cursor: pointer; ${if (!showSavings) "text-decoration: line-through;" else ""}">
                <span style="width: 10px; height: 10px; background: ${if (showSavings) savingsWithin else t.border}; border-radius: 2px;"></span>
                ${StringKey.DASHBOARD_SAVINGS.tr()}
            </span>
            <span id="legend-budget" style="display: flex; align-items: center; gap: 4px; color: ${if (showBudget) t.textSecondary else t.border}; font-size: 10px; cursor: pointer; ${if (!showBudget) "text-decoration: line-through;" else ""}">
                <span style="width: 10px; height: 10px; background: rgba(50,205,50,0.15); border: 0.5px solid rgba(136,136,136,0.4); border-radius: 2px; box-sizing: border-box;"></span>
                ${StringKey.CHART_BUDGET.tr()}
            </span>
        """

        // The outer container is already a flex column (set up in
        // renderTripleBarWidgetContent). Here we further partition that
        // column based on legend position:
        //   HIDDEN — SVG takes the whole slot (flex:1)
        //   BELOW  — SVG (flex:1) over legend (flex:0 0 auto)
        //   RIGHT  — flex row: SVG (flex:1) and legend (flex:0 0 auto)
        //            Outer needs flex-direction: row override.
        return when (layout.legend) {
            TripleBarLegend.HIDDEN -> """
                <div style="flex: 1 1 0; min-height: 0;">
                    $svgBlock
                </div>
            """
            TripleBarLegend.BELOW -> """
                <div style="flex: 1 1 0; min-height: 0;">
                    $svgBlock
                </div>
                <div style="flex: 0 0 auto; display: flex; justify-content: center; gap: 12px; margin-top: 6px; flex-wrap: wrap;">
                    $legendItems
                </div>
            """
            TripleBarLegend.RIGHT -> """
                <div style="flex: 1 1 0; min-height: 0; display: flex; flex-direction: row; gap: 12px;">
                    <div style="flex: 1 1 0; min-width: 0; display: flex; flex-direction: column;">
                        $svgBlock
                    </div>
                    <div style="flex: 0 0 auto; display: flex; flex-direction: column; gap: 8px; justify-content: center; min-width: 88px; padding-right: 4px;">
                        $legendItems
                    </div>
                </div>
            """
        }
    }

    /** Pixel size of the widget's content box, height then width.
     *
     *  The fixed (non-Pro) dashboard renders this panel into an unbounded card,
     *  so there is no box to measure: the fallback is a 5-row tile at the default
     *  five-column width, which is what that layout has always drawn. */
    private fun recommendationContentBox(): Pair<Int, Int> {
        val el = container?.querySelector("#widget-content-recommendation") as? HTMLElement
        val h = el?.clientHeight ?: 0
        val w = el?.clientWidth ?: 0
        return Pair(
            if (h > 0) h else RecWidget.contentHeightForRows(5),
            if (w > 0) w else 520,
        )
    }

    /**
     * One goal's slice of this month's room to save. Structured rather than
     * pre-baked HTML so the same allocation can render as a one-line chip
     * (SMALL) or a full row with a progress bar (MEDIUM/LARGE).
     */
    private data class GoalAllocation(
        val goal: SavingsGoal,
        val amount: Double,
        val daysRemaining: Int,
        val monthsRemaining: Int,
    )

    private fun renderRecommendationPanel(): String {
        // The card is scoped to the dashboard's DISPLAY month, like every other
        // month-scoped widget: the last complete month by default, or whichever month
        // the user stepped to. It used to read Date() directly, which is how "$8
        // available to save this month" came to sit next to a dashboard showing July —
        // two days of August reported as if it were a month.
        val (displayYear, displayMonth) = effectiveDisplayYearMonth()
        // Named in the copy rather than left as "this month", because the month on
        // screen is usually not the one the calendar is on.
        val monthLabel = utils.MonthNames.inSentence(displayMonth)
        val displayMonthData = yearlyData[displayYear]?.get(displayMonth)
        val monthlyAvailableSavings = displayMonthData?.actualSavings ?: 0.0
        // Four signals drive what message to show when there's no room to save:
        //   no data, month running     → "month just started, no transactions yet"
        //   no data, month over        → "nothing landed in <month>"
        //   hasMonthData && !hasIncome → "expenses landed but income hasn't arrived yet"
        //                                (autopayments charged on the 1st, payday is later)
        //   hasMonthData && hasIncome  → genuine overspending → spend-less nudge
        // Without the income-arrived gate, the user sees the spend-less nudge
        // when they actually just haven't been paid yet, which is wrong.
        val hasMonthData = displayMonthData?.hasData == true
        val hasIncomeYet = (displayMonthData?.confirmedIncome ?: 0.0) > 0
        val monthInProgress = isInProgressMonth(displayYear, displayMonth)

        // Get user's goals sorted by priority
        val goals = goalsViewModel.goals
            .filter { it.isActive }
            .sortedWith(compareBy(
                { when (it.priority) {
                    GoalPriority.MUST_HAVE -> 0
                    GoalPriority.NICE_TO_HAVE -> 1
                    GoalPriority.STRETCH_GOAL -> 2
                    GoalPriority.MAYBE -> 3
                    else -> 4
                }},
                { it.getDaysRemaining() }
            ))

        // Greedy allocation split — computed up front so the shared state machine
        // can tell whether there's a breakdown to show. Same arithmetic as before,
        // kept structured so each tier can render it at a different density.
        val allocations: List<GoalAllocation> = if (monthlyAvailableSavings > 0 && goals.isNotEmpty()) {
            var remaining = monthlyAvailableSavings
            goals.take(5).mapNotNull { goal ->
                val daysRemaining = maxOf(1, goal.getDaysRemaining())
                // Shared definition, not days/30: the goals page and iOS divide by whole
                // calendar months, and a second formula here made the same goal need a
                // different amount depending on which screen you were looking at.
                val monthsRemaining = maxOf(1, analytics.goalMonthsRemaining(goal))
                val monthlyNeeded = analytics.computeGoalPace(goal).requiredMonthly
                val allocation = minOf(monthlyNeeded, remaining).coerceAtLeast(0.0)
                if (allocation > 0) {
                    remaining -= allocation
                    GoalAllocation(goal, allocation, daysRemaining, monthsRemaining)
                } else null
            }
        } else emptyList()

        // Single source of truth for WHICH card to show — every renderer routes
        // through this so the decision can't drift again (that split was the
        // "over budget when income just hasn't landed" bug).
        val state = computeSavingsRecommendationCardState(
            hasGoals = goals.isNotEmpty(),
            hasMonthData = hasMonthData,
            hasIncomeYet = hasIncomeYet,
            actualSavings = monthlyAvailableSavings,
            hasAllocations = allocations.isNotEmpty(),
            monthInProgress = monthInProgress,
        )

        val (contentH, contentW) = recommendationContentBox()

        // The biggest over-plan category — named by the over-budget primary at
        // every height, and therefore off limits to the insight pool.
        val overBudgetDriver = biggestOverBudgetCategory(displayYear, displayMonth)

        // Offered at every height, not gated on one: whether they fit is the
        // ladder's decision, and a wide short tile where every sentence is one
        // line has room for an insight that a narrow tall one does not. The pool
        // is told what the primary already names so it can refuse to restate it.
        val insights = DashboardInsights.build(
            buildRecommendationInsightInput(
                state = state,
                year = displayYear,
                month = displayMonth,
                goals = goals,
                allocations = allocations,
                namedCategories = setOfNotNull(overBudgetDriver?.first),
                monthLabel = monthLabel,
            )
        )

        return RecWidget.render(
            t = t,
            contentHeightPx = contentH,
            contentWidthPx = contentW,
            data = RecWidget.Data(
                state = state,
                monthLabel = monthLabel,
                available = monthlyAvailableSavings,
                allocations = allocations.map { it.toSlice() },
                month = displayMonthData?.let { RecWidget.MonthTotals(it.confirmedIncome, it.realizedExpenses) },
                overBudgetDriver = overBudgetDriver,
                insights = insights,
            ),
        )
    }

    /** Flatten an allocation into the pixel-budgeted markup layer's row model. */
    private fun GoalAllocation.toSlice(): RecWidget.GoalSlice = RecWidget.GoalSlice(
        goalId = goal.id,
        name = goal.name,
        iconEmoji = goal.iconEmoji,
        amount = amount,
        progressPercent = if (goal.targetAmount > 0.0) {
            ((goal.currentAmount / goal.targetAmount) * 100).coerceIn(0.0, 100.0).roundToInt()
        } else 0,
        timeLeft = if (daysRemaining < 30) {
            StringKey.GOALS_DAYS_LEFT.tr(daysRemaining.toString())
        } else {
            StringKey.DASHBOARD_MONTHS_LEFT.tr(monthsRemaining.toString())
        },
    )

    /**
     * Adapt the dashboard's existing signals into the shared insight pool's
     * input. Every field comes from a helper the KPI tiles already use — nothing
     * is recomputed here, and no ranking or de-duplication rule lives on this
     * side of the boundary.
     */
    private fun buildRecommendationInsightInput(
        state: SavingsRecommendationCardState,
        year: Int,
        month: Int,
        goals: List<SavingsGoal>,
        allocations: List<GoalAllocation>,
        namedCategories: Set<String>,
        monthLabel: String,
    ): DashboardInsights.Input {
        val monthData = yearlyData[year]?.get(month)
        val spending = getCategorySpendingForMonth(year, month)
        val largest = spending.maxByOrNull { it.value }
        // What each goal actually received this month, from the transactions the user
        // tagged. Account/context scoping matches computeMonthlyAggregates so the
        // per-goal split and the month's totals describe the same set of rows.
        val assignedByGoal = analytics.MonthlyTotalsAggregator.goalAllocationsByGoal(
            allTransactions
                .filter { it.accountId in selectedAccountIds && it.passesContextFilter() }
                .map { MonthlyTxn(it) },
            year, month,
        )

        return DashboardInsights.Input(
            primary = DashboardInsights.Primary(
                kind = DashboardInsights.primaryKindOf(state),
                namedCategories = namedCategories,
                // The expanded HasAllocations primary lists these goals with
                // amount and time-left already.
                namedGoalIds = allocations.map { it.goal.id }.toSet(),
            ),
            formatMoney = { RecWidget.dollars(it) },
            monthLabel = monthLabel,
            budget = monthData?.let {
                DashboardInsights.BudgetOverage(planned = it.plannedExpenses, realized = it.realizedExpenses)
            },
            movers = categoryMoversForMonth(year, month),
            goals = goals.map { goal ->
                DashboardInsights.GoalFunding(
                    goalId = goal.id,
                    name = goal.name,
                    // Same shared figure the goals page prints as "$X/mo", so the
                    // insight cannot ask for a different number than the card shows.
                    monthlyNeeded = analytics.computeGoalPace(goal).requiredMonthly.coerceAtLeast(0.0),
                    // What the goal actually received this month, NOT what the greedy
                    // split recommends giving it — that map is empty whenever the month
                    // has no surplus, which is exactly when this insight fires.
                    allocatedThisMonth = assignedByGoal[goal.id] ?: 0.0,
                    isBehindPlan = analytics.computeGoalPace(goal).status == analytics.GoalPaceStatus.BEHIND,
                )
            },
            yearOverYear = DashboardInsights.YearOverYear(
                thisYear = expensesForMonth(year, month),
                lastYear = expensesForMonth(year - 1, month),
            ),
            largestCategory = largest?.let {
                DashboardInsights.CategoryAmount(it.key, it.value, spending.values.sum())
            },
            savingsStreakMonths = savingsStreakMonths(),
            savingsTargetPercent = savingsTarget.roundToInt(),
        )
    }

    private fun setupEventListeners(container: HTMLElement) {
        // Demo mode: connect an account CTA
        container.querySelector("#demo-connect-btn")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.AccountLinking(daysRequested = 90))
        })

        // Logo click - navigate to Landing page
        container.querySelector("#dashboard-logo")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.Landing)
        })

        // Navigation listeners
        container.querySelector("#nav-activity")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
        })
        container.querySelector("#nav-dashboard")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
        })
        container.querySelector("#nav-goals")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.GOALS)
        })

        // Global month stepper — present in both the grid and fixed layouts, so
        // it's wired here in the common path rather than either layout branch.
        // Steps the focal month (rolling the year at Dec/Jan) and marks the
        // current month as in-progress.
        container.querySelector("#monthStepperPrev")?.addEventListener("click", { stepFocalMonth(-1) })
        container.querySelector("#monthStepperNext")?.addEventListener("click", { stepFocalMonth(1) })
        container.querySelector("#nav-budget")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
        })
        container.querySelector("#nav-guide")?.addEventListener("click", {
            showGuideModal()
        })
        container.querySelector("#nav-tour")?.addEventListener("click", {
            // Pick the tour variant matching the current layout so wording (and the
            // grid-only step) always apply to what the user is looking at.
            val screen = if (useGridLayout) guide.GuideScreen.DASHBOARD_GRID
                         else guide.GuideScreen.DASHBOARD_STANDARD
            // FORCE the Customize panel closed at tour start. The
            // customize-toolbar step auto-opens it (via the
            // 'wiz3r-demo-open-customize' event GuideOverlay
            // dispatches) so the camera catches the slide-open
            // animation in sync with the bubble. If the user had the
            // panel open from a previous session, we hide it now so
            // the demo's first beat actually animates something.
            // restorePanelOnClose ensures the panel closes again at
            // tour end (regardless of whether the user had it open
            // before — predictable post-tour state is better than
            // "preserve whatever was there").
            var restorePanelOnClose: (() -> Unit)? = null
            if (useGridLayout) {
                val panel = container.querySelector("#toolbar-panel") as? HTMLElement
                val chevron = container.querySelector("#toolbar-chevron") as? HTMLElement
                panel?.style?.display = "none"
                chevron?.style?.transform = ""
                restorePanelOnClose = {
                    panel?.style?.display = "none"
                    chevron?.style?.transform = ""
                }
                // The lock-btn anchor needs to exist before the lock
                // step lands. Pre-render the layouts list NOW even
                // though the panel is closed — the DOM nodes exist
                // inside display:none and setAttribute on hidden
                // elements works fine. When the panel opens during
                // the customize step, the data-guide attr is in place.
                renderLayoutList()
                (container.querySelector(".layout-lock-btn") as? HTMLElement)
                    ?.setAttribute("data-guide", "layout-lock")
            }
            GuideOverlay(screen, onClose = restorePanelOnClose).start()
            (container.querySelector("#nav-tour") as? HTMLElement)?.classList?.remove("wiz3r-guide-halo")
        })

        // Marketing-capture demo mode (?demo=1): auto-click the 'i' button
        // 3 s after the page renders so the tour starts hands-free. No-op
        // when demo mode isn't active.
        autoStartTourIfDemoMode("#nav-tour")

        // Language toggle - quick switch between languages
        container.querySelector("#nav-language")?.addEventListener("click", {
            LocaleManager.toggleLocale()
            window.location.reload()
        })

        // Mobile menu handlers
        val mobileMenuOverlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement
        val mobileMenuBackdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement
        fun openMobileMenu() {
            mobileMenuOverlay?.style?.display = "block"
            mobileMenuBackdrop?.style?.display = "block"
        }
        container.querySelector("#hamburgerMenu")?.addEventListener("click", { openMobileMenu() })
        MarketingMenu.attachMobileEventListeners(
            container = container,
            onNavigate = { page ->
                if (page == "guide") {
                    showGuideModal()
                    return@attachMobileEventListeners
                }
                val screen = when (page) {
                    "team" -> Screen.Team
                    "about" -> Screen.About
                    "contact" -> Screen.Contact
                    "subscribe" -> Screen.Subscribe()
                    "privacy" -> Screen.Privacy
                    "terms" -> Screen.Terms
                    "security" -> Screen.Security
                    else -> Screen.Landing
                }
                navigationManager.navigateTo(screen)
            },
            onAppNavigate = { tab ->
                when (tab) {
                    "transactions" -> navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                    "dashboard" -> navigationManager.switchToTab(ui.AppTabType.DASHBOARD)
                    "goals" -> navigationManager.switchToTab(ui.AppTabType.GOALS)
                    "budget" -> navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
                    "settings" -> navigationManager.switchToTab(ui.AppTabType.SETTINGS)
                    // An actual logout, not a walk to the marketing page. Going to
                    // Landing left the stored token untouched, so the visitor was
                    // never signed out: "Get started" found the session still
                    // there and signed the same account straight back in.
                    "logout" -> navigationManager.logout()
                }
            }
        )

        // Desktop hamburger menu — same as mobile, opens the overlay
        container.querySelector("#hamburgerMenuDesktop")?.addEventListener("click", { openMobileMenu() })

        if (useGridLayout) {
            // Grid layout: setup toolbar, palette, GridStack event wiring
            setupToolbarToggle()
        } else {
            // Fixed layout: year nav, chart type buttons, account selector, legends, tooltips, recommendations
            val currentYear = Date().getFullYear()
            val minYear = getMinYearFromData()
            val maxYear = currentYear

            container.querySelector("#yearPrev")?.addEventListener("click", {
                if (selectedYear > minYear) {
                    selectedYear -= 1
                    focusedMonth = null
                    loadBudgetPlansForYear(selectedYear)
                    loadBudgetPlansForYear(selectedYear - 1)
                    updateYearlyDataWithActualTransactions()
                    refreshDashboard()
                }
            })
            container.querySelector("#yearNext")?.addEventListener("click", {
                if (selectedYear < maxYear) {
                    selectedYear += 1
                    focusedMonth = null
                    loadBudgetPlansForYear(selectedYear)
                    loadBudgetPlansForYear(selectedYear - 1)
                    updateYearlyDataWithActualTransactions()
                    refreshDashboard()
                }
            })

            // Play the count-up only once per data load. The dashboard can be rebuilt
            // more than once for the same data (transactions land, then budget plans),
            // and each rebuild creates a fresh element — without this the counter visibly
            // counts up, resets to 0 and counts up again. A genuine data change bumps
            // transactionsVersion, which re-arms the animation.
            val shouldAnimateFocal = focalAnimatedForVersion != transactionsVersion
            focalAnimatedForVersion = transactionsVersion
            animateFocalSavingsDisplay(animate = shouldAnimateFocal)

            // Legend toggle buttons
            container.querySelector("#legend-income")?.addEventListener("click", {
                showIncome = !showIncome
                settingsManager.setChartSeriesVisibility("income", showIncome)
                refreshDashboard()
            })
            container.querySelector("#legend-expenses")?.addEventListener("click", {
                showExpenses = !showExpenses
                settingsManager.setChartSeriesVisibility("expenses", showExpenses)
                refreshDashboard()
            })
            container.querySelector("#legend-savings")?.addEventListener("click", {
                showSavings = !showSavings
                settingsManager.setChartSeriesVisibility("savings", showSavings)
                refreshDashboard()
            })
            container.querySelector("#legend-budget")?.addEventListener("click", {
                showBudget = !showBudget
                settingsManager.setChartSeriesVisibility("budget", showBudget)
                refreshDashboard()
            })
            container.querySelector("#legend-category-budget")?.addEventListener("click", {
                showCategoryBudget = !showCategoryBudget
                settingsManager.setChartSeriesVisibility("categoryBudget", showCategoryBudget)
                refreshDashboard()
            })

            // Chart type buttons
            ChartType.values().forEach { type ->
                container.querySelector("[data-chart-type='${type.name}']")?.addEventListener("click", {
                    selectedChartType = type
                    refreshDashboard()
                })
            }

            // Account selector buttons
            allAccounts.forEach { account ->
                container.querySelector("[data-account-id='${account.accountId}']")?.addEventListener("click", {
                    if (account.accountId in selectedAccountIds) {
                        if (selectedAccountIds.size > 1) {
                            selectedAccountIds.remove(account.accountId)
                        }
                    } else {
                        selectedAccountIds.add(account.accountId)
                    }
                    val deselected = allAccounts.map { it.accountId }.filter { it !in selectedAccountIds }
                    GlobalScope.launch {
                        try { ApiService().setSetting("deselected_account_ids", deselected.joinToString(",")) } catch (_: Exception) {}
                    }
                    updateYearlyDataWithActualTransactions()
                    refreshDashboard()
                })
            }

            // Context selector buttons (fixed layout)
            container.querySelector("[data-context-id='all']")?.addEventListener("click", {
                selectedContextIds.clear()
                updateYearlyDataWithActualTransactions()
                refreshDashboard()
            })
            allContexts.forEach { ctx ->
                container.querySelector("[data-context-id='${ctx.id}']")?.addEventListener("click", {
                    if (selectedContextIds.isEmpty()) {
                        selectedContextIds.add(ctx.id)
                    } else if (ctx.id in selectedContextIds) {
                        selectedContextIds.remove(ctx.id)
                    } else {
                        selectedContextIds.add(ctx.id)
                    }
                    updateYearlyDataWithActualTransactions()
                    refreshDashboard()
                })
            }

            setupChartTooltips(container)
            setupRecommendationEventListeners(container)
            // Standard tier: the flow widget lives in #selectedChartArea, so its
            // CTA / Unclassified strand are wired here rather than per-widget.
            attachNeedsWantsFlowHandlers(container)
        }

        // Scroll listener for navbar shrink animation — only runs while this component is in the DOM
        window.addEventListener("scroll", { _ ->
            if (!document.body!!.contains(container)) return@addEventListener
            val scrollY = window.scrollY
            val navbar = container.querySelector("#dashboard-logo")?.closest("div") as? HTMLElement
            val logo = document.getElementById("dashboard-logo") as? HTMLElement
            val progress = (scrollY / 80.0).coerceIn(0.0, 1.0)
            val vPad = (16 - (8 * progress)).toInt()
            val logoH = (56 - (28 * progress)).toInt()

            navbar?.style?.setProperty("padding", "${vPad}px 32px")
            logo?.style?.setProperty("height", "${logoH}px")

            val navHeight = navbar?.asDynamic()?.offsetHeight?.unsafeCast<Int>() ?: 80
            // Update sticky position for fixed layout's left panel or grid layout's palette
            val stickyEl = document.getElementById("dashboard-left-panel") ?: document.getElementById("widget-palette")
            (stickyEl as? HTMLElement)?.style?.setProperty("top", "${navHeight}px")
        })
    }

    /** Setup tooltips for all charts in the fixed (standard) layout */
    private fun setupChartTooltips(container: HTMLElement) {
        // Same wiring the grid widgets use — tooltips AND the click-to-select-month
        // handler — so both layouts stay in step from one place.
        setupSavingsTrendTooltips(container)
        setupExpenseTimelineTooltips(container)
        // Dismiss tooltips on touch outside
        val savingsTooltip = container.querySelector("#savingsChartTooltip") as? HTMLElement
        val expenseTooltip = container.querySelector("#expenseChartTooltip") as? HTMLElement
        kotlinx.browser.document.addEventListener("touchstart", { event ->
            val target = event.target as? org.w3c.dom.Element
            if (target?.classList?.contains("savings-data-point") != true &&
                target?.classList?.contains("expense-data-point") != true) {
                savingsTooltip?.style?.display = "none"
                expenseTooltip?.style?.display = "none"
            }
        })
        setupTripleBarTooltips(container, "#tripleBarTooltip", "#tripleBarChart", ".triple-bar-group")
        setupTripleBarTooltips(container, "#tripleBarTooltipPrev", "#tripleBarChartPrev", ".triple-bar-group-prev")
        setupCategoryBarHoverEffects(container)
        setupPieLegendHoverEffects(container)
    }

    private fun setupTripleBarTooltips(container: HTMLElement, tooltipId: String, chartId: String, groupClass: String) {
        val tooltip = container.querySelector(tooltipId) as? HTMLElement
        val chart = container.querySelector(chartId) as? org.w3c.dom.Element
        val barGroups = container.querySelectorAll(groupClass)

        for (i in 0 until barGroups.length) {
            val group = barGroups.item(i) as? org.w3c.dom.Element
            group?.addEventListener("mouseenter", { event ->
                val tooltipData = group.getAttribute("data-tooltip") ?: return@addEventListener
                val parts = tooltipData.split("|")
                if (parts.size >= 4) {
                    val (month, income, expenses, savings) = parts
                    tooltip?.let { tip ->
                        tip.innerHTML = """
                            <div style="font-weight: 600; margin-bottom: 6px; color: ${WebTheme.current.textPrimary};">$month</div>
                            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 3px;">
                                <span style="width: 8px; height: 8px; background: ${WebTheme.current.incomeGreen}; border-radius: 2px;"></span>
                                <span style="color: ${WebTheme.current.textSecondary};">${StringKey.CHART_INCOME.tr()}:</span>
                                <span style="color: ${WebTheme.current.incomeGreen}; font-weight: 500;">$income</span>
                            </div>
                            <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 3px;">
                                <span style="width: 8px; height: 8px; background: ${WebTheme.current.expenseRed}; border-radius: 2px;"></span>
                                <span style="color: ${WebTheme.current.textSecondary};">${StringKey.CHART_EXPENSES.tr()}:</span>
                                <span style="color: ${WebTheme.current.expenseRed}; font-weight: 500;">$expenses</span>
                            </div>
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <span style="width: 8px; height: 8px; background: ${WebTheme.current.accent}; border-radius: 2px;"></span>
                                <span style="color: ${WebTheme.current.textSecondary};">${StringKey.DASHBOARD_SAVINGS.tr()}:</span>
                                <span style="color: ${WebTheme.current.accent}; font-weight: 500;">$savings</span>
                            </div>
                        """.trimIndent()
                        tip.style.display = "block"
                        // Position tooltip — clamp to stay within chart bounds
                        chart?.let { svg ->
                            val svgRect = svg.getBoundingClientRect()
                            val groupRect = group.getBoundingClientRect()
                            val tipWidth = tip.offsetWidth.toDouble()
                            // Try placing to the right of the bar group
                            var relativeX = groupRect.left - svgRect.left + groupRect.width + 5
                            // If it overflows the right edge, place to the left instead
                            if (relativeX + tipWidth > svgRect.width) {
                                relativeX = groupRect.left - svgRect.left - tipWidth - 5
                            }
                            // Final clamp: never go off-screen left
                            relativeX = maxOf(0.0, relativeX)
                            val relativeY = maxOf(0.0, groupRect.top - svgRect.top - 20)
                            tip.style.left = "${relativeX.toInt()}px"
                            tip.style.top = "${relativeY.toInt()}px"
                        }
                    }
                }
            })
            group?.addEventListener("mouseleave", {
                tooltip?.style?.display = "none"
            })

            // Add click handler to update focal savings display with animation.
            // The target spans the whole column, so this fires for the bars AND for
            // the savings-% / month labels beneath them.
            group?.addEventListener("click", {
                val month = group.getAttribute("data-month")?.toIntOrNull()
                // Each column carries its own year: the monthly overview renders
                // selectedYear, but the prev-year comparison renders selectedYear - 1,
                // so a month index alone would select the wrong year's data.
                val year = group.getAttribute("data-focal-year")?.toIntOrNull() ?: selectedYear
                if (month != null) selectFocalYearMonth(year, month)
            })
        }
    }

    /**
     * Re-render every month-scoped surface in place after a chart click moved the
     * focal month, and move the stepper label with it. Kept as an in-place update
     * (not a full [refreshDashboard]) so the focal savings count-up animates.
     */
    private fun applyFocalMonthSelection() {
        if (useGridLayout) {
            // Pro grid: re-render every active widget so all month-scoped
            // ones follow the selection — including the monthly overview,
            // savings-trend and expense-timeline, whose X-axis emphasis
            // must move with it — then update the stepper chrome.
            activeWidgetIds.toList().forEach { renderWidgetContent(it) }
            updateMonthStepper()
            return
        }
        // Standard tier: fixed layout has no #widget-content-* anchors,
        // so update the focal display and #selectedChartArea directly.
        val monthName = getFocalMonthName()
        val isYearly = isYearlyAverage()
        val labelText = when {
            isYearly -> StringKey.DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR.tr(selectedYear.toString())
            monthName != null -> StringKey.DASHBOARD_SAVINGS_FOR_MONTH.tr(monthName)
            else -> StringKey.DASHBOARD_SAVINGS_CURRENT_MONTH.tr()
        }
        (container?.querySelector("#focalLabel") as? HTMLElement)?.textContent = labelText
        animateFocalSavingsDisplay()
        // The Savings Recommendation is month-scoped too — it reads
        // effectiveDisplayYearMonth() like every other month-scoped surface — but it
        // lives OUTSIDE #selectedChartArea, so refreshing the charts left it naming
        // the old month. Clicking April redrew every chart around a card still
        // reading July. The month stepper escaped this only because it takes the
        // full refreshDashboard() path; the grid branch above escapes it by
        // re-rendering every widget. The fixed layout had neither.
        (container?.querySelector("#recommendation-panel") as? HTMLElement)?.let { recEl ->
            recEl.innerHTML = renderRecommendationPanel()
            container?.let { setupRecommendationEventListeners(it) }
        }
        // Every chart in #selectedChartArea is month-scoped — the category bars and
        // pie by their data, the two line charts by which month they emphasize on
        // the X axis — so re-render whichever one is showing and re-wire it.
        val chartArea = container?.querySelector("#selectedChartArea") as? HTMLElement
        chartArea?.innerHTML = renderChart()
        when (selectedChartType) {
            ChartType.BAR_CHART -> {
                container?.querySelector("#legend-category-budget")?.addEventListener("click", {
                    showCategoryBudget = !showCategoryBudget
                    settingsManager.setChartSeriesVisibility("categoryBudget", showCategoryBudget)
                    refreshDashboard()
                })
                chartArea?.let { setupCategoryBarHoverEffects(it) }
            }
            ChartType.PIE_CHART -> {
                chartArea?.let { setupPieLegendHoverEffects(it) }
            }
            ChartType.NEEDS_WANTS_FLOW -> {
                attachNeedsWantsFlowHandlers(chartArea)
            }
            ChartType.SAVINGS_LINE -> {
                container?.let { setupSavingsTrendTooltips(it) }
            }
            ChartType.EXPENSE_TIMELINE -> {
                container?.let { setupExpenseTimelineTooltips(it) }
            }
            ChartType.PREV_YEAR_COMPARISON -> {
                container?.let { c ->
                    setupTripleBarTooltips(c, "#tripleBarTooltipPrev", "#tripleBarChartPrev", ".triple-bar-group-prev")
                }
            }
        }
        // Re-render the monthly overview so the selected month's
        // X-axis label emphasis moves, and update the stepper.
        (container?.querySelector("#classic-triple-bar") as? HTMLElement)?.let { bars ->
            bars.innerHTML = renderTripleBarChart(selectedYear)
            container?.let { c ->
                setupTripleBarTooltips(c, "#tripleBarTooltip", "#tripleBarChart", ".triple-bar-group")
            }
        }
        updateMonthStepper()
    }

    /**
     * Animate the focal savings percentage count-up with gradient color.
     *
     * Pass [animate] = false to jump straight to the final value. A full dashboard
     * rebuild re-runs this, so a rebuild triggered by a secondary loader (budget plans
     * landing after transactions) would otherwise restart the counter from 0 and the
     * user would watch it count up twice.
     */
    private fun animateFocalSavingsDisplay(animate: Boolean = true) {
        val targetValue = getFocalSavingsPercentage()
        val percentageElement = container?.querySelector("#focalPercentage") as? HTMLElement ?: return
        val gradientContainer = container?.querySelector("#focalGradientContainer") as? HTMLElement

        val progressFill = container?.querySelector("#focalProgressFill") as? HTMLElement

        fun applyGradientForValue(pct: Double) {
            gradientContainer?.setAttribute("style",
                "display: inline-flex; align-items: baseline; justify-content: center; gap: 2px; ${savingsGradientStyle(pct)}")
            // Progress bar: fill width = pct / target * 100, capped at 100%
            val fillPct = if (savingsTarget > 0) (pct / savingsTarget * 100).coerceIn(0.0, 100.0) else 0.0
            val barProgress = (pct / savingsTarget).coerceIn(0.0, 1.0)
            val bandWidth = 12
            val bandCenter = (barProgress * 100).toInt()
            val bandStart = (bandCenter - bandWidth / 2).coerceIn(0, 100)
            val bandEnd = (bandCenter + bandWidth / 2).coerceIn(0, 100)
            val barColor = if (pct <= 0) WebTheme.current.border
                else savingsGradientColor(pct)
            progressFill?.setAttribute("style",
                "width: ${fillPct.toInt()}%; height: 100%; background: $barColor;")
        }

        if (!animate) {
            // Same end state the animation settles on, applied immediately.
            percentageElement.textContent = analytics.formatSavingsPercentValue(targetValue)
            applyGradientForValue(targetValue)
            return
        }

        // Count-up animation (slower to show gradient progression)
        var currentValue = 0.0
        val steps = 40
        val stepDelay = 1400 / steps  // 1.4 seconds total

        fun animateStep(step: Int) {
            if (step >= steps) {
                percentageElement.textContent = analytics.formatSavingsPercentValue(targetValue)
                applyGradientForValue(targetValue)
                return
            }
            val progress = (step + 1).toDouble() / steps
            val base = 1 - progress
            currentValue = targetValue * (1 - base * base * base)  // Cubic ease-out: 1 - (1-p)^3
            percentageElement.textContent = analytics.formatSavingsPercentValue(currentValue)
            applyGradientForValue(currentValue)
            kotlinx.browser.window.setTimeout({ animateStep(step + 1) }, stepDelay)
        }

        animateStep(0)
    }

    // Update focal savings display without full dashboard refresh
    private fun updateFocalSavingsDisplay() {
        val percentage = getFocalSavingsPercentage()
        val monthName = getFocalMonthName()
        val isYearly = isYearlyAverage()

        val labelText = when {
            isYearly -> StringKey.DASHBOARD_AVERAGE_SAVINGS_FOR_YEAR.tr(selectedYear.toString())
            monthName != null -> StringKey.DASHBOARD_SAVINGS_FOR_MONTH.tr(monthName)
            else -> StringKey.DASHBOARD_SAVINGS_CURRENT_MONTH.tr()
        }

        val percentageElement = container?.querySelector("#focalPercentage") as? HTMLElement
        val gradientContainer = container?.querySelector("#focalGradientContainer") as? HTMLElement
        val labelElement = container?.querySelector("#focalLabel") as? HTMLElement

        percentageElement?.textContent = analytics.formatSavingsPercentValue(percentage)
        gradientContainer?.setAttribute("style",
            "display: inline-flex; align-items: baseline; justify-content: center; gap: 2px; ${savingsGradientStyle(percentage)}")
        labelElement?.textContent = labelText
    }

    private fun setupCategoryBarHoverEffects(container: HTMLElement) {
        val barRows = container.querySelectorAll(".category-bar-row")

        fun deactivateRow(row: HTMLElement) {
            row.setAttribute("data-selected", "false")
            row.style.background = "transparent"
            (row.querySelector(".category-name") as? HTMLElement)?.let {
                it.style.fontSize = "10px"; it.style.fontWeight = "normal"; it.style.color = WebTheme.current.textSecondary
            }
            (row.querySelector(".category-value") as? HTMLElement)?.let {
                it.style.fontSize = "10px"; it.style.fontWeight = "normal"; it.style.color = WebTheme.current.textSecondary
            }
            (row.querySelector(".category-comparison") as? HTMLElement)?.style?.display = "none"
        }

        fun activateRow(row: HTMLElement) {
            row.setAttribute("data-selected", "true")
            row.style.background = if (WebTheme.current.isDark) "rgba(255, 255, 255, 0.02)" else "rgba(0, 0, 0, 0.02)"
            (row.querySelector(".category-name") as? HTMLElement)?.let {
                it.style.fontSize = "12px"; it.style.fontWeight = "600"; it.style.color = WebTheme.current.textPrimary
            }
            (row.querySelector(".category-value") as? HTMLElement)?.let {
                it.style.fontSize = "12px"; it.style.fontWeight = "600"; it.style.color = WebTheme.current.accent
            }
            (row.querySelector(".category-comparison") as? HTMLElement)?.style?.display = "block"
        }

        for (i in 0 until barRows.length) {
            val row = barRows.item(i) as? HTMLElement ?: continue

            // Click: toggle selected (deselects all others first, like mobile)
            row.addEventListener("click", {
                val isSelected = row.getAttribute("data-selected") == "true"
                for (j in 0 until barRows.length) {
                    (barRows.item(j) as? HTMLElement)?.let { deactivateRow(it) }
                }
                if (!isSelected) activateRow(row)
            })

            // Hover: highlight only if not already click-selected
            row.addEventListener("mouseenter", {
                if (row.getAttribute("data-selected") != "true") {
                    row.style.background = if (WebTheme.current.isDark) "rgba(255, 255, 255, 0.02)" else "rgba(0, 0, 0, 0.02)"
                    (row.querySelector(".category-name") as? HTMLElement)?.let {
                        it.style.fontSize = "12px"; it.style.fontWeight = "600"; it.style.color = WebTheme.current.textPrimary
                    }
                    (row.querySelector(".category-value") as? HTMLElement)?.let {
                        it.style.fontSize = "12px"; it.style.fontWeight = "600"; it.style.color = WebTheme.current.accent
                    }
                    (row.querySelector(".category-comparison") as? HTMLElement)?.style?.display = "block"
                }
            })

            // Mouse leave: reset only if not click-selected
            row.addEventListener("mouseleave", {
                if (row.getAttribute("data-selected") != "true") {
                    deactivateRow(row)
                }
            })
        }
    }

    private fun setupPieLegendHoverEffects(container: HTMLElement) {
        // Pill toggle: rolling-12 vs selected month
        container.querySelector("#pie-pill-rolling")?.addEventListener("click", {
            if (!pieRollingMode) {
                pieRollingMode = true
                settingsManager.setChartSeriesVisibility("pieRollingMode", true)
                rerenderPieChart()
            }
        })
        container.querySelector("#pie-pill-month")?.addEventListener("click", {
            if (pieRollingMode) {
                pieRollingMode = false
                settingsManager.setChartSeriesVisibility("pieRollingMode", false)
                rerenderPieChart()
            }
        })

        val legendItems = container.querySelectorAll(".pie-legend-item")
        val pieSlices = container.querySelectorAll(".pie-slice")

        // Helper to highlight a legend item
        fun highlightLegendItem(item: HTMLElement, highlight: Boolean) {
            if (highlight) {
                item.style.background = "rgba(0, 217, 255, 0.1)"
                item.style.transform = "scale(1.05)"
                val nameEl = item.querySelector(".pie-legend-name") as? HTMLElement
                val valueEl = item.querySelector(".pie-legend-value") as? HTMLElement
                val colorEl = item.querySelector(".pie-legend-color") as? HTMLElement
                nameEl?.let {
                    it.style.fontSize = "12px"
                    it.style.fontWeight = "600"
                    it.style.color = WebTheme.current.textPrimary
                }
                valueEl?.let {
                    it.style.fontSize = "11px"
                    it.style.fontWeight = "500"
                    it.style.color = WebTheme.current.accent
                }
                colorEl?.let {
                    it.style.transform = "scale(1.3)"
                }
            } else {
                item.style.background = "transparent"
                item.style.transform = "scale(1)"
                val nameEl = item.querySelector(".pie-legend-name") as? HTMLElement
                val valueEl = item.querySelector(".pie-legend-value") as? HTMLElement
                val colorEl = item.querySelector(".pie-legend-color") as? HTMLElement
                nameEl?.let {
                    it.style.fontSize = "10px"
                    it.style.fontWeight = "normal"
                    it.style.color = WebTheme.current.textSecondary
                }
                valueEl?.let {
                    it.style.fontSize = "10px"
                    it.style.fontWeight = "normal"
                    it.style.color = WebTheme.current.textSecondary
                }
                colorEl?.let {
                    it.style.transform = "scale(1)"
                }
            }
        }

        // Helper to highlight a pie slice
        fun highlightPieSlice(slice: org.w3c.dom.Element, highlight: Boolean) {
            if (highlight) {
                slice.setAttribute("style", "cursor: pointer; transform-origin: 75px 75px; transition: transform 0.2s ease-out, opacity 0.2s; transform: scale(1.08); opacity: 1;")
            } else {
                slice.setAttribute("style", "cursor: pointer; transform-origin: 75px 75px; transition: transform 0.2s ease-out, opacity 0.2s; transform: scale(1); opacity: 0.85;")
            }
        }

        // Legend item hover -> highlight corresponding pie slice
        for (i in 0 until legendItems.length) {
            val item = legendItems.item(i) as? HTMLElement
            item?.addEventListener("mouseenter", {
                highlightLegendItem(item, true)
                // Find and highlight corresponding pie slice
                val index = item.getAttribute("data-index")
                for (j in 0 until pieSlices.length) {
                    val slice = pieSlices.item(j) as? org.w3c.dom.Element
                    if (slice != null && slice.getAttribute("data-index") == index) {
                        highlightPieSlice(slice, true)
                    }
                }
            })
            item?.addEventListener("mouseleave", {
                highlightLegendItem(item, false)
                // Reset corresponding pie slice
                val index = item.getAttribute("data-index")
                for (j in 0 until pieSlices.length) {
                    val slice = pieSlices.item(j) as? org.w3c.dom.Element
                    if (slice != null && slice.getAttribute("data-index") == index) {
                        highlightPieSlice(slice, false)
                    }
                }
            })
        }

        // Pie slice hover -> highlight corresponding legend item
        for (i in 0 until pieSlices.length) {
            val slice = pieSlices.item(i) as? org.w3c.dom.Element
            slice?.addEventListener("mouseenter", {
                highlightPieSlice(slice, true)
                // Find and highlight corresponding legend item
                val index = slice.getAttribute("data-index")
                for (j in 0 until legendItems.length) {
                    val item = legendItems.item(j) as? HTMLElement
                    if (item != null && item.getAttribute("data-index") == index) {
                        highlightLegendItem(item, true)
                    }
                }
            })
            slice?.addEventListener("mouseleave", {
                highlightPieSlice(slice, false)
                // Reset corresponding legend item
                val index = slice.getAttribute("data-index")
                for (j in 0 until legendItems.length) {
                    val item = legendItems.item(j) as? HTMLElement
                    if (item != null && item.getAttribute("data-index") == index) {
                        highlightLegendItem(item, false)
                    }
                }
            })
        }
    }

    private fun saveCategoryColor(category: String, color: String) {
        val mutableColors = userCategoryColors.toMutableMap()
        // Store both normalized forms for matching
        val normalizedUpper = category.uppercase().replace(' ', '_')
        val normalizedLower = category.lowercase().replace(' ', '_')
        mutableColors[normalizedUpper] = color
        mutableColors[normalizedLower] = color
        userCategoryColors = mutableColors
        // Save to server
        GlobalScope.launch {
            try {
                val json = Json.encodeToString(userCategoryColors)
                apiService.setSetting("custom_category_colors", json)
            } catch (_: Exception) {}
        }
        // Re-render all widgets that use category colors
        activeWidgetIds.forEach { renderWidgetContent(it) }
        // Update transaction list icons behind the dashboard (if visible)
        kotlinx.browser.document.querySelectorAll("[data-cat-color-key]")?.let { icons ->
            for (j in 0 until icons.length) {
                val icon = icons.item(j) as? org.w3c.dom.HTMLElement ?: continue
                val iconCat = icon.getAttribute("data-cat-color-key") ?: continue
                val iconCatNorm = iconCat.lowercase().replace(' ', '_')
                if (iconCatNorm == normalizedLower) {
                    icon.style.borderColor = color
                    icon.setAttribute("data-cat-color", color)
                }
            }
        }
    }

    /**
     * Placeholder shown while the first transaction load is in flight. Mirrors the real
     * dashboard's shape (nav strip, focal number, chart block, side panel) so the layout
     * doesn't jump when data arrives, and pulses gently so it reads as "loading" rather
     * than "empty". Pure CSS animation — no JS timers.
     */
    private fun renderLoadingSkeleton(): HTMLElement {
        val t = WebTheme.current
        val el = document.createElement("div") as HTMLElement
        el.className = "dashboard-redesigned"
        val block = "background:${t.border}; border-radius:8px; opacity:.55;"
        el.innerHTML = """
            <style>
                @keyframes wiz3rSkeletonPulse { 0%,100% { opacity:.35; } 50% { opacity:.7; } }
                @keyframes wiz3rSkeletonSpin { to { transform: rotate(360deg); } }
                .wiz3r-skel { animation: wiz3rSkeletonPulse 1.4s ease-in-out infinite; }
                @media (prefers-reduced-motion: reduce) { .wiz3r-skel { animation: none; } }
            </style>
            <div style="position:relative; padding: 24px 32px; max-width: 1400px; margin: 0 auto; min-height: calc(100vh - 140px);">
                <!-- Layout hint: dimmed so the centred spinner stays the focal point. -->
                <div style="opacity:.45;">
                    <div class="wiz3r-skel" style="$block height:28px; width:220px; margin-bottom:28px;"></div>
                    <div style="display:flex; gap:24px; flex-wrap:wrap;">
                        <div style="flex:2; min-width:320px;">
                            <div class="wiz3r-skel" style="$block height:120px; margin-bottom:16px;"></div>
                            <div class="wiz3r-skel" style="$block height:260px;"></div>
                        </div>
                        <div style="flex:1; min-width:260px;">
                            <div class="wiz3r-skel" style="$block height:90px; margin-bottom:16px;"></div>
                            <div class="wiz3r-skel" style="$block height:90px; margin-bottom:16px;"></div>
                            <div class="wiz3r-skel" style="$block height:160px;"></div>
                        </div>
                    </div>
                </div>
                <!-- Spinner centred in the visible area (the wrapper is ~viewport tall),
                     rather than tucked in the corner where it reads as a stray element. -->
                <div style="position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; pointer-events:none;">
                    <span style="width:34px; height:34px; border:3px solid ${t.border}; border-top-color:${t.accent}; border-radius:50%; display:inline-block; animation: wiz3rSkeletonSpin 0.7s linear infinite;"></span>
                    <span style="font-size:14px; font-weight:500; color:${t.textSecondary};">${StringKey.LABEL_LOADING.tr()}</span>
                </div>
            </div>
        """.trimIndent()
        return el
    }

    // C1 perf fix: on a page load, the transaction loader and the budget-plan loader each
    // finish independently and both call refreshDashboard() — without coalescing, that
    // destroys and rebuilds the whole dashboard subtree twice (skeleton -> dashboard ->
    // dashboard). Debounce so any burst of refresh requests in the same short window
    // collapses into exactly one rebuild, using whatever state is current when the
    // scheduled callback runs (so the last trigger's data is never lost).
    private var refreshScheduled = false

    private fun refreshDashboard() {
        // Several loaders finish independently (transactions, budget plans, per-year
        // budget plans, settings) and each asks for a refresh. While the data isn't ready
        // yet, render() only produces the skeleton, so rebuilding is pure waste — and in
        // the default (non-grid) layout a "refresh" is a full teardown + re-render, not a
        // widget update. Suppress those: the loader that flips dashboardDataReady does the
        // one real render. Budget plans typically land before transactions, so in the
        // common case this collapses the whole load to a single build of the dashboard.
        if (!dashboardDataReady) return
        if (refreshScheduled) return
        refreshScheduled = true
        window.setTimeout({
            refreshScheduled = false
            performDashboardRefresh()
        }, 80)
    }

    private fun performDashboardRefresh() {
        // Update widget content without destroying the grid
        if (grid != null) {
            activeWidgetIds.toList().forEach { renderWidgetContent(it) }
            updateMonthStepper() // chrome isn't rebuilt on a grid refresh — update it explicitly
        } else {
            // Grid not yet initialized - full re-render
            container?.let { cont ->
                val parent = cont.parentElement
                if (parent != null) {
                    val newDashboard = render()
                    parent.replaceChild(newDashboard, cont)
                }
            }
        }
    }

    private fun setupRecommendationEventListeners(container: HTMLElement) {
        // Create goal button in recommendation panel (no-goals empty state)
        container.querySelector("#createGoalBtn")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.GOALS)
        })

        // "Review my budget →" CTA — shown when the user has goals but no
        // savings room this month (or is over budget). Sends them to the
        // Budget Planner to find places to trim.
        container.querySelector("#viewBudgetBtn")?.addEventListener("click", {
            navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
        })

        // Clickable goal links in recommendation text
        val goalLinks = container.querySelectorAll(".goal-link")
        for (i in 0 until goalLinks.length) {
            goalLinks.item(i)?.addEventListener("click", {
                navigationManager.switchToTab(ui.AppTabType.GOALS)
            })
        }

        // CTAs on the insight rows the shared pool supplied. OPEN_GOAL rides the
        // .goal-link hook above, so only the other destinations land here.
        val insightActions = container.querySelectorAll("[data-insight-action]")
        for (i in 0 until insightActions.length) {
            val el = insightActions.item(i) as? HTMLElement ?: continue
            when (el.getAttribute("data-insight-action")) {
                "review-budget" -> el.addEventListener("click", {
                    navigationManager.switchToTab(ui.AppTabType.BUDGET_PLANNING)
                })
                "open-transactions" -> el.addEventListener("click", {
                    navigationManager.switchToTab(ui.AppTabType.TRANSACTIONS)
                })
            }
        }
    }

    /** Expenses that landed in one month, under the current account/context
     *  filters. One place — the YoY tile and the insight pool both read it. */
    private fun expensesForMonth(year: Int, month: Int): Double =
        allTransactions.filter {
            !it.isIncome && !it.excludeFromTotals && !it.isTransfer &&
                it.accountId in selectedAccountIds && it.passesContextFilter() &&
                it.year == year && it.month == month
        }.sumOf { it.amount }

    /**
     * Every category's spend in [year]/[month] against its own average over the
     * six preceding months. One place, because both the Movers tile and the
     * Recommendation widget's insight pool need the same baseline — and a second
     * copy of a "vs 6-month average" rule is exactly how these drift.
     */
    private fun categoryMoversForMonth(year: Int, month: Int): List<DashboardInsights.CategoryMover> {
        val thisMonth = getCategorySpendingForMonth(year, month)
        if (thisMonth.isEmpty()) return emptyList()

        val totals = mutableMapOf<String, Double>()
        val counts = mutableMapOf<String, Int>()
        for (offset in 1..6) {
            var m = month - offset
            var y = year
            if (m <= 0) { m += 12; y -= 1 }
            getCategorySpendingForMonth(y, m).forEach { (cat, amt) ->
                totals[cat] = (totals[cat] ?: 0.0) + amt
                counts[cat] = (counts[cat] ?: 0) + 1
            }
        }
        return thisMonth.map { (cat, amount) ->
            val count = counts[cat] ?: 0
            DashboardInsights.CategoryMover(
                category = cat,
                thisMonth = amount,
                sixMonthAverage = if (count > 0) (totals[cat] ?: 0.0) / count else 0.0,
                monthsOfHistory = count,
            )
        }
    }

    /** Consecutive completed months that met the savings target, walking back
     *  from last month. One place — the Streak tile and the insight pool. */
    private fun savingsStreakMonths(): Int {
        val now = Date()
        var year = now.getFullYear()
        // Start from the previous month — the current one isn't complete yet.
        var month = now.getMonth() // getMonth() is 0-based, so this IS last month 1-based
        if (month <= 0) { month += 12; year -= 1 }

        var streak = 0
        repeat(60) { // cap the walk at 5 years
            val data = yearlyData[year]?.get(month) ?: return@repeat
            if (!data.hasData) return@repeat
            val rate = if (data.confirmedIncome > 0) data.actualSavings / data.confirmedIncome * 100 else 0.0
            if (rate >= savingsTarget) {
                streak++
                month -= 1
                if (month <= 0) { month += 12; year -= 1 }
            } else {
                return@repeat
            }
        }
        return streak
    }

    /** The category furthest past its own budget line this month, with the
     *  overage — the "biggest driver" the expanded over-budget primary names. */
    private fun biggestOverBudgetCategory(year: Int, month: Int): Pair<String, Double>? {
        val planned = getPlannedBudgetsForMonth(year, month)
        if (planned.isEmpty()) return null
        return getCategorySpendingForMonth(year, month)
            .mapNotNull { (displayCategory, actual) ->
                val plaid = categoryDisplayToPlaidCode[displayCategory] ?: displayCategory
                val budget = planned[plaid] ?: return@mapNotNull null
                if (budget <= 0.0) return@mapNotNull null
                val over = actual - budget
                if (over > 0.0) displayCategory to over else null
            }
            .maxByOrNull { it.second }
    }

    // -----------------------------------------------------------------
    // New KPI widgets — all read from `allTransactions`, `yearlyData`, and
    // `cachedBudgetPlans`, filtered by `selectedAccountIds` and contexts.
    // -----------------------------------------------------------------

    /** Shared shell for single-number KPI widgets. Number + optional unit share a
     *  single line (e.g. "0 months"), so wide-but-short tiles fill horizontally
     *  instead of stacking and cropping. Everything scales via container queries
     *  so caps only kick in on very large tiles. overflow: hidden is a safety
     *  net — the cqh multipliers sum well under 100% so nothing should clip. */
    private fun kpiShell(
        title: String,
        bigValue: String,
        valueColor: String,
        footnote: String,
        unit: String = "",
    ): String {
        // The big value stays responsive — it is the single hero number that is
        // meant to fill the tile. The unit ("months") rides at the TITLE size and
        // the title/footnote drop to the fixed LABEL scale.
        val valueLine = if (unit.isNotEmpty()) {
            """
                <div style="line-height: 1.05; white-space: nowrap; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 100%; overflow: hidden;">
                    <span style="color: $valueColor; font-size: clamp(24px, min(34cqw, 40cqh), 220px); font-weight: 700;">$bigValue</span><span style="color: ${t.textSecondary}; font-size: ${DashType.TITLE}; font-weight: 500; margin-left: 0.25em;">$unit</span>
                </div>
            """.trimIndent()
        } else {
            """<div style="color: $valueColor; font-size: clamp(24px, min(30cqw, 40cqh), 260px); font-weight: 700; line-height: 1.05; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; white-space: nowrap; max-width: 100%; overflow: hidden;">$bigValue</div>"""
        }
        val footnoteHtml = if (footnote.isNotEmpty()) {
            """<div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-top: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%;">$footnote</div>"""
        } else ""
        return """
            <div style="
                height: 100%; width: 100%;
                display: flex; flex-direction: column;
                justify-content: center; align-items: center;
                padding: 4px; container-type: size; text-align: center; box-sizing: border-box;
                overflow: hidden;
            ">
                <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-bottom: 6px;">$title</div>
                $valueLine
                $footnoteHtml
            </div>
        """
    }

    /** Format a dollar amount with sign. Positive cash flow shows with "+$", negative with "-$". */
    private fun formatSignedDollars(amount: Double): String {
        val abs = kotlin.math.round(kotlin.math.abs(amount)).toInt()
        val sign = if (amount > 0) "+$" else if (amount < 0) "-$" else "$"
        return "$sign$abs"
    }

    /** Cash flow month-to-date: income − expenses so far this month, with delta vs same point last month. */
    private fun renderCashFlowMtdWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val now = Date()
        val currentYear = now.getFullYear()
        val currentMonth = now.getMonth() + 1
        val (year, month) = effectiveDisplayYearMonth()
        val isCurrentMonth = year == currentYear && month == currentMonth
        // Current month: cap at today (true MTD). Past months: full month.
        val dayCap = if (isCurrentMonth) now.getDate() else 31

        fun cashFlowUpToDay(y: Int, m: Int, maxDay: Int): Double {
            val txns = allTransactions.filter { txn ->
                txn.year == y && txn.month == m && txn.day <= maxDay &&
                !txn.excludeFromTotals && !txn.isTransfer &&
                txn.accountId in selectedAccountIds && txn.passesContextFilter()
            }
            val income = txns.filter { it.isIncome }.sumOf { it.amount }
            val expenses = txns.filter { !it.isIncome }.sumOf { it.amount }
            return income - expenses
        }

        val flow = cashFlowUpToDay(year, month, dayCap)
        val prevYm = if (month == 1) (year - 1) to 12 else year to (month - 1)
        val prevFlow = cashFlowUpToDay(prevYm.first, prevYm.second, dayCap)
        val delta = flow - prevFlow

        val color = if (flow >= 0) t.accent else t.expenseRed
        val deltaColor = if (delta >= 0) t.incomeGreen else t.expenseRed
        val arrow = if (delta > 0) "\u25B2" else if (delta < 0) "\u25BC" else "="
        val note = if (isFr) "$arrow ${formatSignedDollars(delta)} vs le mois pr\u00e9c\u00e9dent" else "$arrow ${formatSignedDollars(delta)} vs prev month"
        val monthName = monthNames[month - 1]
        val title = if (isCurrentMonth) {
            if (isFr) "Flux de tr\u00e9sorerie (MTD)" else "Cash flow (MTD)"
        } else {
            if (isFr) "Flux de tr\u00e9sorerie \u2014 $monthName" else "Cash flow \u2014 $monthName"
        }
        return kpiShell(title, formatSignedDollars(flow), color, """<span style="color: $deltaColor;">$note</span>""")
    }

    /** Sum planned and realized expenses for the focused (or current) month; show % consumed. */
    private fun renderBudgetHealthWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()
        val data = yearlyData[year]?.get(month)
        val planned = data?.plannedExpenses ?: 0.0
        val realized = data?.realizedExpenses ?: 0.0
        if (planned <= 0.0) {
            val title = if (isFr) "Budget consomm\u00e9" else "Budget consumed"
            val empty = if (isFr) "Aucun budget pour ce mois" else "No budget for this month"
            return kpiShell(title, "—", t.textSecondary, empty)
        }
        val pct = (realized / planned * 100).toInt()
        val color = when {
            pct < 80 -> t.incomeGreen
            pct <= 100 -> t.targetOrange
            else -> t.expenseRed
        }
        val title = if (isFr) "Budget consomm\u00e9" else "Budget consumed"
        val note = "\$${realized.roundToInt()} / \$${planned.roundToInt()}"
        return kpiShell(title, "$pct%", color, note)
    }

    /** Count categories over budget, under budget, and without a budget for the focused (or current) month. */
    private fun renderBudgetCategoriesCountWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()
        val plannedByPlaid = getPlannedBudgetsForMonth(year, month)
        val actualByDisplay = getCategorySpendingForMonth(year, month)

        if (plannedByPlaid.isEmpty() && actualByDisplay.isEmpty()) {
            val title = if (isFr) "Statut par cat\u00e9gorie" else "Category status"
            val empty = if (isFr) "Aucune donn\u00e9e ce mois-ci" else "No data this month"
            return kpiShell(title, "—", t.textSecondary, empty)
        }

        var over = 0; var under = 0; var noBudget = 0
        actualByDisplay.forEach { (displayCat, actual) ->
            val plaid = categoryDisplayToPlaidCode[displayCat] ?: displayCat
            val planned = plannedByPlaid[plaid] ?: 0.0
            when {
                planned <= 0.0 -> noBudget++
                actual > planned -> over++
                else -> under++
            }
        }
        // Count categories that have a budget but no spend yet — they count as "under".
        plannedByPlaid.keys.forEach { plaid ->
            val hasActual = actualByDisplay.any { (displayCat, _) ->
                (categoryDisplayToPlaidCode[displayCat] ?: displayCat) == plaid
            }
            if (!hasActual) under++
        }

        val title = if (isFr) "Cat\u00e9gories" else "Categories"
        val overLabel = if (isFr) "au-dessus" else "over"
        val underLabel = if (isFr) "sous" else "under"
        val noBudgetLabel = if (isFr) "sans budget" else "no budget"
        return """
            <div style="
                height: 100%; width: 100%;
                display: flex; flex-direction: column;
                justify-content: center; align-items: center;
                padding: 8px; container-type: size; text-align: center; box-sizing: border-box;
            ">
                <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-bottom: 8px;">$title</div>
                <div style="display: flex; gap: clamp(10px, min(8cqw, 14cqh), 44px); align-items: baseline;">
                    <div>
                        <div style="color: ${t.expenseRed}; font-size: clamp(22px, min(18cqw, 42cqh), 180px); font-weight: 700; line-height: 1;">$over</div>
                        <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$overLabel</div>
                    </div>
                    <div>
                        <div style="color: ${t.incomeGreen}; font-size: clamp(22px, min(18cqw, 42cqh), 180px); font-weight: 700; line-height: 1;">$under</div>
                        <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$underLabel</div>
                    </div>
                    <div>
                        <div style="color: ${t.textSecondary}; font-size: clamp(22px, min(18cqw, 42cqh), 180px); font-weight: 700; line-height: 1;">$noBudget</div>
                        <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$noBudgetLabel</div>
                    </div>
                </div>
            </div>
        """
    }

    /** Consecutive past months meeting the savings target. Walks back from the last completed month. */
    private fun renderSavingsStreakWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val streak = savingsStreakMonths()

        val unit = if (isFr) "mois" else (if (streak == 1) "month" else "months")
        val title = if (isFr) "S\u00e9rie d'\u00e9pargne" else "Savings streak"
        val note = if (streak == 0) {
            if (isFr) "Atteignez ${savingsTarget.toInt()}% ce mois-ci" else "Hit ${savingsTarget.toInt()}% this month"
        } else {
            if (isFr) "cible: ${savingsTarget.toInt()}%" else "target: ${savingsTarget.toInt()}%"
        }
        val color = if (streak > 0) t.accent else t.textSecondary
        return kpiShell(title, streak.toString(), color, note, unit = unit)
    }

    /** Side-by-side expense totals: focused (or current) month vs the same month a year earlier. */
    private fun renderYoyMonthWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()

        val thisYear = expensesForMonth(year, month)
        val lastYear = expensesForMonth(year - 1, month)
        val monthName = monthNames[month - 1]
        val maxVal = maxOf(thisYear, lastYear, 1.0)
        val thisBar = (thisYear / maxVal * 100).toInt()
        val lastBar = (lastYear / maxVal * 100).toInt()
        val hasComparison = lastYear > 0
        val delta = if (hasComparison) ((thisYear - lastYear) / lastYear * 100).toInt() else 0
        val deltaColor = if (delta > 0) t.expenseRed else if (delta < 0) t.incomeGreen else t.textSecondary
        val deltaSign = if (delta > 0) "+" else ""
        val deltaHtml = if (hasComparison) {
            """<span style="color: $deltaColor; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI};">$deltaSign$delta%</span>"""
        } else ""
        val title = if (isFr) "$monthName vs ${year - 1}" else "$monthName vs ${year - 1}"
        val spendLabel = if (isFr) "d\u00e9penses" else "expenses"

        return """
            <div style="
                height: 100%; width: 100%;
                display: flex; flex-direction: column;
                padding: 14px 16px; box-sizing: border-box;
            ">
                <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 10px;">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$title</span>
                    $deltaHtml
                </div>
                <div style="display: flex; flex-direction: column; gap: 12px; flex: 1; justify-content: center;">
                    <div>
                        <div style="display: flex; justify-content: space-between; font-size: ${DashType.LABEL}; color: ${t.textSecondary}; margin-bottom: 3px;">
                            <span>$year</span><span>${'$'}${thisYear.roundToInt()} $spendLabel</span>
                        </div>
                        <div style="height: 10px; background: ${t.border}; border-radius: 4px; overflow: hidden;">
                            <div style="width: $thisBar%; height: 100%; background: ${t.accent}; border-radius: 4px;"></div>
                        </div>
                    </div>
                    <div>
                        <div style="display: flex; justify-content: space-between; font-size: ${DashType.LABEL}; color: ${t.textSecondary}; margin-bottom: 3px;">
                            <span>${year - 1}</span><span>${'$'}${lastYear.roundToInt()} $spendLabel</span>
                        </div>
                        <div style="height: 10px; background: ${t.border}; border-radius: 4px; overflow: hidden;">
                            <div style="width: $lastBar%; height: 100%; background: ${t.textSecondary}; opacity: 0.6; border-radius: 4px;"></div>
                        </div>
                    </div>
                </div>
            </div>
        """
    }

    /** Categories with biggest % change vs their 6-month rolling average. Shows top 3 movers (absolute delta). */
    private fun renderCategoryMoversWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()

        val thisMonth = getCategorySpendingForMonth(year, month)
        if (thisMonth.isEmpty()) {
            val title = if (isFr) "Variations" else "Movers"
            val empty = if (isFr) "Aucune donn\u00e9e ce mois-ci" else "No data this month"
            return kpiShell(title, "—", t.textSecondary, empty)
        }

        // Shared 6-month baseline (see categoryMoversForMonth). This tile keeps its
        // own display rule on top of it: at least 2 months of history and a
        // positive average before a category is worth a row.
        data class Mover(val cat: String, val pct: Int, val delta: Double)
        val movers = categoryMoversForMonth(year, month).mapNotNull { m ->
            if (m.monthsOfHistory < 2 || m.sixMonthAverage <= 0) return@mapNotNull null
            val pct = ((m.thisMonth - m.sixMonthAverage) / m.sixMonthAverage * 100).toInt()
            Mover(m.category, pct, m.thisMonth - m.sixMonthAverage)
        }.sortedByDescending { kotlin.math.abs(it.pct) }

        if (movers.isEmpty()) {
            val title = if (isFr) "Variations" else "Movers"
            val empty = if (isFr) "Plus de donn\u00e9es sont n\u00e9cessaires" else "Need more months of data"
            return kpiShell(title, "—", t.textSecondary, empty)
        }

        val title = if (isFr) "Variations vs moy. 6 mois" else "Movers vs 6-mo avg"
        val maxRows = fitRows("category-movers", movers.size, rowPx = 34, headerPx = 44, min = 2)
        val rows = movers.take(maxRows).joinToString("") { m ->
            val arrow = if (m.pct > 0) "\u25B2" else if (m.pct < 0) "\u25BC" else "="
            val color = if (m.pct > 0) t.expenseRed else if (m.pct < 0) t.incomeGreen else t.textSecondary
            val sign = if (m.pct > 0) "+" else ""
            """
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 7px 0; border-bottom: 1px solid ${t.border};">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.BODY}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;" title="${m.cat}">${m.cat}</span>
                    <span style="color: $color; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">$arrow $sign${m.pct}%</span>
                </div>
            """
        }
        return """
            <div style="padding: 14px 16px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column;">
                <div style="color: ${t.textSecondary}; font-size: ${DashType.LABEL}; margin-bottom: 8px;">$title</div>
                <div style="flex: 1; display: flex; flex-direction: column; overflow: hidden;">$rows</div>
            </div>
        """
    }

    /** Biggest expenses for the focused (or current) month, as many as fit the tile height; click navigates to the Transactions tab. */
    private fun renderLargestTransactionsWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()
        val top = allTransactions
            .filter {
                !it.isIncome && !it.excludeFromTotals && !it.isTransfer &&
                it.accountId in selectedAccountIds && it.passesContextFilter() &&
                it.year == year && it.month == month
            }
            .sortedByDescending { it.amount }

        val title = if (isFr) "Plus grosses d\u00e9penses" else "Largest expenses"
        if (top.isEmpty()) {
            val empty = if (isFr) "Aucune d\u00e9pense ce mois-ci" else "No expenses this month"
            return kpiShell(title, "—", t.textSecondary, empty)
        }

        val maxRows = fitRows("largest-txns", top.size, rowPx = 34, headerPx = 44, min = 2)
        val rows = top.take(maxRows).joinToString("") { txn ->
            val name = if (txn.name.isNotEmpty()) txn.name else
                utils.CategoryUtils.getEffectiveDisplayCategory(txn.category, txn.detailedCategory)
            val escaped = name.replace("<", "&lt;").replace(">", "&gt;")
            """
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 7px 0; border-bottom: 1px solid ${t.border};">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.BODY}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;" title="$escaped">$escaped</span>
                    <span style="color: ${t.expenseRed}; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">${'$'}${txn.amount.roundToInt()}</span>
                </div>
            """
        }
        val hint = if (isFr) "Tout voir \u2192" else "See all \u2192"
        return """
            <div data-nav-to-transactions style="padding: 14px 16px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; cursor: pointer;">
                <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px;">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$title</span>
                    <span style="color: ${t.accent}; font-size: ${DashType.LABEL};">$hint</span>
                </div>
                <div style="flex: 1; display: flex; flex-direction: column; overflow: hidden;">$rows</div>
            </div>
        """
    }

    /** Top 5 merchants by spend for the focused (or current) month. Groups by transaction.name. */
    private fun renderTopMerchantsWidget(): String {
        val isFr = LocaleManager.getCurrentLocale().code == "fr"
        val (year, month) = effectiveDisplayYearMonth()
        val byMerchant = allTransactions
            .filter {
                !it.isIncome && !it.excludeFromTotals && !it.isTransfer &&
                it.accountId in selectedAccountIds && it.passesContextFilter() &&
                it.year == year && it.month == month && it.name.isNotEmpty()
            }
            .groupBy { it.name }
            .mapValues { (_, v) -> v.sumOf { it.amount } }
            .entries
            .sortedByDescending { it.value }

        val title = if (isFr) "Commer\u00e7ants" else "Merchants"
        if (byMerchant.isEmpty()) {
            val empty = if (isFr) "Aucune donn\u00e9e ce mois-ci" else "No data this month"
            return kpiShell(title, "—", t.textSecondary, empty)
        }

        val maxRows = fitRows("top-merchants", byMerchant.size, rowPx = 34, headerPx = 44, min = 2)
        val rows = byMerchant.take(maxRows).joinToString("") { (name, amt) ->
            val escaped = name.replace("<", "&lt;").replace(">", "&gt;")
            """
                <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 7px 0; border-bottom: 1px solid ${t.border};">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.BODY}; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1;" title="$escaped">$escaped</span>
                    <span style="color: ${t.textPrimary}; font-size: ${DashType.BODY}; font-weight: ${DashType.SEMI}; white-space: nowrap;">${'$'}${amt.roundToInt()}</span>
                </div>
            """
        }
        val hint = if (isFr) "Tout voir \u2192" else "See all \u2192"
        return """
            <div data-nav-to-transactions style="padding: 14px 16px; height: 100%; box-sizing: border-box; display: flex; flex-direction: column; cursor: pointer;">
                <div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px;">
                    <span style="color: ${t.textSecondary}; font-size: ${DashType.LABEL};">$title</span>
                    <span style="color: ${t.accent}; font-size: ${DashType.LABEL};">$hint</span>
                </div>
                <div style="flex: 1; display: flex; flex-direction: column; overflow: hidden;">$rows</div>
            </div>
        """
    }

    /**
     * Needs & Wants Flow — the 50/30/20 split as a Sankey.
     *
     * Reads focusedMonth (falling back to the last full month) so clicking a
     * bar in the monthly chart re-targets it, like the other month-aware
     * widgets. Everything is computed client-side from data already loaded —
     * no AI, no server call — which is why this works on every tier.
     */
    private fun renderNeedsWantsFlowWidget(): String {
        val el = container?.querySelector("#widget-content-needs-wants-flow") as? HTMLElement
        val widthPx = el?.clientWidth?.takeIf { it > 0 } ?: 480
        val heightPx = el?.clientHeight?.takeIf { it > 0 } ?: 300
        return renderNeedsWantsFlowSurface(widthPx, heightPx)
    }

    /**
     * The widget's markup, shared by the Pro grid and the classic dashboard's
     * chart area so both tiers get the month label and the edit CTA. The two
     * surfaces differ only in how big they are.
     *
     * The month is labelled because the widget defaults to the LAST FULL month:
     * without it, a user in mid-July reads June's split as month-to-date.
     */
    private fun renderNeedsWantsFlowSurface(widthPx: Int, heightPx: Int): String {
        val flow = buildBucketFlowForFocusedMonth() ?: return renderFlowEmptyState()
        val svg = SankeyChart.renderToSvg(flow, widthPx, heightPx, WebTheme.current.isDark)
        val (year, month) = flowYearMonth()

        val t = WebTheme.current
        return """
            <div style="display:flex; flex-direction:column; height:100%;">
                <div style="flex:0 0 auto; color:${t.textSecondary}; font-size:11px; margin-bottom:4px;">
                    ${YearMonth(year, month).toFullLabel()}
                </div>
                <div style="flex:1; min-height:0; position:relative;">
                    $svg
                    <div id="needs-wants-flow-tooltip" style="
                        position:absolute; display:none;
                        background:${if (t.isDark) "rgba(20,20,20,0.95)" else t.card};
                        border:1px solid ${t.border}; border-radius:8px; padding:10px 14px;
                        color:${t.textPrimary}; font-size:12.5px; pointer-events:none; z-index:100;
                        white-space:nowrap;
                        box-shadow:${if (t.isDark) "0 4px 12px rgba(0,0,0,0.4)" else "0 4px 12px rgba(0,0,0,0.12)"};
                    "></div>
                </div>
                <button id="needs-wants-edit-cta" style="
                    align-self:flex-start; margin-top:8px;
                    display:inline-flex; align-items:center; gap:6px;
                    padding:6px 12px; border-radius:8px;
                    background:${t.chipSelectedBg}; border:1px solid ${t.accent};
                    color:${t.accent}; font-size:12px; font-weight:600; cursor:pointer;
                    white-space:nowrap; transition:background 0.15s ease, color 0.15s ease;
                ">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                    ${StringKey.BUDGET_EDIT_BUCKETS_CTA.tr()}
                </button>
            </div>
        """.trimIndent()
    }

    /**
     * Opens the edit-buckets modal for whatever month the widget is showing.
     * Shared by the CTA and the Unclassified strand on BOTH surfaces — the
     * classic dashboard is the only place a Standard user can reclassify a
     * category now that the Budget-page chips are gone.
     */
    private fun openBucketEditModal() {
        // The overrides map is the modal's precedence input, not just a display
        // hint: with an empty map it can't see an existing primary override, so a
        // per-sub click skips materialization and writes a sub override that stays
        // dead under the shadowing primary — while reporting success. The GET
        // races the widget's first paint, so fetch it before opening rather than
        // opening against the empty default. Same "not loaded yet" gate the charts
        // apply with transactionsLoaded.
        if (!bucketClassificationsLoaded) {
            GlobalScope.launch {
                loadBucketClassificationsForDashboard()
                if (bucketClassificationsLoaded) showBucketEditModal()
            }
            return
        }
        showBucketEditModal()
    }

    private fun showBucketEditModal() {
        val flow = buildBucketFlowForFocusedMonth() ?: return
        BucketEditModal.show(flow, bucketClassifications) {
            // Re-fetch overrides; loadBucketClassificationsForDashboard() redraws
            // the visible surface itself once the new map has landed.
            GlobalScope.launch { loadBucketClassificationsForDashboard() }
        }
    }

    /** Wires the CTA and the Unclassified strand inside [root] to [openBucketEditModal]. */
    private fun attachNeedsWantsFlowHandlers(root: org.w3c.dom.Element?) {
        (root?.querySelector("#needs-wants-edit-cta") as? HTMLElement)?.let { btn ->
            btn.addEventListener("click", { openBucketEditModal() })
            // Fill on hover so it reads unmistakably as a button, not a text link.
            val onFill = if (t.isDark) "#0A0A0A" else "#FFFFFF"
            btn.addEventListener("mouseenter", {
                btn.style.background = t.accent
                btn.style.color = onFill
            })
            btn.addEventListener("mouseleave", {
                btn.style.background = t.chipSelectedBg
                btn.style.color = t.accent
            })
        }
        root?.querySelector("#needs-wants-unclassified-strand")?.addEventListener("click", { openBucketEditModal() })
        attachSankeyTooltip(root)
    }

    /**
     * Instant, custom hover tooltip for the Sankey bands — the native SVG
     * `<title>` had the browser's ~1s delay. Mirrors the monthly bar chart's
     * tooltip: shows on mouseenter, follows the cursor, same card styling and
     * a colour swatch matching the band. Reads the data-* attributes SankeyChart
     * writes on each `.sk-band`.
     */
    private fun attachSankeyTooltip(root: org.w3c.dom.Element?) {
        val tip = root?.querySelector("#needs-wants-flow-tooltip") as? HTMLElement ?: return
        val host = tip.parentElement as? HTMLElement ?: return
        val bands = root.querySelectorAll(".sk-band")
        val t = WebTheme.current
        val fr = LocaleManager.getCurrentLocale().code == "fr"
        for (i in 0 until bands.length) {
            // SVG <g> is an SVGElement, NOT an HTMLElement — casting to
            // HTMLElement returned null and silently skipped every band, so the
            // tooltip never fired. Element carries addEventListener/getAttribute.
            val band = bands.item(i) as? org.w3c.dom.Element ?: continue
            band.addEventListener("mouseenter", {
                val cat = band.getAttribute("data-cat") ?: ""
                val amt = band.getAttribute("data-amt") ?: ""
                val color = band.getAttribute("data-color") ?: t.accent
                val pct = band.getAttribute("data-pct")
                val parent = band.getAttribute("data-parent")
                val shareLine = if (!pct.isNullOrEmpty() && !parent.isNullOrEmpty()) {
                    """<div style="color:${t.textSecondary};">$amt · <span style="color:${t.textPrimary}; font-weight:500;">$pct% ${if (fr) "de" else "of"} $parent</span></div>"""
                } else {
                    """<div style="color:${t.textSecondary};">$amt</div>"""
                }
                tip.innerHTML =
                    """<div style="display:flex; align-items:center; gap:6px; font-weight:600; color:${t.textPrimary}; margin-bottom:3px;">""" +
                    """<span style="width:9px; height:9px; background:$color; border-radius:2px; flex:0 0 auto;"></span>$cat</div>$shareLine"""
                tip.style.display = "block"
            })
            band.addEventListener("mousemove", { ev ->
                val e = ev as org.w3c.dom.events.MouseEvent
                val hostRect = host.getBoundingClientRect()
                val tw = tip.offsetWidth.toDouble()
                val th = tip.offsetHeight.toDouble()
                var x = e.clientX - hostRect.left + 14
                var y = e.clientY - hostRect.top + 14
                if (x + tw > hostRect.width) x = e.clientX - hostRect.left - tw - 14
                if (y + th > hostRect.height) y = e.clientY - hostRect.top - th - 14
                tip.style.left = "${maxOf(0.0, x).toInt()}px"
                tip.style.top = "${maxOf(0.0, y).toInt()}px"
            })
            band.addEventListener("mouseleave", { tip.style.display = "none" })
        }
    }

    /** Redraws whichever Needs & Wants surface is on screen. Mirrors rerenderPieChart(). */
    private fun rerenderNeedsWantsFlow() {
        if (useGridLayout) {
            renderWidgetContent("needs-wants-flow")
        } else if (selectedChartType == ChartType.NEEDS_WANTS_FLOW) {
            val chartArea = container?.querySelector("#selectedChartArea") as? HTMLElement
            chartArea?.innerHTML = renderChart()
            attachNeedsWantsFlowHandlers(chartArea)
        }
    }

    /** Adapts dashboard TransactionData to the shared builder's TxnLike. */
    private class FlowTxn(private val t: TransactionData) : ai.rules.BudgetHealthAggregator.TxnLike {
        override val amount: Double get() = t.amount
        override val categoryCode: String? get() = t.category
        override val detailedCode: String? get() = t.detailedCategory
        override val excluded: Boolean get() = t.excludeFromTotals
    }

    /** The user's saved bucket overrides, keyed by category code. */
    private var bucketClassifications = mutableMapOf<String, ai.rules.BudgetBucket>()

    /** False until the classifications GET has landed. An empty map means "none
     *  saved" only once this is true; before that it means "unknown", and the
     *  edit modal must not be opened against it (see [openBucketEditModal]). */
    private var bucketClassificationsLoaded = false

    /** Reused across calls instead of constructing per-request like the other Web*Component classes. */
    private val bucketClassificationsJson = Json { ignoreUnknownKeys = true }

    /** Previous calendar (year, month) as of "now". The 50/30/20 split is only
     *  honest over a complete month, so this is the default when no bar is
     *  selected. In January this rolls back to December of the *prior* year —
     *  paired with the month, not just a bare month index, so the January
     *  edge case can't be paired with the wrong year by a caller. */
    private fun lastFullYearMonth(): Pair<Int, Int> {
        val now = kotlin.js.Date()
        return utils.DashboardMonth.lastComplete(now.getFullYear(), now.getMonth() + 1)
    }

    /** The (year, month) the flow widget is showing — focused month if the user
     *  clicked a bar, otherwise the last full month. Shared by the builder and
     *  the month label so the two can never disagree. */
    private fun flowYearMonth(): Pair<Int, Int> =
        focusedMonth?.let { selectedYear to it } ?: lastFullYearMonth()

    /**
     * Builds the flow for the focused month, defaulting to the last full month.
     * Returns null when there's no income to divide — the widget shows an
     * empty state rather than a diagram that can't reconcile.
     *
     * The January rollback in `lastFullYearMonth()` only applies to this
     * no-focus default: once the user has focused a month via a bar click,
     * `selectedYear` is already the correct year for `focusedMonth` and is
     * used as-is.
     *
     * Expense scoping matches every other spending aggregation in this file
     * (see computeMonthlyAggregates and the category-spending widgets): income
     * and transfers are dropped, and the selectedAccountIds/passesContextFilter()
     * scoping mirrors what feeds confirmedIncome into yearlyData. Income is the
     * root of the diagram, not a strand; transfers would otherwise double-count
     * savings against goalAllocations and, because allTransactions stores
     * absolute amounts, refunds would land in Needs/Wants as spending.
     * excludeFromTotals is deliberately NOT filtered here — FlowTxn forwards it
     * as `excluded` and BucketFlowBuilder drops it.
     */
    private fun buildBucketFlowForFocusedMonth(): ai.rules.BucketFlow? {
        val (year, month) = flowYearMonth()
        val monthTxns = allTransactions.filter {
            it.year == year && it.month == month &&
                !it.isIncome && !it.isTransfer &&
                it.accountId in selectedAccountIds && it.passesContextFilter()
        }
        val monthData = yearlyData[year]?.get(month)
        val income = monthData?.confirmedIncome ?: 0.0
        if (income <= 0.0) return null
        return ai.rules.BucketFlowBuilder.build(
            txns = monthTxns.map { FlowTxn(it) },
            goalAllocations = monthData?.goalAllocations ?: 0.0,
            userClassifications = bucketClassifications,
            monthlyIncome = income,
            // Same denominator MonthlyFinanceData.savingsPercentage uses for this
            // month, so the Sankey's Saving band and the hero savings rate can't
            // print two different numbers on one screen.
            percentBasis = monthData?.smoothedMonthlyIncome,
        )
    }

    /** Matches the empty state the other chart widgets use. */
    private fun renderFlowEmptyState(): String {
        val t = WebTheme.current
        val msg = if (transactionsLoaded) StringKey.CHART_NO_DATA_ACCOUNTS.tr()
                  else StringKey.CHART_LOADING_TRANSACTIONS.tr()
        return """
            <div style="display:flex; align-items:center; justify-content:center; height:100%;">
                <div style="text-align:center; color:${t.textSecondary}; font-size:12px;">$msg</div>
            </div>
        """.trimIndent()
    }

    /** Base URL for direct fetch() calls — mirrors the pattern already used by
     *  WebBudgetPlanningComponent and the other Web*Component classes. */
    private fun getBaseUrl(): String =
        if (window.location.hostname == "localhost" || window.location.hostname == "127.0.0.1") "http://localhost:3000" else ""

    /** GET /api/budget/classifications — same endpoint the old chips used. */
    private suspend fun loadBucketClassificationsForDashboard() {
        try {
            val token = ApiService.getSharedAuthToken() ?: return
            val resp = kotlinx.browser.window.fetch(
                "${getBaseUrl()}/api/budget/classifications",
                org.w3c.fetch.RequestInit(
                    method = "GET",
                    headers = kotlin.js.json("Authorization" to "Bearer $token"),
                ),
            ).await()
            if (!resp.ok) return
            val text = resp.text().await()
            val parsed = bucketClassificationsJson.decodeFromString<BucketClassificationsDto>(text)
            bucketClassifications = parsed.classifications
                .associate { it.categoryCode to ai.rules.BudgetBucket.fromWire(it.bucket) }
                .toMutableMap()
            bucketClassificationsLoaded = true
            // The initial fetch races the dashboard's own reveal (gated only on
            // transactionsSettled/budgetPlansSettled — see maybeRevealDashboard) and can
            // land after the widget already rendered with the empty-map engine defaults.
            // Re-render it directly, same as the month-bar click handler does for widgets
            // whose data just changed; rerenderNeedsWantsFlow() is a no-op if the surface
            // isn't on screen yet, so this is safe to call unconditionally. It is also the
            // single redraw after an edit-modal save — openBucketEditModal() must not
            // re-render on its own or the widget would be built twice per click.
            rerenderNeedsWantsFlow()
        } catch (e: Throwable) {
            console.warn("Failed to load bucket classifications: ${e.message}")
        }
    }
}

@kotlinx.serialization.Serializable
private data class BucketClassificationsDto(
    val classifications: List<BucketClassificationRowDto> = emptyList(),
)

@kotlinx.serialization.Serializable
private data class BucketClassificationRowDto(
    val categoryCode: String,
    val bucket: String,
    val isOverride: Boolean = false,
)

/**
 * Show a color palette popover anchored to the given element.
 * Package-level function accessible from other files in the same package.
 */
fun showColorPalettePopover(
    anchorElement: org.w3c.dom.HTMLElement,
    currentColor: String,
    onColorPicked: (String) -> Unit
) {
    // Remove any existing popover
    kotlinx.browser.document.getElementById("color-palette-popover")?.remove()

    val palette = utils.CategoryUtils.colorPalette
    val popover = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLElement
    popover.id = "color-palette-popover"

    // Position below the anchor
    val rect = anchorElement.getBoundingClientRect()
    val top = rect.bottom + kotlinx.browser.window.scrollY + 4
    val left = rect.left + kotlinx.browser.window.scrollX

    popover.style.cssText = """
        position: absolute;
        top: ${top}px;
        left: ${left}px;
        background: ${WebTheme.current.card};
        border: 1px solid ${WebTheme.current.border};
        border-radius: 12px;
        padding: 12px;
        z-index: 2000;
        box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        display: grid;
        grid-template-columns: repeat(6, 28px);
        gap: 6px;
    """

    palette.forEach { color ->
        val swatch = kotlinx.browser.document.createElement("div") as org.w3c.dom.HTMLElement
        val isSelected = color.equals(currentColor, ignoreCase = true)
        swatch.style.cssText = """
            width: 28px; height: 28px; border-radius: 50%;
            background: $color;
            cursor: pointer;
            border: 2px solid ${if (isSelected) "#fff" else "transparent"};
            display: flex; align-items: center; justify-content: center;
            transition: transform 0.1s;
        """
        if (isSelected) {
            swatch.innerHTML = """<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>"""
        }
        swatch.addEventListener("mouseover", { swatch.style.transform = "scale(1.15)" })
        swatch.addEventListener("mouseout", { swatch.style.transform = "scale(1)" })
        swatch.addEventListener("click", { e ->
            e.stopPropagation()
            popover.remove()
            onColorPicked(color)
        })
        popover.appendChild(swatch)
    }

    kotlinx.browser.document.body?.appendChild(popover)

    // Close on click outside (delayed to avoid immediate close)
    kotlinx.browser.window.setTimeout({
        kotlinx.browser.document.addEventListener("click", { popover.remove() }, js("({once: true})"))
    }, 10)
}
